import { getPlanningMutationActivityMessage } from "./planningMutationActivity.js";
import { normalizeCurrencyDisplay } from "./currencyDisplay.js";
import { buildAddressRoutingMetric } from "./addressRoutingValidation.js";
import { buildSolverExecutionMetrics, buildSolverRoutingSummaryMetrics } from "./solverExecution.js";
import { buildDispatchDayControlsModel } from "./dispatchDayControls.js";
import { resolveOptimizationModeFromDiagnostics } from "./optimizationModes.js";
import { collectOptimizationPreviewRetainedOrderIds } from "./optimizationPreviewTimeline.js";
import {
  normalizeAddressDisplayLabel,
  normalizeClientDisplayLabel,
} from "./displayLabelFormatters.js";
import {
  formatTons,
  getAssignmentTripCount,
  getDeliveryHighlightKey,
  isTruckCompatibilityActionable,
} from "./resultsFleetData.js";
import { countTruckPreloadTimeOverrides } from "./resultsWorkspaceData.js";
import {
  formatDataManagerAuditFieldLabel,
  formatDataManagerAuditSafeFieldValue,
} from "./dataManagerAuditLabels.js";

const DISPATCH_METRIC_DISPLAY_ORDER = [
  "total-trips",
  "assignment-progress",
  "confirmed-orders",
  "non-assignable-orders",
  "suspended-deliveries",
];

const DIAGNOSTIC_METRIC_UNAVAILABLE_LABEL = "Non disponible";
const DIAGNOSTIC_METRIC_UNAVAILABLE_NOTE = "Valeur non remontee";
const DIAGNOSTIC_INTEGER_FORMATTER = new Intl.NumberFormat("fr-CA", {
  maximumFractionDigits: 0,
});
const DIAGNOSTIC_SECONDS_FORMATTER = new Intl.NumberFormat("fr-CA", {
  maximumFractionDigits: 1,
});
const RESULTS_SOURCE_STALE_REASON_LABELS = {
  "admin-manual-invalidation": "Invalidation admin",
  "data-manager-mutation": "Mutation Data Manager",
  "dispatch-day-closed": "Journée clôturée",
  "dispatch-day-reopened": "Journée rouverte",
  "driver-portal-order-eligibility-changed": "Admissibilité modifiée par le portail chauffeur",
  "end-of-day-load-assignment-rollback": "Retour arrière du Chargé PM",
  "import-changed-fields": "Import modifié",
  "source-changes-unresolved": "Mises à jour de commandes non résolues",
  "source-data-updated": "Données source modifiées",
  "source-signature-mismatch": "Sources remplacées",
};
const RESULTS_SOURCE_STALE_CAUSE_ENTITY_LABELS = {
  client_correspondence: "Client",
  client_correspondence_import: "Import clients",
  compartment: "Compartiment",
  compartment_import: "Import compartiments",
  employee: "Employé",
  order: "Commande",
  order_import: "Import commandes",
  product_correspondence: "Produit",
  truck: "Camion",
  truck_import: "Import camions",
};
const RESULTS_SOURCE_STALE_CAUSE_ACTION_LABELS = {
  approve_for_billing: "approbation facturation",
  cancel: "annulation",
  clear_delivered: "retrait du statut livré",
  create: "création",
  delete: "suppression",
  import_merge: "import fusion",
  import_replace: "import remplacement",
  reopen: "réouverture",
  update: "mise à jour",
};
const RESULTS_SOURCE_STALE_VISIBLE_FIELD_LIMIT = 2;
const RESULTS_SOURCE_STALE_TIMESTAMP_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  month: "long",
  timeZone: "America/Toronto",
  year: "numeric",
});
const RUN_DIAGNOSTIC_DISPLAY_TIME_ZONE = "America/Toronto";
const SERVICE_VALIDATION_SERVICE_LABELS = {
  excel: "Données source",
  geogratis: "GeoGratis",
  here: "HERE",
  nominatim: "Nominatim",
  ortools: "OR-Tools",
};

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeInlineTextList(values) {
  return Array.isArray(values)
    ? values.map((value) => normalizeInlineText(value)).filter(Boolean)
    : [];
}

function normalizeNonRenderableConflictIds(...values) {
  const ids = [];
  values.forEach((value) => {
    (Array.isArray(value) ? value : [value]).forEach((candidate) => {
      const normalized = normalizeInlineText(candidate);
      if (normalized) {
        ids.push(normalized);
      }
    });
  });
  return [...new Set(ids)];
}

export function resolveNonRenderableConflictAffectedIds(conflict = {}, fallback = {}) {
  const missingOrderIds = normalizeNonRenderableConflictIds(
    conflict?.missing_order_ids,
    conflict?.missing_order_id,
    fallback?.missing_order_ids,
    fallback?.missing_order_id,
  );
  const missingDeliveryIds = normalizeNonRenderableConflictIds(
    conflict?.missing_delivery_ids,
    conflict?.missing_delivery_id,
    fallback?.missing_delivery_ids,
    fallback?.missing_delivery_id,
  );
  const fallbackOrderIds = normalizeNonRenderableConflictIds(
    conflict?.order_ids,
    conflict?.order_id,
    fallback?.order_ids,
    fallback?.order_id,
  );
  const fallbackDeliveryIds = normalizeNonRenderableConflictIds(
    conflict?.delivery_ids,
    conflict?.delivery_id,
    fallback?.delivery_ids,
    fallback?.delivery_id,
  );

  return {
    deliveryIds: missingDeliveryIds.length > 0 ? missingDeliveryIds : fallbackDeliveryIds,
    hasMissingDeliveryIds: missingDeliveryIds.length > 0,
    hasMissingOrderIds: missingOrderIds.length > 0,
    orderIds: missingOrderIds.length > 0 ? missingOrderIds : fallbackOrderIds,
  };
}

function normalizeExcludedTruckRefreshImpactedLocations(values) {
  if (!Array.isArray(values)) {
    return [];
  }

  return values
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null;
      }
      const label = normalizeInlineText(item.label);
      if (!label) {
        return null;
      }
      return {
        kind: normalizeInlineText(item.kind),
        kindLabel: normalizeInlineText(item.kind_label || item.kindLabel),
        label,
        orderIds: normalizeInlineTextList(item.order_ids || item.orderIds),
        currentOpeningTime: normalizeInlineText(item.current_opening_time || item.currentOpeningTime),
        dataManagerOpeningTime: normalizeInlineText(item.data_manager_opening_time || item.dataManagerOpeningTime),
        currentClosingTime: normalizeInlineText(item.current_closing_time || item.currentClosingTime),
        dataManagerClosingTime: normalizeInlineText(item.data_manager_closing_time || item.dataManagerClosingTime),
        currentStartTime: normalizeInlineText(item.current_start_time || item.currentStartTime),
        dataManagerStartTime: normalizeInlineText(item.data_manager_start_time || item.dataManagerStartTime),
      };
    })
    .filter(Boolean);
}

function formatResultsSourceStaleTimestamp(value) {
  const normalizedValue = normalizeInlineText(value);
  if (!normalizedValue) {
    return "";
  }

  const parsedDate = new Date(normalizedValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return normalizedValue;
  }

  return RESULTS_SOURCE_STALE_TIMESTAMP_FORMATTER.format(parsedDate);
}

function resolveResultsSourceStaleReasonLabel(reason) {
  const normalizedReason = normalizeInlineText(reason).toLowerCase();
  return RESULTS_SOURCE_STALE_REASON_LABELS[normalizedReason] || "Données source modifiées";
}

function readResultsSourceDataState(resultsData = {}) {
  return resultsData?.source_data_state && typeof resultsData.source_data_state === "object"
    ? resultsData.source_data_state
    : null;
}

function normalizeResultsSourceStaleCauseText(value, maxLength = 240) {
  const normalizedValue = normalizeInlineText(value).replace(/\s+/g, " ");
  return normalizedValue.length <= maxLength ? normalizedValue : "";
}

function lowerCaseResultsSourceStaleLabel(value) {
  const normalizedValue = normalizeResultsSourceStaleCauseText(value, 120);
  return normalizedValue
    ? `${normalizedValue.charAt(0).toLocaleLowerCase("fr-CA")}${normalizedValue.slice(1)}`
    : "";
}

function formatResultsSourceStaleFieldChange(fieldChange, { compact = true } = {}) {
  if (!fieldChange || typeof fieldChange !== "object" || Array.isArray(fieldChange)) {
    return "";
  }
  const fieldName = normalizeInlineText(fieldChange.field).toLowerCase();
  if (!fieldName || fieldName === "updated_at") {
    return "";
  }
  const fieldLabel = lowerCaseResultsSourceStaleLabel(
    formatDataManagerAuditFieldLabel(fieldName, { compact }),
  );
  if (!fieldLabel) {
    return "";
  }
  if (fieldChange.values_included !== true) {
    return fieldLabel;
  }
  const oldValue = formatDataManagerAuditSafeFieldValue(fieldName, fieldChange.old_value);
  const newValue = formatDataManagerAuditSafeFieldValue(fieldName, fieldChange.new_value);
  return oldValue && newValue
    ? `${fieldLabel} ${oldValue} → ${newValue}`
    : fieldLabel;
}

function resolveResultsSourceStaleCauseEntityLabel(resultsData, invalidationCause) {
  const entityKind = normalizeInlineText(invalidationCause?.entity_kind).toLowerCase();
  const entityId = normalizeResultsSourceStaleCauseText(invalidationCause?.entity_id, 120);
  const rawEntityLabel = normalizeResultsSourceStaleCauseText(invalidationCause?.entity_label, 120);
  const entityKindLabel = RESULTS_SOURCE_STALE_CAUSE_ENTITY_LABELS[entityKind] || "";
  const entityLabel = entityKind === "truck"
    ? resolveReassignmentTruckDisplayLabel(resultsData, entityId, rawEntityLabel || entityId)
    : rawEntityLabel || entityId;

  if (!entityLabel) {
    return entityKindLabel;
  }
  if (!entityKindLabel || entityLabel.toLocaleLowerCase("fr-CA").startsWith(entityKindLabel.toLocaleLowerCase("fr-CA"))) {
    return entityLabel;
  }
  return `${entityKindLabel} ${entityLabel}`;
}

function formatResultsSourceStaleCauseSentence(label) {
  const normalizedLabel = normalizeResultsSourceStaleCauseText(label, 2000).replace(/[.!?…]+$/u, "");
  return normalizedLabel ? `${normalizedLabel}.` : "";
}

function readResultsSourceStaleInvalidationCauses(sourceDataState) {
  const primaryCause = sourceDataState?.invalidation_cause
    && typeof sourceDataState.invalidation_cause === "object"
    && !Array.isArray(sourceDataState.invalidation_cause)
    ? sourceDataState.invalidation_cause
    : null;
  const candidates = [
    ...(Array.isArray(sourceDataState?.invalidation_causes) ? sourceDataState.invalidation_causes : []),
    ...(primaryCause ? [primaryCause] : []),
  ];
  const seenKeys = new Set();
  return candidates.filter((candidate) => {
    if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
      return false;
    }
    const eventId = normalizeInlineText(candidate.event_id);
    let identity = eventId ? `event:${eventId}` : "";
    if (!identity) {
      try {
        identity = `cause:${JSON.stringify(candidate)}`;
      } catch {
        return false;
      }
    }
    if (seenKeys.has(identity)) {
      return false;
    }
    seenKeys.add(identity);
    return true;
  });
}

function buildResultsSourceStaleCauseEntry(resultsData, invalidationCause) {
  const specificity = normalizeInlineText(invalidationCause.specificity).toLowerCase();
  if (specificity === "unknown") {
    return null;
  }

  const entityLabel = resolveResultsSourceStaleCauseEntityLabel(resultsData, invalidationCause);
  const useCompactFieldLabels = normalizeInlineText(invalidationCause.entity_kind).toLowerCase() !== "truck";
  const fieldChanges = Array.isArray(invalidationCause.field_changes)
    ? invalidationCause.field_changes
      .map((fieldChange) => formatResultsSourceStaleFieldChange(fieldChange, { compact: useCompactFieldLabels }))
      .filter(Boolean)
    : [];
  const compactFieldChanges = fieldChanges.slice(0, RESULTS_SOURCE_STALE_VISIBLE_FIELD_LIMIT);
  if (fieldChanges.length > RESULTS_SOURCE_STALE_VISIBLE_FIELD_LIMIT) {
    compactFieldChanges.push(`+${fieldChanges.length - RESULTS_SOURCE_STALE_VISIBLE_FIELD_LIMIT} champ${fieldChanges.length - RESULTS_SOURCE_STALE_VISIBLE_FIELD_LIMIT > 1 ? "s" : ""}`);
  }
  const action = normalizeInlineText(invalidationCause.action).toLowerCase();
  const actionLabel = RESULTS_SOURCE_STALE_CAUSE_ACTION_LABELS[action]
    || normalizeResultsSourceStaleCauseText(action.replace(/_/g, " "), 80);
  const isEntityLifecycleAction = action === "create" || action === "delete";
  const summary = normalizeResultsSourceStaleCauseText(invalidationCause.summary, 180);
  const displayText = normalizeResultsSourceStaleCauseText(invalidationCause.display_text, 180);
  const compactBaseLabel = displayText || (isEntityLifecycleAction && entityLabel && actionLabel
    ? `${entityLabel} : ${actionLabel}`
    : fieldChanges.length > 0
    ? `${entityLabel ? `${entityLabel} : ` : ""}${compactFieldChanges.join(", ")}`
    : entityLabel && actionLabel
      ? `${entityLabel} : ${actionLabel}`
      : summary);
  const fullBaseLabel = displayText || (isEntityLifecycleAction && entityLabel && actionLabel
    ? `${entityLabel} : ${actionLabel}`
    : fieldChanges.length > 0
    ? `${entityLabel ? `${entityLabel} : ` : ""}${fieldChanges.join(", ")}`
    : compactBaseLabel);
  if (!compactBaseLabel) {
    return null;
  }

  return {
    compactLine: formatResultsSourceStaleCauseSentence(compactBaseLabel),
    occurredAtLabel: formatResultsSourceStaleTimestamp(invalidationCause.occurred_at),
    fullLine: formatResultsSourceStaleCauseSentence(fullBaseLabel),
  };
}

export function formatResultsSourceStaleCauseLine(resultsData = {}, cause = {}) {
  return buildResultsSourceStaleCauseEntry(resultsData, cause)?.fullLine || "";
}

function buildResultsSourceStaleCauseModel(resultsData, sourceDataState) {
  const invalidationCauses = readResultsSourceStaleInvalidationCauses(sourceDataState);
  const parsedEventCount = Number(sourceDataState?.invalidation_event_count);
  const eventCount = Math.max(
    Number.isFinite(parsedEventCount) && parsedEventCount > 0 ? Math.floor(parsedEventCount) : 0,
    invalidationCauses.length,
  );
  const causeEntries = invalidationCauses
    .map((cause) => buildResultsSourceStaleCauseEntry(resultsData, cause))
    .filter(Boolean);
  const firstCause = causeEntries[0] || null;

  return {
    causeLine: firstCause?.compactLine || "",
    causeLines: causeEntries.map((entry) => entry.fullLine),
    causeOccurredAtLabel: firstCause?.occurredAtLabel || "",
    causeTooltip: firstCause?.fullLine || "",
    invalidationEventCount: eventCount,
  };
}

export function buildDispatchSourceStaleNotice(resultsData = {}, { planningUpdatedAt = "" } = {}) {
  const isMissingLatestOptimization = resultsData?.code === "resource_not_found"
    && resultsData?.resource === "latest_optimization";
  if (isMissingLatestOptimization) {
    return null;
  }

  const sourceDataState = readResultsSourceDataState(resultsData);
  const sourceDataStatus = normalizeInlineText(sourceDataState?.status).toLowerCase();
  const isOperationallyCurrent = resultsData?.is_operationally_current !== false;
  const isSourceStale = sourceDataState?.stale === true
    || sourceDataStatus === "stale"
    || isOperationallyCurrent === false;

  if (!isSourceStale) {
    return null;
  }

  const dispatchDates = normalizeInlineTextList(sourceDataState?.dispatch_dates);
  const updatedAtLabel = formatResultsSourceStaleTimestamp(
    sourceDataState?.updated_at || planningUpdatedAt,
  );
  const dispatchDatesLabel = dispatchDates.join(", ");
  const reasonLabel = resolveResultsSourceStaleReasonLabel(sourceDataState?.reason);
  const causeModel = buildResultsSourceStaleCauseModel(resultsData, sourceDataState);

  return {
    ...causeModel,
    detail: "Relancez Optimiser la flotte pour recalculer la journée sans perdre le portrait actuellement affiché.",
    dispatchDatesLabel,
    dispatchDatesLabelPrefix: dispatchDates.length > 1 ? "Journées" : "Journée",
    message: "Le dernier dispatch calculé reste visible, mais il n'est plus opérationnellement à jour.",
    reasonLabel,
    updatedAtLabel,
  };
}

function normalizeServiceValidationStatus(status) {
  if (status === "ok" || status === "degraded" || status === "error" || status === "unknown") {
    return status;
  }

  return "unknown";
}

function normalizeServiceValidationLabel(key, label) {
  if (typeof label === "string" && label.trim()) {
    return label.trim();
  }

  if (typeof key === "string" && key.trim()) {
    return SERVICE_VALIDATION_SERVICE_LABELS[key.trim().toLowerCase()] || key.trim();
  }

  return "Service";
}

function collectServiceValidationIssues(resultsData = {}) {
  return (Array.isArray(resultsData?.service_health) ? resultsData.service_health : [])
    .filter((item) => item && typeof item === "object")
    .map((item) => {
      const key = typeof item.key === "string" ? item.key.trim().toLowerCase() : "";
      const status = normalizeServiceValidationStatus(item.status);

      return {
        key,
        label: normalizeServiceValidationLabel(key, item.label),
        status,
      };
    })
    .filter((item) => item.status === "error" || item.status === "degraded");
}

function normalizeOperationalStopType(stopType) {
  const normalizedStopType = normalizeInlineText(stopType).toLowerCase();
  return normalizedStopType === "loading" || normalizedStopType === "delivery"
    ? normalizedStopType
    : "";
}

function resolveOperationalVisitTypes(item = null, primaryStopType = "") {
  const operationTypes = new Set();
  const normalizedPrimaryStopType = normalizeOperationalStopType(primaryStopType);
  if (normalizedPrimaryStopType) {
    operationTypes.add(normalizedPrimaryStopType);
  }

  [
    ...(Array.isArray(item?.operationTypes) ? item.operationTypes : []),
    ...(Array.isArray(item?.operation_types) ? item.operation_types : []),
  ].forEach((operationType) => {
    const normalizedOperationType = normalizeOperationalStopType(operationType);
    if (normalizedOperationType) {
      operationTypes.add(normalizedOperationType);
    }
  });

  if (Number(item?.loadingVisitCount ?? item?.loading_visit_count) > 0) {
    operationTypes.add("loading");
  }
  if (Number(item?.deliveryVisitCount ?? item?.delivery_visit_count) > 0) {
    operationTypes.add("delivery");
  }
  if (
    item?.sameSiteGroupContainsMixedStopTypes === true
    || item?.same_site_group_contains_mixed_stop_types === true
  ) {
    operationTypes.add("loading");
    operationTypes.add("delivery");
  }

  return Array.from(operationTypes);
}

function normalizeOperationalVisitIdentity(value) {
  const normalizedValue = normalizeInlineText(value);
  if (!normalizedValue) {
    return "";
  }

  return normalizedValue
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function resolveOperationalVisitSequence(item = null) {
  if (!item || typeof item !== "object") {
    return null;
  }

  const candidates = [
    item.routeSequenceDisplaySequence,
    item.globalSequence,
    item.global_sequence,
    item.sequence,
    item.stop_sequence,
    item.seq,
  ];

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === "") {
      continue;
    }
    const numericCandidate = Number(candidate);
    if (Number.isFinite(numericCandidate)) {
      return numericCandidate;
    }
  }

  return null;
}

function orderOperationalVisitItems(items = []) {
  const resolvedItems = Array.isArray(items) ? items.filter(Boolean) : [];
  const indexedItems = resolvedItems.map((item, index) => ({
    index,
    item,
    sequence: resolveOperationalVisitSequence(item),
  }));

  if (!indexedItems.length || indexedItems.some(({ sequence }) => sequence === null)) {
    return resolvedItems;
  }

  return indexedItems
    .sort((left, right) => left.sequence - right.sequence || left.index - right.index)
    .map(({ item }) => item);
}

function resolveOperationalVisitTravelMinutes(item = null) {
  if (!item || typeof item !== "object") {
    return 0;
  }

  const candidates = [
    item.travelDurationMinutes,
    item.travel_duration_minutes,
    item.travel_time_minutes,
    item.displayTravelDurationMinutes,
  ];

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === "") {
      continue;
    }
    const numericCandidate = Number(candidate);
    if (Number.isFinite(numericCandidate) && numericCandidate >= 0) {
      return numericCandidate;
    }
  }

  return 0;
}

function resolveOperationalVisitCoordinateKey(item = null) {
  if (!item || typeof item !== "object") {
    return "";
  }

  const rawLatitude = item.latitude ?? item.to?.latitude;
  const rawLongitude = item.longitude ?? item.to?.longitude;
  if (
    rawLatitude === null
    || rawLatitude === undefined
    || rawLatitude === ""
    || rawLongitude === null
    || rawLongitude === undefined
    || rawLongitude === ""
  ) {
    return "";
  }

  const latitude = Number(rawLatitude);
  const longitude = Number(rawLongitude);
  return Number.isFinite(latitude) && Number.isFinite(longitude)
    ? `${latitude.toFixed(6)}|${longitude.toFixed(6)}`
    : "";
}

function buildOperationalVisitMetadata(item = null, source = "trip") {
  const isRouteSequenceItem = source === "route-sequence";
  const primaryStopType = normalizeOperationalStopType(
    isRouteSequenceItem
      ? item?.stopType || item?.to?.stopType
      : item?.stop_type || item?.stopType,
  );
  const operationTypes = resolveOperationalVisitTypes(item, primaryStopType);
  const stopType = primaryStopType || operationTypes[0] || "";
  const addressKey = normalizeOperationalVisitIdentity(
    isRouteSequenceItem
      ? item?.operationAddress || item?.toAddress || item?.to?.address || item?.routeToAddress
      : item?.address,
  );
  const labelKey = normalizeOperationalVisitIdentity(
    isRouteSequenceItem
      ? item?.operationOwnerLabel || item?.toLabel || item?.to?.label || item?.routeToLabel
      : item?.label || item?.client_name,
  );
  const groupKey = normalizeOperationalVisitIdentity(
    isRouteSequenceItem
      ? item?.sameSiteGroupKey
      : item?.same_site_group_key || item?.sameSiteGroupKey,
  );

  return {
    addressKey,
    coordinateKey: resolveOperationalVisitCoordinateKey(item),
    groupKey,
    hasMixedStopTypes: item?.sameSiteGroupContainsMixedStopTypes === true
      || item?.same_site_group_contains_mixed_stop_types === true
      || operationTypes.length > 1,
    isAspiratorVisit: item?.requiresAspirator === true
      || item?.requires_aspirator === true
      || item?.requires_aspiration === true
      || Number(item?.aspirator_service_minutes) > 0
      || Number(item?.aspiratorServiceMinutes) > 0,
    labelKey,
    operationTypes,
    stopType,
    travelMinutes: resolveOperationalVisitTravelMinutes(item),
  };
}

function doOperationalVisitItemsShareSite(left, right) {
  if (left.groupKey && right.groupKey && left.groupKey === right.groupKey) {
    return true;
  }

  if (left.coordinateKey && right.coordinateKey) {
    return left.coordinateKey === right.coordinateKey;
  }

  if (left.addressKey && right.addressKey) {
    return left.addressKey === right.addressKey;
  }

  return Boolean(left.labelKey && right.labelKey && left.labelKey === right.labelKey);
}

function shouldAppendToOperationalVisit(previous, current) {
  if (!previous || !current) {
    return false;
  }

  if (previous.groupKey && current.groupKey && previous.groupKey === current.groupKey) {
    return true;
  }

  if (current.travelMinutes > 0 || !doOperationalVisitItemsShareSite(previous, current)) {
    return false;
  }

  if (
    previous.stopType === current.stopType
    && previous.groupKey
    && current.groupKey
    && previous.groupKey !== current.groupKey
  ) {
    return false;
  }

  if (previous.stopType !== current.stopType) {
    return previous.isAspiratorVisit
      || current.isAspiratorVisit
      || previous.hasMixedStopTypes
      || current.hasMixedStopTypes;
  }

  return true;
}

function buildOperationalVisitSummary(items = [], source = "trip") {
  const visits = [];
  let currentVisit = null;
  let previousMetadata = null;
  const orderedMetadata = orderOperationalVisitItems(items)
    .map((item) => buildOperationalVisitMetadata(item, source));
  const groupFlagsByKey = new Map();

  orderedMetadata.forEach((metadata) => {
    if (!metadata.groupKey || !metadata.stopType) {
      return;
    }

    const groupFlags = groupFlagsByKey.get(metadata.groupKey) || {
      hasMixedStopTypes: false,
      isAspiratorVisit: false,
      operationTypes: new Set(),
    };
    groupFlags.hasMixedStopTypes = groupFlags.hasMixedStopTypes || metadata.hasMixedStopTypes;
    groupFlags.isAspiratorVisit = groupFlags.isAspiratorVisit || metadata.isAspiratorVisit;
    metadata.operationTypes.forEach((operationType) => groupFlags.operationTypes.add(operationType));
    groupFlagsByKey.set(metadata.groupKey, groupFlags);
  });

  orderedMetadata.forEach((metadata) => {
    if (!metadata.stopType) {
      currentVisit = null;
      previousMetadata = null;
      return;
    }

    const groupFlags = metadata.groupKey ? groupFlagsByKey.get(metadata.groupKey) : null;
    if (groupFlags) {
      metadata.hasMixedStopTypes = groupFlags.hasMixedStopTypes || groupFlags.operationTypes.size > 1;
      metadata.isAspiratorVisit = groupFlags.isAspiratorVisit;
      metadata.operationTypes = Array.from(groupFlags.operationTypes);
    }

    if (!currentVisit || !shouldAppendToOperationalVisit(previousMetadata, metadata)) {
      currentVisit = {
        stopTypes: new Set(),
      };
      visits.push(currentVisit);
    }

    metadata.operationTypes.forEach((operationType) => currentVisit.stopTypes.add(operationType));

    previousMetadata = metadata;
  });

  return {
    deliveryCount: visits.filter((visit) => visit.stopTypes.has("delivery")).length,
    hasOperationalData: visits.length > 0,
    loadingCount: visits.filter((visit) => visit.stopTypes.has("loading")).length,
    note: "Le compteur regroupe les étapes consécutives d'une même visite physique. Un retour ultérieur au même site compte comme une nouvelle visite.",
    totalCount: visits.length,
  };
}

function buildDisplayedStopSummaryFromRouteSequence(routeSequence = null) {
  const sequenceItems = Array.isArray(routeSequence?.items) ? routeSequence.items.filter(Boolean) : [];
  if (!sequenceItems.length) {
    return null;
  }

  const summary = buildOperationalVisitSummary(sequenceItems, "route-sequence");
  return summary.hasOperationalData ? summary : null;
}

function buildDisplayedStopSummaryFromTrip(trip = null) {
  const stops = Array.isArray(trip?.stops) ? trip.stops.filter(Boolean) : [];
  if (!stops.length) {
    return {
      deliveryCount: 0,
      hasOperationalData: false,
      loadingCount: 0,
      note: "Le compteur dénombre les sites de chargement et de livraison de la tournée.",
      totalCount: 0,
    };
  }

  return buildOperationalVisitSummary(stops);
}

function resolveDisplayedStopSummary(trip, routeSequence = null) {
  return buildDisplayedStopSummaryFromRouteSequence(routeSequence) || buildDisplayedStopSummaryFromTrip(trip);
}

function sumTripStopServiceMinutesByType(trip, stopType) {
  const stops = Array.isArray(trip?.stops) ? trip.stops : [];

  return stops.reduce((totalMinutes, stop) => {
    if (stop?.stop_type !== stopType) {
      return totalMinutes;
    }

    const serviceMinutes = Number(stop?.service_time_minutes);
    return Number.isFinite(serviceMinutes) ? totalMinutes + serviceMinutes : totalMinutes;
  }, 0);
}

function formatServiceRateLabel(value) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return Number.isInteger(numericValue)
    ? String(numericValue)
    : numericValue.toFixed(2).replace(/\.0+$/, "").replace(/(\.[1-9]*)0+$/, "$1");
}

export function resolveServiceValidationMeta(resultsData = {}) {
  const routingDiagnostics = Array.isArray(resultsData?.routing_diagnostics)
    ? resultsData.routing_diagnostics.filter(Boolean)
    : [];
  const routingAddressCount = routingDiagnostics.filter((entry) => entry?.address_action_required === true).length;
  const routingServiceIssueCount = routingDiagnostics.filter(
    (entry) => entry?.kind === "route" && entry?.address_action_required !== true && entry?.severity === "error",
  ).length;
  const serviceHealthIssues = collectServiceValidationIssues(resultsData);
  const serviceHealthIssueCount = serviceHealthIssues.length;
  const serviceIssueLabels = serviceHealthIssues.map((item) => item.label);
  const effectiveServiceIssueCount = serviceHealthIssueCount > 0 ? serviceHealthIssueCount : routingServiceIssueCount;
  const serviceValidationAlert = routingAddressCount > 0 || effectiveServiceIssueCount > 0;
  // Un service seulement dégradé est rangé dans « Avertissements » : le clic y mène.
  const errorViewName = serviceHealthIssueCount > 0 && serviceHealthIssues.every((item) => item.status === "degraded")
    ? "warnings"
    : "errors";

  // Voyant des services (pas l'onglet Alertes) : « Services OK » évite la confusion.
  let value = "Services OK";
  let note = "Validation et services disponibles.";
  let tone = "success";
  let errorFilter = "";

  if (effectiveServiceIssueCount > 0 && routingAddressCount > 0) {
    value = `${effectiveServiceIssueCount} incident(s) service • ${routingAddressCount} adresse(s)`;
    note = serviceIssueLabels.length > 0
      ? `${serviceIssueLabels.join(", ")} en incident. ${routingAddressCount} adresse(s) à corriger avant relance.`
      : `${effectiveServiceIssueCount} incident(s) techniques à relancer et ${routingAddressCount} adresse(s) à corriger.`;
    tone = "danger";
    errorFilter = "service_health";
  } else if (effectiveServiceIssueCount > 0) {
    value = effectiveServiceIssueCount > 1 ? `${effectiveServiceIssueCount} services en incident` : "1 service en incident";
    note = serviceIssueLabels.length > 0
      ? `${serviceIssueLabels.join(", ")} demandent une vérification avant relance.`
      : `${effectiveServiceIssueCount} incident(s) techniques de service à analyser.`;
    tone = "danger";
    errorFilter = "service_health";
  } else if (routingAddressCount > 0) {
    value = `Services disponibles • ${routingAddressCount} adresse(s)`;
    note = `${routingAddressCount} adresse(s) à corriger avant relance, sans incident service détecté.`;
    errorFilter = "technical_precheck";
  } else if (serviceValidationAlert) {
    value = "validation incomplète";
    note = "Le statut du précheck technique n'a pas pu être qualifié.";
    tone = "warning";
  }

  return {
    errorFilter,
    errorSource: serviceValidationAlert ? "results_overview" : "",
    errorViewName,
    isInteractive: serviceValidationAlert,
    label: "Validation & services",
    note,
    routingAddressCount,
    routingServiceIssueCount,
    serviceHealthIssueCount,
    serviceIssueCount: effectiveServiceIssueCount,
    serviceIssueLabels,
    serviceIssueMessage: serviceIssueLabels.length > 0
      ? `${serviceIssueLabels.join(", ")} en incident`
      : effectiveServiceIssueCount > 0
        ? `${effectiveServiceIssueCount} service(s) en incident`
        : "",
    serviceValidationAlert,
    shouldPulse: effectiveServiceIssueCount > 0,
    tone,
    value,
  };
}

function getSafeResultsNumber(source, key) {
  const value = source && typeof source === "object" ? source[key] : null;
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

export function resolveLiveSuspendedDeliveryStats(resultsData = {}, planningSummary = {}) {
  const suspendedDeliveries = Array.isArray(resultsData?.suspended_deliveries)
    ? resultsData.suspended_deliveries.filter((item) => item && typeof item === "object")
    : [];

  if (!suspendedDeliveries.length) {
    return {
      suspendedCount: getSafeResultsNumber(planningSummary, "suspended_count"),
      suspendedQuantityTons: getSafeResultsNumber(planningSummary, "suspended_quantity_tons"),
    };
  }

  return {
    suspendedCount: suspendedDeliveries.length,
    suspendedQuantityTons: Math.round(suspendedDeliveries.reduce((totalQuantity, item) => {
      const quantityTons = Number(item?.quantity_tons);
      return totalQuantity + (Number.isFinite(quantityTons) ? quantityTons : 0);
    }, 0) * 10) / 10,
  };
}

const BUSINESS_NON_ASSIGNABLE_BREAKDOWN_LABELS = {
  compatibility: "Camion incompatible",
  capacity: "Capacité insuffisante",
  timing: "Horaires / temps disponible",
  planning: "Contraintes planning",
};

function normalizeMetricDiagnosticText(value) {
  if (typeof value !== "string") {
    return "";
  }

  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function formatSuspendedLocationAddress(value) {
  const normalizedAddress = normalizeAddressDisplayLabel(value);
  if (!normalizedAddress) {
    return "";
  }

  const mainSegment = normalizedAddress.split(";")[0]?.trim() || normalizedAddress;
  const parts = mainSegment
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) {
    return "";
  }

  if (parts.length === 1) {
    return parts[0];
  }

  return `${parts[0]}, ${parts[1]}`;
}

function buildSuspendedLocationLine(clientName, address) {
  const normalizedClientName = normalizeClientDisplayLabel(clientName) || "À confirmer";
  const normalizedAddress = formatSuspendedLocationAddress(address);

  return {
    address: normalizedAddress,
    name: normalizedClientName,
  };
}

const SUSPENDED_HISTORICAL_SOURCE_REASON_CODES = new Set([
  "already_assigned",
  "current_truck",
  "truck_in_use",
]);

function normalizeSuspendedHistoricalSourceCompatibility(entry, currentTruckId = "") {
  const normalizedCurrentTruckId = normalizeInlineText(currentTruckId);
  const normalizedTruckId = normalizeInlineText(entry?.truck_id);
  const normalizedReasonCode = normalizeInlineText(entry?.reason_code).toLowerCase();
  if (
    !entry
    || !normalizedCurrentTruckId
    || normalizedTruckId !== normalizedCurrentTruckId
    || entry.is_current_truck !== true
    || !SUSPENDED_HISTORICAL_SOURCE_REASON_CODES.has(normalizedReasonCode)
  ) {
    return entry;
  }

  return {
    ...entry,
    detail: "Ce camion peut reprendre la commande suspendue; sa tournée sera validée lors de la réactivation.",
    is_assignable: true,
    is_current_truck: false,
    reason_code: "local_rebuild_check_pending",
    reason_label: "Validation locale requise",
    tone: "warning",
  };
}

function filterAssignableTruckCompatibilities(entries, currentTruckId = "") {
  return (Array.isArray(entries) ? entries : [])
    .map((entry) => normalizeSuspendedHistoricalSourceCompatibility(entry, currentTruckId))
    .filter((entry) => (
      entry
      && isTruckCompatibilityActionable(entry)
      && typeof entry.truck_id === "string"
      && entry.truck_id.trim()
    ));
}

function resolveSuspendedDeliveryReference(state, item) {
  const safeDeliveryId = normalizeInlineText(item?.delivery_id);
  const safeOrderId = normalizeInlineText(item?.order_id);
  if (!safeDeliveryId && !safeOrderId) {
    return null;
  }

  const planningItem = state?.deliveryItemsByOrderId?.get?.(safeDeliveryId)
    || (safeOrderId ? state?.deliveryItemsByOrderId?.get?.(safeOrderId) : null)
    || null;
  if (planningItem) {
    return planningItem;
  }

  const assignments = Array.isArray(state?.assignmentList)
    ? state.assignmentList
    : (Array.isArray(state?.resultsData?.assignments) ? state.resultsData.assignments : []);

  for (const assignment of assignments) {
    const deliveryItem = Array.isArray(assignment?.delivery_items)
      ? assignment.delivery_items.find((entry) => (
        normalizeInlineText(entry?.delivery_id) === safeDeliveryId
        || (safeOrderId && normalizeInlineText(entry?.order_id) === safeOrderId)
      )) || null
      : null;
    if (deliveryItem) {
      return deliveryItem;
    }

    const assignmentOrder = Array.isArray(assignment?.orders)
      ? assignment.orders.find((order) => normalizeInlineText(order?.order_id) === safeOrderId) || null
      : null;
    if (assignmentOrder) {
      return assignmentOrder;
    }

    for (const trip of assignment?.trips || []) {
      const matchingOrder = Array.isArray(trip?.orders)
        ? trip.orders.find((order) => {
            if (normalizeInlineText(order?.order_id) === safeOrderId) {
              return true;
            }

            const deliveries = Array.isArray(order?.deliveries) ? order.deliveries : [];
            return deliveries.some((delivery) => normalizeInlineText(delivery?.delivery_id) === safeDeliveryId);
          }) || null
        : null;

      if (matchingOrder) {
        return matchingOrder;
      }

      const matchingClient = Array.isArray(trip?.clients)
        ? trip.clients.find((client) => normalizeInlineText(client?.delivery_id) === safeDeliveryId) || null
        : null;
      if (matchingClient) {
        const clientOrder = Array.isArray(trip?.orders)
          ? trip.orders.find((order) => normalizeInlineText(order?.order_id) === normalizeInlineText(matchingClient?.order_id)) || null
          : null;
        const matchingDelivery = clientOrder && Array.isArray(clientOrder.deliveries)
          ? clientOrder.deliveries.find((delivery) => normalizeInlineText(delivery?.delivery_id) === safeDeliveryId) || null
          : null;

        return {
          ...(clientOrder && typeof clientOrder === "object" ? clientOrder : {}),
          ...(matchingDelivery && typeof matchingDelivery === "object" ? matchingDelivery : {}),
          ...(matchingClient && typeof matchingClient === "object" ? matchingClient : {}),
        };
      }
    }
  }

  if (Array.isArray(state?.resultsData?.unassigned_orders)) {
    return state.resultsData.unassigned_orders.find((order) => normalizeInlineText(order?.order_id) === safeOrderId) || null;
  }

  return null;
}

function resolveSuspendedDeliveryDetailReference(state, item) {
  const reference = resolveSuspendedDeliveryReference(state, item);
  if (!reference || typeof reference !== "object") {
    return null;
  }

  const safeDeliveryId = normalizeInlineText(item?.delivery_id);
  if (safeDeliveryId && Array.isArray(reference.deliveries)) {
    const matchingDelivery = reference.deliveries.find(
      (delivery) => normalizeInlineText(delivery?.delivery_id) === safeDeliveryId,
    );
    if (matchingDelivery) {
      return {
        ...reference,
        ...matchingDelivery,
      };
    }
  }

  return reference;
}

function normalizeSuspendedDetailText(value) {
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return "";
}

function normalizeSuspendedBooleanText(value) {
  if (typeof value === "string") {
    return value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase();
  }
  return "";
}

function normalizeSuspendedBooleanValue(value) {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return value !== 0;
  }

  const normalizedValue = normalizeSuspendedBooleanText(value);
  if (["1", "o", "oui", "true", "vrai", "yes"].includes(normalizedValue)) {
    return true;
  }
  if (["0", "n", "non", "false", "faux", "no"].includes(normalizedValue)) {
    return false;
  }
  return null;
}

function resolveSuspendedDetailValue(source, keys) {
  if (!source || typeof source !== "object" || !Array.isArray(keys)) {
    return "";
  }

  for (const key of keys) {
    const value = normalizeSuspendedDetailText(source[key]);
    if (value) {
      return value;
    }
  }

  const rawData = source.raw_data && typeof source.raw_data === "object" ? source.raw_data : null;
  if (!rawData) {
    return "";
  }

  for (const key of keys) {
    const value = normalizeSuspendedDetailText(rawData[key]);
    if (value) {
      return value;
    }
  }

  return "";
}

function resolveSuspendedBooleanValue(source, keys) {
  if (!source || typeof source !== "object" || !Array.isArray(keys)) {
    return null;
  }

  for (const key of keys) {
    const normalizedValue = normalizeSuspendedBooleanValue(source[key]);
    if (normalizedValue != null) {
      return normalizedValue;
    }
  }

  const rawData = source.raw_data && typeof source.raw_data === "object" ? source.raw_data : null;
  if (!rawData) {
    return null;
  }

  for (const key of keys) {
    const normalizedValue = normalizeSuspendedBooleanValue(rawData[key]);
    if (normalizedValue != null) {
      return normalizedValue;
    }
  }

  return null;
}

const SUSPENDED_SILO_DETAIL_KEYS = [
  "loading_silo",
  "loading_silo_count",
  "loadingSilo",
  "silo",
  "silo_count",
  "Silo chargement",
  "Silo",
];

const SUSPENDED_BUILDING_DETAIL_KEYS = [
  "building",
  "buildingLabel",
  "batiment",
  "Bâtiment",
  "Batiment",
];

const SUSPENDED_DOMPEUR_DETAIL_KEYS = [
  "is_dompeur",
  "isDompeur",
  "dompeur",
  "Dompeur",
];

const SUSPENDED_WORK_ORDER_DETAIL_KEYS = [
  "work_order_number",
  "workOrderNumber",
  "workOrderLabel",
  "bon_travail",
  "bon_de_travail",
  "Bon de travail",
  "Bon travail",
];

function resolveSuspendedDeliveryWorkOrderLabel(state, item) {
  if (item?.source_metadata_fields?.includes("work_order_number")) {
    return normalizeInlineText(item.work_order_number);
  }
  const reference = resolveSuspendedDeliveryDetailReference(state, item);
  return resolveSuspendedDetailValue(item, SUSPENDED_WORK_ORDER_DETAIL_KEYS)
    || resolveSuspendedDetailValue(reference, SUSPENDED_WORK_ORDER_DETAIL_KEYS);
}

function resolveSuspendedDeliverySiloLabel(state, item) {
  if (item?.source_metadata_fields?.includes("loading_silo")) {
    return normalizeInlineText(item.loading_silo);
  }
  const reference = resolveSuspendedDeliveryDetailReference(state, item);
  return resolveSuspendedDetailValue(item, SUSPENDED_SILO_DETAIL_KEYS)
    || resolveSuspendedDetailValue(reference, SUSPENDED_SILO_DETAIL_KEYS);
}

function resolveSuspendedDeliveryBuildingLabel(state, item) {
  if (item?.source_metadata_fields?.includes("building")) {
    return normalizeInlineText(item.building);
  }
  const reference = resolveSuspendedDeliveryDetailReference(state, item);
  return resolveSuspendedDetailValue(item, SUSPENDED_BUILDING_DETAIL_KEYS)
    || resolveSuspendedDetailValue(reference, SUSPENDED_BUILDING_DETAIL_KEYS);
}

function resolveSuspendedDeliveryIsDompeur(state, item) {
  const itemValue = resolveSuspendedBooleanValue(item, SUSPENDED_DOMPEUR_DETAIL_KEYS);
  if (itemValue != null) {
    return itemValue;
  }

  const reference = resolveSuspendedDeliveryDetailReference(state, item);
  const referenceValue = resolveSuspendedBooleanValue(reference, SUSPENDED_DOMPEUR_DETAIL_KEYS);
  return referenceValue === true;
}

function resolveSuspendedDeliveryCompatibilities(state, item) {
  const currentTruckId = normalizeInlineText(item?.current_truck_id || item?.currentTruckId);
  if (Array.isArray(item?.truck_compatibility)) {
    return filterAssignableTruckCompatibilities(item.truck_compatibility, currentTruckId);
  }
  if (Array.isArray(item?.compatibilities)) {
    return filterAssignableTruckCompatibilities(item.compatibilities, currentTruckId);
  }

  const reference = resolveSuspendedDeliveryReference(state, item);
  const referenceCurrentTruckId = currentTruckId || normalizeInlineText(
    reference?.current_truck_id
    || reference?.currentTruckId
    || reference?.source_truck_id,
  );
  if (Array.isArray(reference?.compatibilities)) {
    return filterAssignableTruckCompatibilities(reference.compatibilities, referenceCurrentTruckId);
  }
  return filterAssignableTruckCompatibilities(reference?.truck_compatibility, referenceCurrentTruckId);
}

function getResultsErrorEntries(resultsData = {}) {
  return Array.isArray(resultsData?.error_entries)
    ? resultsData.error_entries.filter((entry) => entry && typeof entry === "object")
    : [];
}

function getBusinessNonAssignableEntries(resultsData = {}) {
  const seenKeys = new Set();

  return getResultsErrorEntries(resultsData).filter((entry, index) => {
    const severity = normalizeMetricDiagnosticText(entry?.severity);
    const rejectionCategory = normalizeMetricDiagnosticText(entry?.rejection_category);
    const filterKey = normalizeMetricDiagnosticText(entry?.filter_key);

    if (severity && severity !== "error") {
      return false;
    }

    if (rejectionCategory !== "business_assignment" && filterKey !== "non_assignable_orders") {
      return false;
    }

    const uniqueKey = typeof entry?.order_id === "string" && entry.order_id.trim()
      ? entry.order_id.trim()
      : typeof entry?.error_id === "string" && entry.error_id.trim()
        ? entry.error_id.trim()
        : `business-non-assignable-${index}`;

    if (seenKeys.has(uniqueKey)) {
      return false;
    }

    seenKeys.add(uniqueKey);
    return true;
  });
}

function classifyBusinessNonAssignableEntry(entry) {
  if (entry?.technical_details?.diagnostic_type === "time_window") {
    return "timing";
  }

  const diagnosticText = normalizeMetricDiagnosticText([
    entry?.title,
    entry?.cause,
    entry?.technical_summary,
  ].filter(Boolean).join(" "));

  if (/(capacite|compartiment)/.test(diagnosticText)) {
    return "capacity";
  }

  if (/(compatible|produit|verrouill)/.test(diagnosticText)) {
    return "compatibility";
  }

  if (/(temps|horaire|fenetre|journalier|insertion)/.test(diagnosticText)) {
    return "timing";
  }

  return "planning";
}

function buildBusinessNonAssignableHoverCardDetails(resultsData = {}, businessNonAssignableCount = 0) {
  const entries = getBusinessNonAssignableEntries(resultsData);
  const breakdown = {
    compatibility: 0,
    capacity: 0,
    timing: 0,
    planning: 0,
  };

  entries.forEach((entry) => {
    breakdown[classifyBusinessNonAssignableEntry(entry)] += 1;
  });

  const lines = Object.entries(BUSINESS_NON_ASSIGNABLE_BREAKDOWN_LABELS)
    .map(([key, label]) => ({
      label,
      value: String(breakdown[key] || 0),
    }))
    .filter((line) => businessNonAssignableCount > 0 && Number(line.value) > 0);

  if (businessNonAssignableCount > 0 && !lines.length) {
    lines.push({
      label: "Blocages métier",
      value: String(businessNonAssignableCount),
    });
  }

  return {
    type: "formula",
    lines,
    total: String(businessNonAssignableCount),
    totalLabel: "Commandes bloquées",
    note: businessNonAssignableCount > 0
      ? "Commandes bloquées par les contraintes actuelles de la flotte. Cliquez pour voir les causes."
      : "Aucune commande bloquée par les contraintes actuelles de la flotte.",
  };
}

function getFirstFiniteNumber(...values) {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }

  return null;
}

function sumFiniteNumbers(...values) {
  const numbers = values.filter((value) => typeof value === "number" && Number.isFinite(value));
  return numbers.length ? numbers.reduce((total, value) => total + value, 0) : null;
}

function sumFiniteDiagnosticStagesByPrefix(stages = {}, prefix = "") {
  if (!stages || typeof stages !== "object" || Array.isArray(stages) || !prefix) {
    return null;
  }

  return sumFiniteNumbers(
    ...Object.entries(stages)
      .filter(([stageKey]) => stageKey.startsWith(prefix))
      .map(([, stageValue]) => stageValue),
  );
}

function formatCadRate(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }

  return value.toFixed(2).replace(".", ",");
}

function cleanBackendNote(note) {
  if (typeof note !== "string") {
    return note;
  }

  return normalizeCurrencyDisplay(note
    .replace(/Vitesse_moy/g, "vitesse moyenne")
    .replace(/_/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim());
}

function normalizeRouteDistanceSource(candidate) {
  const source = typeof candidate === "string" ? candidate.trim().toLowerCase() : "";
  if (source === "geometry") {
    return "geometrymeasured";
  }
  if (source === "estimate") {
    return "haversineestimate";
  }
  if (source === "roadapi" || source === "geometrymeasured" || source === "haversineestimate" || source === "unavailable") {
    return source;
  }

  return "unavailable";
}

function getRouteDistanceSourceLabel(source) {
  if (source === "roadapi") {
    return null;
  }
  if (source === "geometrymeasured") {
    return "Mesuré (géométrie)";
  }
  if (source === "haversineestimate") {
    return "Estimé (haversine)";
  }

  return "Indisponible";
}

function getRouteDistanceHoverSourceLabel(source, sourceLabel = "") {
  if (source === "roadapi") {
    return "HERE Routing (mode camion)";
  }

  return sourceLabel || "Indisponible";
}

function joinNoteSections(...sections) {
  return sections
    .map((section) => (typeof section === "string" ? section.trim() : ""))
    .filter(Boolean)
    .join(" ");
}

function buildDistanceKpiMeta(source, hasDistance, sourceLabel) {
  if (!hasDistance) {
    return {
      label: "Distance",
      title: "Distance indisponible pour la tournée sélectionnée.",
      note: "Aucune distance exploitable n'est disponible pour la tournée sélectionnée.",
    };
  }

  if (source === "unavailable") {
    return {
      label: "Distance",
      title: "Distance disponible sans source qualifiée.",
      note: "La distance affichée est disponible, mais sa source métier n'a pas été remontée. Elle ne doit pas être interprétée comme une distance routée confirmée.",
    };
  }

  if (source === "geometrymeasured") {
    return {
      label: "Distance (mesurée)",
      title: sourceLabel
        ? `Distance mesurée de la tournée sélectionnée. Source: ${sourceLabel}.`
        : "Distance mesurée de la tournée sélectionnée.",
      note: sourceLabel
        ? `Cet agrégat reste une distance planifiée mesurée à partir de la géométrie. Source: ${sourceLabel}.`
        : "Cet agrégat reste une distance planifiée mesurée à partir de la géométrie.",
    };
  }

  if (source === "haversineestimate") {
    return {
      label: "Distance (estimée)",
      title: sourceLabel
        ? `Distance estimée de la tournée sélectionnée. Source: ${sourceLabel}.`
        : "Distance estimée de la tournée sélectionnée.",
      note: sourceLabel
        ? `Cet agrégat reste une distance planifiée estimée à vol d'oiseau. Source: ${sourceLabel}.`
        : "Cet agrégat reste une distance planifiée estimée à vol d'oiseau.",
    };
  }

  return {
    label: "Distance (API)",
    title: "Distance routée de la tournée sélectionnée.",
    note: "Cet agrégat reprend la distance du trajet routier fourni par HERE en mode camion.",
  };
}

function resolveEstimatedRouteDistanceSource(route) {
  const source = normalizeRouteDistanceSource(route?.estimated_distance_source);
  return source;
}

function resolveActualRouteDistanceSource(route) {
  const source = normalizeRouteDistanceSource(route?.actual_distance_source);
  return source;
}

function buildEstimatedCostDetails(assignment, trip) {
  const rawData = assignment?.truck_raw_data && typeof assignment.truck_raw_data === "object"
    ? assignment.truck_raw_data
    : {};
  const route = trip?.route || {};
  const tariffKm = getFirstFiniteNumber(
    assignment?.fuel_cost_per_km_cad,
    rawData["Tarif km (CAD/km)"],
  );
  const tariffMin = getFirstFiniteNumber(
    assignment?.waiting_cost_per_minute_cad,
    rawData["Tarif minute attentes (CAD/min)"],
  );

  if (tariffKm === null && tariffMin === null) {
    return null;
  }

  return {
    tarifKm: formatCadRate(tariffKm),
    tarifMin: formatCadRate(tariffMin),
    distanceLabel: route.estimated_distance_label || null,
    durationLabel: route.driving_duration_label || null,
    totalLabel: route?.estimated_cost_label ? normalizeCurrencyDisplay(route.estimated_cost_label) : null,
  };
}

function formatDurationMinutesLabel(minutes) {
  if (typeof minutes !== "number" || !Number.isFinite(minutes) || minutes < 0) {
    return null;
  }

  const roundedMinutes = Math.round(minutes);
  const hours = Math.floor(roundedMinutes / 60);
  const remainingMinutes = roundedMinutes % 60;

  if (hours <= 0) {
    return `${remainingMinutes} min`;
  }

  if (remainingMinutes === 0) {
    return `${hours} h`;
  }

  return `${hours} h ${remainingMinutes} min`;
}

function isFiniteDiagnosticNumber(value) {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

function formatDiagnosticInteger(value) {
  return DIAGNOSTIC_INTEGER_FORMATTER.format(Math.round(value));
}

export function formatDiagnosticDurationLabel(valueMs) {
  if (!isFiniteDiagnosticNumber(valueMs)) {
    return null;
  }

  if (valueMs < 1000) {
    return `${formatDiagnosticInteger(valueMs)} ms`;
  }

  if (valueMs < 60000) {
    return `${DIAGNOSTIC_SECONDS_FORMATTER.format(valueMs / 1000)} s`;
  }

  return formatDurationMinutesLabel(valueMs / 60000);
}

function isRunDiagnosticsObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function resolveRunDiagnostics(source = null) {
  if (!isRunDiagnosticsObject(source)) {
    return null;
  }

  if (isRunDiagnosticsObject(source.planningDiagnostics)) {
    return source.planningDiagnostics;
  }

  if (isRunDiagnosticsObject(source._planning_diagnostics)) {
    return source._planning_diagnostics;
  }

  if (isRunDiagnosticsObject(source.planning_diagnostics)) {
    return source.planning_diagnostics;
  }

  if (isRunDiagnosticsObject(source.optimization_diagnostics)) {
    return source.optimization_diagnostics;
  }

  if (isRunDiagnosticsObject(source.optimizationDiagnostics)) {
    return source.optimizationDiagnostics;
  }

  if (isRunDiagnosticsObject(source.diagnostics)) {
    return source.diagnostics;
  }

  if ("total_backend_ms" in source || "stages_ms" in source || "routing" in source) {
    return source;
  }

  return null;
}

function resolvePerformanceSummary(diagnostics = null) {
  const resolvedDiagnostics = resolveRunDiagnostics(diagnostics);
  const performanceSummary = resolvedDiagnostics?.performance_summary;

  return performanceSummary && typeof performanceSummary === "object" && !Array.isArray(performanceSummary)
    ? performanceSummary
    : null;
}

const EXECUTION_SUMMARY_METRIC_DEFINITIONS = [
  {
    key: "backend-total",
    label: "Backend total",
    noticeDescription: "Temps total passé côté backend pour produire ce résultat, de la lecture des données jusqu'à la réponse finale.",
		noticeExample: "Inclut notamment la lecture des données source importées, le solveur, l'enrichissement HERE et l'écriture du snapshot.",
    noticeLabel: "Backend",
    type: "value",
    formatter: formatDiagnosticDurationLabel,
    selectValue: (diagnostics) => getFirstFiniteNumber(
      resolvePerformanceSummary(diagnostics)?.backend_total_ms,
      diagnostics?.total_backend_ms,
    ),
  },
  {
    key: "client-cohesion-validation",
    label: "Cohésion client",
    note: "Validation finale, réparations post-solveur et scan de conflits",
    type: "value",
    formatter: formatDiagnosticDurationLabel,
    selectNote: (diagnostics) => {
      const detail = diagnostics?.validate_client_cohesion_detail;
      if (!detail || typeof detail !== "object") {
        return "Validation finale, réparations post-solveur et scan de conflits";
      }

      const performanceSummary = resolvePerformanceSummary(diagnostics);
      const referenceMetrics = diagnostics?.reference_metrics && typeof diagnostics.reference_metrics === "object"
        ? diagnostics.reference_metrics
        : {};
      const sameSitePostPass = detail?.same_site_post_pass && typeof detail.same_site_post_pass === "object"
        ? detail.same_site_post_pass
        : {};

      const noteSegments = [
        "Validation finale, réparations post-solveur et scan de conflits",
      ];
      const clientCohesionMs = getFirstFiniteNumber(
        diagnostics?.stages_ms?.validate_client_cohesion,
        referenceMetrics?.validate_client_cohesion_ms,
      );
      const backendComparisonDurations = [
        getFirstFiniteNumber(performanceSummary?.db_read_ms, diagnostics?.stages_ms?.load_workbook_data),
        getFirstFiniteNumber(performanceSummary?.transformation_ms),
        getFirstFiniteNumber(performanceSummary?.address_validation_ms),
        getFirstFiniteNumber(performanceSummary?.solver_preparation_ms),
        getFirstFiniteNumber(performanceSummary?.persistence_ms),
        getFirstFiniteNumber(
          performanceSummary?.route_enrichment_ms,
          diagnostics?.stages_ms?.enrich_result_with_actual_routes,
        ),
      ].filter((value) => value != null);
      const clientCohesionDominatesBackendOutsideSolver = clientCohesionMs != null
        && (
          backendComparisonDurations.length === 0
          || backendComparisonDurations.every((value) => clientCohesionMs >= value)
        );
      const payloadRefreshCount = getFirstFiniteNumber(detail?.effective_payload_refresh_count);
      const sameSiteIterationCount = getFirstFiniteNumber(detail?.same_site_iteration_count);
      const payloadRebuildAttemptCount = getFirstFiniteNumber(detail?.same_site_post_pass?.payload_rebuild_attempt_count);
      const payloadRebuildTotalMs = getFirstFiniteNumber(
        sameSitePostPass?.payload_rebuild_total_ms,
        referenceMetrics?.payload_rebuild_total_ms,
      );
      const localSolverMs = getFirstFiniteNumber(
        sameSitePostPass?.local_solver_ms,
        referenceMetrics?.local_solver_ms,
      );
      const orderedRebuildTotalMs = getFirstFiniteNumber(
        sameSitePostPass?.ordered_rebuild_total_ms,
        referenceMetrics?.ordered_rebuild_total_ms,
      );
      const sourceRebuildMs = getFirstFiniteNumber(
        sameSitePostPass?.source_rebuild_ms,
        referenceMetrics?.source_rebuild_ms,
      );
      const targetRebuildMs = getFirstFiniteNumber(
        sameSitePostPass?.target_rebuild_ms,
        referenceMetrics?.target_rebuild_ms,
      );
      const targetLockedScopeEntryCount = getFirstFiniteNumber(
        sameSitePostPass?.target_locked_scope_entry_count,
        referenceMetrics?.target_locked_scope_entry_count,
      );
      const targetLockedScopeEntryMs = getFirstFiniteNumber(
        sameSitePostPass?.target_locked_scope_entry_ms,
        referenceMetrics?.target_locked_scope_entry_ms,
      );
      const targetLockedScopeSameOdExactCount = getFirstFiniteNumber(
        sameSitePostPass?.target_locked_scope_same_od_exact_count,
        referenceMetrics?.target_locked_scope_same_od_exact_count,
      );
      const targetLockedScopeProbeShortCircuitCount = getFirstFiniteNumber(
        sameSitePostPass?.target_locked_scope_probe_short_circuit_count,
        referenceMetrics?.target_locked_scope_probe_short_circuit_count,
      );
      const targetLockedScopeProbeExecuteCount = getFirstFiniteNumber(
        sameSitePostPass?.target_locked_scope_probe_execute_count,
        referenceMetrics?.target_locked_scope_probe_execute_count,
      );
      const targetLockedScopeProbeReplaySuccessCount = getFirstFiniteNumber(
        sameSitePostPass?.target_locked_scope_probe_replay_success_count,
        referenceMetrics?.target_locked_scope_probe_replay_success_count,
      );
      const targetLockedScopeProbeReplayFailureCount = getFirstFiniteNumber(
        sameSitePostPass?.target_locked_scope_probe_replay_failure_count,
        referenceMetrics?.target_locked_scope_probe_replay_failure_count,
      );
      const targetRebuildPrunedByExactFailure = getFirstFiniteNumber(
        sameSitePostPass?.target_rebuild_pruned_by_exact_failure,
        referenceMetrics?.target_rebuild_pruned_by_exact_failure,
      );
      const payloadRebuildPrunedByExactAttemptFailureCount = getFirstFiniteNumber(
        sameSitePostPass?.payload_rebuild_pruned_by_exact_attempt_failure_count,
        referenceMetrics?.payload_rebuild_pruned_by_exact_attempt_failure_count,
      );

      if (payloadRebuildAttemptCount != null && payloadRebuildAttemptCount > 0) {
        noteSegments.push(`${formatDiagnosticInteger(payloadRebuildAttemptCount)} rebuild(s) locaux`);
      } else if (sameSiteIterationCount != null && sameSiteIterationCount > 0) {
        noteSegments.push(`${formatDiagnosticInteger(sameSiteIterationCount)} passe(s) same-site`);
      }

			if (localSolverMs != null && localSolverMs > 0) {
				noteSegments.push(`mini-solveur ${formatDiagnosticDurationLabel(localSolverMs)}`);
			}

      if (targetLockedScopeEntryCount != null && targetLockedScopeEntryCount > 0) {
        const lockedScopeParts = [`locked-scope ${formatDiagnosticInteger(targetLockedScopeEntryCount)} entrée(s)`];
        if (targetLockedScopeEntryMs != null && targetLockedScopeEntryMs > 0) {
          lockedScopeParts.push(formatDiagnosticDurationLabel(targetLockedScopeEntryMs));
        }
        if (targetLockedScopeSameOdExactCount != null && targetLockedScopeSameOdExactCount > 0) {
          lockedScopeParts.push(`${formatDiagnosticInteger(targetLockedScopeSameOdExactCount)} same O/D exact`);
        }
        noteSegments.push(lockedScopeParts.join(" · "));
      }

      if (targetLockedScopeProbeShortCircuitCount != null && targetLockedScopeProbeShortCircuitCount > 0) {
        const probeParts = [`probe locked-scope ${formatDiagnosticInteger(targetLockedScopeProbeShortCircuitCount)} court-circuit(s)`];
        if (targetLockedScopeProbeExecuteCount != null && targetLockedScopeProbeExecuteCount > 0) {
          probeParts.push(`${formatDiagnosticInteger(targetLockedScopeProbeExecuteCount)} entrée(s) gardée(s)`);
        }
        if (targetLockedScopeProbeReplaySuccessCount != null && targetLockedScopeProbeReplaySuccessCount > 0) {
          probeParts.push(`${formatDiagnosticInteger(targetLockedScopeProbeReplaySuccessCount)} replay(s) ok`);
        }
        if (targetLockedScopeProbeReplayFailureCount != null && targetLockedScopeProbeReplayFailureCount > 0) {
          probeParts.push(`${formatDiagnosticInteger(targetLockedScopeProbeReplayFailureCount)} replay(s) KO`);
        }
        noteSegments.push(probeParts.join(" · "));
      }

			if (orderedRebuildTotalMs != null && orderedRebuildTotalMs > 0) {
				noteSegments.push(`replay ${formatDiagnosticDurationLabel(orderedRebuildTotalMs)}`);
			}

      if (payloadRefreshCount != null && payloadRefreshCount > 0) {
        noteSegments.push(`${formatDiagnosticInteger(payloadRefreshCount)} refresh payload`);
      }

			if (targetRebuildPrunedByExactFailure != null && targetRebuildPrunedByExactFailure > 0) {
				noteSegments.push(`${formatDiagnosticInteger(targetRebuildPrunedByExactFailure)} prune(s) exactes`);
			}

      if (
        payloadRebuildPrunedByExactAttemptFailureCount != null
        && payloadRebuildPrunedByExactAttemptFailureCount > 0
      ) {
        noteSegments.push(`${formatDiagnosticInteger(payloadRebuildPrunedByExactAttemptFailureCount)} prune(s) payload exactes`);
      }

      if (clientCohesionDominatesBackendOutsideSolver) {
        if (payloadRebuildTotalMs != null && payloadRebuildTotalMs > 0) {
          noteSegments.push(`payload rebuild ${formatDiagnosticDurationLabel(payloadRebuildTotalMs)}`);
        }
        if (sourceRebuildMs != null && sourceRebuildMs > 0) {
          noteSegments.push(`source rebuild ${formatDiagnosticDurationLabel(sourceRebuildMs)}`);
        }
        if (targetRebuildMs != null && targetRebuildMs > 0) {
          noteSegments.push(`target rebuild ${formatDiagnosticDurationLabel(targetRebuildMs)}`);
        }
      }

      return noteSegments.join(" • ");
    },
    selectValue: (diagnostics, stages) => getFirstFiniteNumber(
      stages?.validate_client_cohesion,
      diagnostics?.reference_metrics?.validate_client_cohesion_ms,
    ),
  },
  {
    key: "solver",
    label: "Solveur",
    noticeDescription: "Temps passé à calculer l'affectation des commandes aux camions.",
    noticeExample: "C'est l'étape où OR-Tools cherche le meilleur plan de tournées selon les contraintes métier.",
    noticeLabel: "Solveur",
    type: "value",
    formatter: formatDiagnosticDurationLabel,
    selectValue: (diagnostics, stages) => getFirstFiniteNumber(
      resolvePerformanceSummary(diagnostics)?.solver_ms,
      stages?.optimize_orders_to_trucks,
    ),
  },
  {
    key: "here-enrichment",
    label: "HERE enrichissement",
    note: "Enrichissement des routes",
    noticeDescription: "Temps passé à compléter le plan avec les trajets routiers HERE et leurs métriques réelles.",
    noticeExample: "Cette étape remplit les distances, durées et géométries de route utilisées dans l'affichage final.",
    noticeLabel: "HERE enrich.",
    type: "value",
    formatter: formatDiagnosticDurationLabel,
    selectValue: (diagnostics, stages) => getFirstFiniteNumber(
      resolvePerformanceSummary(diagnostics)?.route_enrichment_ms,
      stages?.enrich_result_with_actual_routes,
    ),
  },
  {
    key: "route-cache",
    label: "Cache route",
    noticeDescription: "Nombre de relectures du cache route persistant pendant ce run. Chaque ligne du détail représente un trajet origine → destination, pas une adresse isolée. Le total affiché ne correspond pas au nombre total d'entrées stockées dans le cache, et un même trajet peut être relu plusieurs fois s'il est réutilisé à différents endroits. Les trajets repris directement depuis le snapshot précédent n'entrent pas dans ce compteur.",
    noticeExample: "Exemple : le segment Chargement → Client est relu depuis le cache local au lieu d'être redemandé à HERE Routing.",
    noticeExamplesKey: "persistent_cache_hit_examples",
    noticeExampleRecordsKey: "persistent_cache_hit_example_records",
    noticeExamplesTitle: "Trajets relus pendant ce run",
    noticeLabel: "Routes en cache",
    type: "cache",
    selectHitCount: (_, __, routing) => routing?.persistent_cache_hit_count,
    selectMissCount: (_, __, routing) => routing?.persistent_cache_miss_count,
  },
  {
    key: "geocode-cache",
    label: "Cache géocodage",
    noticeDescription: "Nombre de relectures du cache de géocodage pendant ce run pour retrouver des coordonnées déjà connues. Ici, chaque ligne correspond à un point unique, pas à un trajet entre deux points. Le total affiché ne correspond pas au nombre total d'entrées stockées dans le cache, et une même adresse peut être relue plusieurs fois si plusieurs étapes ou entités la réutilisent. Ce compteur décrit les lectures d'adresse ; un hit n'exclut pas un appel fournisseur si une validation stricte déclenche un fallback réseau.",
    noticeExample: "Exemple : l'adresse d'un client ou d'un chargement est reprise depuis le cache de géocodage pour retrouver ses coordonnées.",
    noticeExamplesKey: "geocode_cache_hit_examples",
    noticeExampleRecordsKey: "geocode_cache_hit_example_records",
    noticeExamplesTitle: "Adresses relues pendant ce run",
    noticeLabel: "Géocodage cache",
    type: "cache",
    selectHitCount: (_, __, routing) => routing?.geocode_cache_hit_count,
    selectMissCount: (_, __, routing) => routing?.geocode_cache_miss_count,
  },
  {
    key: "here-calls",
    label: "Appels HERE",
    note: "Requêtes HERE Routing",
    noticeDescription: "Nombre de requêtes HERE Routing réellement envoyées pendant ce run.",
    noticeExample: "Un total à 0 signifie généralement que les trajets utiles étaient déjà disponibles via le cache ou un snapshot réutilisé.",
    noticeLabel: "Appels HERE",
    type: "value",
    formatter: formatDiagnosticInteger,
    selectValue: (_, __, routing) => routing?.here_request_count,
  },
];

function buildExecutionSummaryMetric(config, diagnostics, stages, routing) {
  if (config.type === "cache") {
    return buildDecisionDiagnosticCacheMetric({
      key: config.key,
      label: config.label,
      hitCount: config.selectHitCount(diagnostics, stages, routing),
      missCount: config.selectMissCount(diagnostics, stages, routing),
    });
  }

  return buildDecisionDiagnosticValueMetric({
    key: config.key,
    label: config.label,
    note: typeof config.selectNote === "function"
      ? config.selectNote(diagnostics, stages, routing)
      : (config.note || ""),
    formatter: config.formatter,
    value: config.selectValue(diagnostics, stages, routing),
  });
}

export function buildExecutionSummaryMetrics(source = null) {
  const diagnostics = resolveRunDiagnostics(source);
  if (!diagnostics) {
    return [];
  }

  const stages = diagnostics?.stages_ms && typeof diagnostics.stages_ms === "object"
    ? diagnostics.stages_ms
    : {};
  const routing = diagnostics?.routing && typeof diagnostics.routing === "object"
    ? diagnostics.routing
    : {};

  return EXECUTION_SUMMARY_METRIC_DEFINITIONS.map((config) => (
    buildExecutionSummaryMetric(config, diagnostics, stages, routing)
  ));
}

function normalizeExecutionServiceHealthText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeExecutionServiceHealthSignals(candidate) {
  return Array.isArray(candidate) ? candidate.filter((item) => typeof item === "string" && item.trim()) : [];
}

function parseExecutionServiceHealthLeadingCount(text) {
  const match = normalizeExecutionServiceHealthText(text).match(/^(\d+)/);
  if (!match) {
    return null;
  }

  const parsedValue = Number.parseInt(match[1], 10);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function hasExecutionServiceHealthKeyword(text, keywords) {
  return keywords.some((keyword) => text.includes(keyword));
}

function countExecutionServiceHealthSignals(signals, keywords) {
  return signals.reduce((total, signal) => {
    const normalizedSignal = normalizeExecutionServiceHealthText(signal).toLowerCase();
    if (!normalizedSignal || !hasExecutionServiceHealthKeyword(normalizedSignal, keywords)) {
      return total;
    }

    const signalCount = parseExecutionServiceHealthLeadingCount(normalizedSignal);
    return total + (signalCount != null ? signalCount : 1);
  }, 0);
}

function resolveFallbackExecutionSummaryTone(value, status) {
  if (status === "error" || value === "erreur") {
    return "danger";
  }
  if (status === "degraded" || value === "dégradé") {
    return "warning";
  }
  if (["chargé", "exécuté", "API directe", "cache + API"].includes(value)) {
    return "success";
  }

  return "neutral";
}

function buildFallbackExecutionSummaryItemFromServiceHealth(candidate) {
  if (!candidate || typeof candidate !== "object") {
    return null;
  }

  const key = normalizeExecutionServiceHealthText(candidate.key).toLowerCase();
  const label = normalizeExecutionServiceHealthText(candidate.label);
  if (!key || !label) {
    return null;
  }

  const status = normalizeExecutionServiceHealthText(candidate.status).toLowerCase();
  const summary = normalizeExecutionServiceHealthText(candidate.summary).toLowerCase();
  const detail = normalizeExecutionServiceHealthText(candidate.detail).toLowerCase();
  const signals = normalizeExecutionServiceHealthSignals(candidate.signals);
  const signalsText = signals.join(" ").toLowerCase();
  const combinedText = [summary, detail, signalsText].filter(Boolean).join(" ");
  const cacheSignalCount = countExecutionServiceHealthSignals(signals, ["cache"]);
  const snapshotSignalCount = countExecutionServiceHealthSignals(signals, ["snapshot"]);
  const liveSignalCount = countExecutionServiceHealthSignals(signals, ["appel", "requête", "requete", "live", "direct"]);
  const hasReuseSignal = cacheSignalCount > 0 || snapshotSignalCount > 0 || hasExecutionServiceHealthKeyword(combinedText, ["cache", "snapshot"]);
  const hasLiveSignal = liveSignalCount > 0 || (status === "ok" && key !== "excel" && key !== "ortools" && !hasReuseSignal);
  const observedCount = signals.reduce((bestCount, signal) => {
    const signalCount = parseExecutionServiceHealthLeadingCount(signal);
    return signalCount != null ? Math.max(bestCount, signalCount) : bestCount;
  }, 0);

  let value = "à confirmer";

  if (key === "excel") {
    value = "importé";
  } else if (key === "ortools") {
    value = observedCount > 0 || status === "ok" ? "exécuté" : status === "degraded" ? "dégradé" : "à confirmer";
  } else if (status === "error") {
    value = "erreur";
  } else if (hasReuseSignal && hasLiveSignal) {
    value = "cache + API";
  } else if (cacheSignalCount > 0) {
    value = "cache";
  } else if (snapshotSignalCount > 0 || hasExecutionServiceHealthKeyword(combinedText, ["snapshot"])) {
    value = "réutilisé";
  } else if (hasLiveSignal) {
    value = "API directe";
  } else if (status === "degraded") {
    value = "dégradé";
  }

  return {
    key,
    label,
    value,
    tone: resolveFallbackExecutionSummaryTone(value, status),
  };
}

function buildFallbackExecutionSummaryItems(serviceHealth) {
  return (Array.isArray(serviceHealth) ? serviceHealth : [])
    .map((item) => buildFallbackExecutionSummaryItemFromServiceHealth(item))
    .filter(Boolean);
}

const EXECUTION_SUMMARY_ACTIVITY_TONE_BY_METRIC_TONE = {
  accent: "success",
  neutral: "neutral",
  warning: "warning",
};

function normalizeExecutionSummaryExamples(values = []) {
  const seenValues = new Set();

  return (Array.isArray(values) ? values : []).reduce((items, value) => {
    const normalizedValue = typeof value === "string" ? value.trim() : "";
    if (!normalizedValue) {
      return items;
    }

    const key = normalizedValue.toLowerCase();
    if (seenValues.has(key)) {
      return items;
    }

    seenValues.add(key);
    items.push(normalizedValue);
    return items;
  }, []).slice(0, 10);
}

function normalizeExecutionSummaryExampleRecords(values = []) {
  const seenValues = new Set();

  return (Array.isArray(values) ? values : []).reduce((items, value, index) => {
    if (!value || typeof value !== "object") {
      return items;
    }

    const label = typeof value.label === "string" ? value.label.trim() : "";
    const detail = typeof value.detail === "string" ? value.detail.trim() : "";
    if (!label) {
      return items;
    }

    const key = `${label.toLowerCase()}::${detail.toLowerCase()}`;
    if (seenValues.has(key)) {
      return items;
    }

    seenValues.add(key);
    items.push({
      ...(detail ? { detail } : {}),
      label,
    });
    return items;
  }, []).slice(0, 10);
}

function resolveExecutionSummaryContextItem(context, metricKey) {
  if (!context || typeof context !== "object") {
    return null;
  }

  if (metricKey === "route-cache" && context.routeCache && typeof context.routeCache === "object") {
    return context.routeCache;
  }

  if (metricKey === "geocode-cache" && context.geocodeCache && typeof context.geocodeCache === "object") {
    return context.geocodeCache;
  }

  return null;
}

export function buildStatusNoticeExecutionSummaryItems({
  allowServiceHealthFallback = true,
  diagnostics = null,
  serviceHealth = null,
  context = null,
} = {}) {
  const resolvedDiagnostics = resolveRunDiagnostics(diagnostics);
  if (resolvedDiagnostics) {
    const metricConfigByKey = new Map(EXECUTION_SUMMARY_METRIC_DEFINITIONS.map((config) => [config.key, config]));
    const routing = resolvedDiagnostics?.routing && typeof resolvedDiagnostics.routing === "object"
      ? resolvedDiagnostics.routing
      : {};

    return buildExecutionSummaryMetrics(resolvedDiagnostics)
      .filter((metric) => metric.hasData)
      .map((metric) => {
        const config = metricConfigByKey.get(metric.key) || {};
        const contextItem = resolveExecutionSummaryContextItem(context, metric.key);
        const exampleRecords = normalizeExecutionSummaryExampleRecords(
          Array.isArray(contextItem?.exampleRecords)
            ? contextItem.exampleRecords
            : routing?.[config.noticeExampleRecordsKey],
        );
        const examples = normalizeExecutionSummaryExamples(
          Array.isArray(contextItem?.examples)
            ? contextItem.examples
            : routing?.[config.noticeExamplesKey],
        );

        return {
          description: typeof contextItem?.description === "string" && contextItem.description.trim()
            ? contextItem.description.trim()
            : (typeof config.noticeDescription === "string" ? config.noticeDescription : ""),
          example: typeof contextItem?.example === "string" && contextItem.example.trim()
            ? contextItem.example.trim()
            : (typeof config.noticeExample === "string" ? config.noticeExample : ""),
          ...(exampleRecords.length ? { exampleRecords } : {}),
          ...(examples.length ? { examples } : {}),
          ...(typeof contextItem?.examplesTitle === "string" && contextItem.examplesTitle.trim()
            ? { examplesTitle: contextItem.examplesTitle.trim() }
            : (typeof config.noticeExamplesTitle === "string" && config.noticeExamplesTitle.trim() && (exampleRecords.length || examples.length)
              ? { examplesTitle: config.noticeExamplesTitle.trim() }
              : {})),
          key: metric.key,
          label: typeof config.noticeLabel === "string" && config.noticeLabel.trim() ? config.noticeLabel : metric.label,
          value: typeof contextItem?.value === "string" && contextItem.value.trim()
            ? contextItem.value.trim()
            : metric.value,
          tone: EXECUTION_SUMMARY_ACTIVITY_TONE_BY_METRIC_TONE[metric.tone] || "neutral",
        };
      });
  }

  if (!allowServiceHealthFallback) {
    return [];
  }

  return buildFallbackExecutionSummaryItems(serviceHealth);
}

function buildDecisionDiagnosticValueMetric({
  key,
  label,
  note = "",
  formatter,
  value,
}) {
  const hasValue = isFiniteDiagnosticNumber(value);

  return {
    key,
    label,
    note: hasValue ? note : DIAGNOSTIC_METRIC_UNAVAILABLE_NOTE,
    rawValue: hasValue ? value : null,
    tone: hasValue ? "accent" : "neutral",
    value: hasValue ? formatter(value) : DIAGNOSTIC_METRIC_UNAVAILABLE_LABEL,
    hasData: hasValue,
    isComplete: hasValue,
  };
}

function buildDecisionDiagnosticCacheMetric({
  hitCount,
  key,
  label,
  missCount,
}) {
  const hasHits = isFiniteDiagnosticNumber(hitCount);
  const hasMisses = isFiniteDiagnosticNumber(missCount);

  let value = DIAGNOSTIC_METRIC_UNAVAILABLE_LABEL;
  let note = DIAGNOSTIC_METRIC_UNAVAILABLE_NOTE;
  if (hasHits) {
    value = `${formatDiagnosticInteger(hitCount)} hit${Math.round(hitCount) > 1 ? "s" : ""}`;
    note = hasMisses
      ? `${formatDiagnosticInteger(missCount)} miss${Math.round(missCount) > 1 ? "es" : ""}`
      : "Misses non disponibles";
  } else if (hasMisses) {
    value = `${formatDiagnosticInteger(missCount)} miss${Math.round(missCount) > 1 ? "es" : ""}`;
    note = "Hits non disponibles";
  }

  return {
    key,
    label,
    note,
    tone: hasHits && hasMisses ? "accent" : (hasHits || hasMisses ? "warning" : "neutral"),
    value,
    hasData: hasHits || hasMisses,
    isComplete: hasHits && hasMisses,
  };
}

const RUN_DIAGNOSTIC_TIMESTAMP_COMPACT_DATE_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "short",
  timeZone: RUN_DIAGNOSTIC_DISPLAY_TIME_ZONE,
  year: "numeric",
});
const RUN_DIAGNOSTIC_TIMESTAMP_TIME_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  timeZone: RUN_DIAGNOSTIC_DISPLAY_TIME_ZONE,
});

function parseRunDiagnosticTimestamp(value) {
  const normalizedValue = normalizeInlineText(value);
  if (!normalizedValue) {
    return null;
  }

  const frenchDateTimeMatch = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/.exec(normalizedValue);
  if (frenchDateTimeMatch) {
    // Les labels snapshot `generated_at` sont ancrés en UTC (Railway / helper backend).
    const parsedDate = new Date(Date.UTC(
      Number(frenchDateTimeMatch[3]),
      Number(frenchDateTimeMatch[2]) - 1,
      Number(frenchDateTimeMatch[1]),
      Number(frenchDateTimeMatch[4] || 12),
      Number(frenchDateTimeMatch[5] || 0),
      Number(frenchDateTimeMatch[6] || 0),
      0,
    ));

    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate;
    }
  }

  const parsedDate = new Date(normalizedValue);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate;
  }

  return null;
}

function formatRunDiagnosticCompactDateLabel(value) {
  const parsedDate = parseRunDiagnosticTimestamp(value);
  if (!parsedDate) {
    return null;
  }

  return RUN_DIAGNOSTIC_TIMESTAMP_COMPACT_DATE_FORMATTER.format(parsedDate);
}

export function formatRunDiagnosticTimeLabel(value) {
  const parsedDate = parseRunDiagnosticTimestamp(value);
  if (!parsedDate) {
    return null;
  }

  return RUN_DIAGNOSTIC_TIMESTAMP_TIME_FORMATTER.format(parsedDate);
}

export function formatPlanningDispatchDateLabel(value) {
  const rawValue = normalizeInlineText(value);
  if (!rawValue) {
    return null;
  }

  const parsedDate = new Date(`${rawValue.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) {
    return rawValue;
  }

  return new Intl.DateTimeFormat("fr-CA", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(parsedDate);
}

function resolveReassignmentDiagnosticDispatchDate(resultsData = {}) {
  return normalizeInlineText(resultsData?.dispatch_date)
    || normalizeInlineText(resultsData?.planning_summary?.dispatch_date);
}

function buildReassignmentDiagnosticTimestampItems(resultsData = {}, diagnostics = {}) {
  const recordedAt = normalizeInlineText(diagnostics?.recorded_at)
    || normalizeInlineText(resultsData?.captured_at);
  const dispatchDate = resolveReassignmentDiagnosticDispatchDate(resultsData);
  const performedDateLabel = formatRunDiagnosticCompactDateLabel(recordedAt);
  const performedTimeLabel = formatRunDiagnosticTimeLabel(recordedAt);
  const dispatchDateLabel = formatPlanningDispatchDateLabel(dispatchDate);
  const actorLabel = normalizeInlineText(diagnostics?.actor_label)
    || normalizeInlineText(diagnostics?.actor_name)
    || normalizeInlineText(resultsData?.actor_label);

  return [
    performedDateLabel ? {
      key: "performed-date",
      label: "Date",
      value: performedDateLabel,
      detail: "",
    } : null,
    performedTimeLabel ? {
      key: "performed-time",
      label: "Heure",
      value: performedTimeLabel,
      detail: "",
    } : null,
    dispatchDateLabel ? {
      key: "dispatch-date",
      label: "Dispatch",
      value: dispatchDateLabel,
      detail: dispatchDate,
    } : null,
    actorLabel ? {
      key: "actor",
      label: "Utilisateur",
      value: actorLabel,
      detail: "",
    } : null,
  ].filter(Boolean);
}

function normalizeReassignmentDiagnosticText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function looksLikeSuspendedSourceGroupKey(value) {
  const normalizedValue = normalizeReassignmentDiagnosticText(value);
  return Boolean(
    normalizedValue
    && (
      normalizedValue.includes("::backend:delivery:")
      || normalizedValue.includes("::SIM-")
      || normalizedValue.startsWith("__suspended_source__:")
      || normalizedValue.startsWith("suspended-group")
    ),
  );
}

function looksLikeInternalTruckId(value) {
  const normalizedValue = normalizeReassignmentDiagnosticText(value);
  if (!normalizedValue) {
    return false;
  }
  const upperValue = normalizedValue.toUpperCase();
  return upperValue.startsWith("TRK-")
    || upperValue.startsWith("TRK_")
    || normalizedValue.includes("::")
    || looksLikeSuspendedSourceGroupKey(normalizedValue);
}

function pickHumanTruckDisplayLabel(...candidates) {
  for (const candidate of candidates) {
    const text = normalizeReassignmentDiagnosticText(candidate);
    if (text && !looksLikeInternalTruckId(text)) {
      return text;
    }
  }
  return "";
}

function getFirstHumanTruckLabelFromKeys(objects = [], keys = []) {
  return pickHumanTruckDisplayLabel(...collectDiagnosticTextValuesFromKeys(objects, keys));
}

function resolveReassignmentLocalPatchDetails(diagnostics = null) {
	const localPatch = diagnostics?.local_patch && typeof diagnostics.local_patch === "object" && !Array.isArray(diagnostics.local_patch)
		? diagnostics.local_patch
		: {};
  const details = localPatch.details && typeof localPatch.details === "object" && !Array.isArray(localPatch.details)
    ? localPatch.details
    : {};

	return { details, localPatch };
}

function isPlainDiagnosticObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function pickFirstPlainDiagnosticObject(...values) {
  return values.find((value) => isPlainDiagnosticObject(value)) || {};
}

function collectReassignmentDiagnosticDetailObjects(diagnostics = null, details = {}) {
  const detailObjects = [];
  const seenObjects = new Set();
  const addObject = (value) => {
    if (!isPlainDiagnosticObject(value) || seenObjects.has(value)) {
      return;
    }
    seenObjects.add(value);
    detailObjects.push(value);
  };

  addObject(diagnostics);
  addObject(details);

  const localPatch = isPlainDiagnosticObject(diagnostics?.local_patch) ? diagnostics.local_patch : {};
  addObject(localPatch);
  addObject(localPatch.details);
  addObject(localPatch.source_details);
  addObject(localPatch.sourceDetails);
  addObject(localPatch.target_details);
  addObject(localPatch.targetDetails);
  addObject(localPatch.source_restore_details);
  addObject(localPatch.target_restore_details);

  Object.values(isPlainDiagnosticObject(diagnostics) ? diagnostics : {}).forEach((value) => {
    if (!isPlainDiagnosticObject(value)) {
      return;
    }
    addObject(value);
    addObject(value.details);
    addObject(value.local_patch_details);
    addObject(value.localPatchDetails);
    addObject(value.source_details);
    addObject(value.sourceDetails);
    addObject(value.target_details);
    addObject(value.targetDetails);
    addObject(value.source_restore_details);
    addObject(value.sourceRestoreDetails);
  });

  return detailObjects;
}

function collectDiagnosticTextValuesFromKeys(objects = [], keys = []) {
  const values = [];
  const seenValues = new Set();
  const addValue = (value) => {
    if (Array.isArray(value)) {
      value.forEach(addValue);
      return;
    }
    const normalizedValue = normalizeReassignmentDiagnosticText(value);
    if (!normalizedValue || seenValues.has(normalizedValue)) {
      return;
    }
    seenValues.add(normalizedValue);
    values.push(normalizedValue);
  };

  objects.forEach((object) => {
    if (!isPlainDiagnosticObject(object)) {
      return;
    }
    keys.forEach((key) => addValue(object[key]));
  });

  return values;
}

function getFirstDiagnosticTextFromKeys(objects = [], keys = []) {
  return collectDiagnosticTextValuesFromKeys(objects, keys)[0] || "";
}

function resolveProtectAfterReassignmentFlag(diagnostics, detailObjects = []) {
  const candidates = [
    diagnostics?.protect_after_reassignment,
    ...(Array.isArray(detailObjects) ? detailObjects : []).map((detail) => detail?.protect_after_reassignment),
  ];
  for (const value of candidates) {
    if (typeof value === "boolean") {
      return value;
    }
  }
  return null;
}

function getFirstDiagnosticNumberFromKeys(objects = [], keys = []) {
  for (const object of objects) {
    if (!isPlainDiagnosticObject(object)) {
      continue;
    }
    for (const key of keys) {
      const value = getFirstFiniteNumber(object[key]);
      if (value !== null) {
        return value;
      }
    }
  }
  return null;
}

function isSuspendedSourceMoveDiagnostic(action = "", diagnostics = null, detailObjects = [], sourceTruckId = "") {
  if (action === "reactivate") {
    return true;
  }
  if (action !== "move_delivery_group") {
    return false;
  }
  if (detailObjects.some((detail) => Boolean(detail.is_suspended_source_move || detail.is_suspended_source))) {
    return true;
  }
  const normalizedSourceTruckId = normalizeReassignmentDiagnosticText(sourceTruckId);
  if (looksLikeSuspendedSourceGroupKey(normalizedSourceTruckId)) {
    return true;
  }
  const sourceGroupKey = normalizeReassignmentDiagnosticText(diagnostics?.source_group_key);
  if (!normalizedSourceTruckId && looksLikeSuspendedSourceGroupKey(sourceGroupKey)) {
    return true;
  }
  return false;
}

function resolveReassignmentAnchorOrderId(resultsData = {}, deliveryId = "") {
  const normalizedDeliveryId = normalizeReassignmentDiagnosticText(deliveryId);
  if (!normalizedDeliveryId) {
    return "";
  }
  const seen = new Set();
  const walk = (value, inheritedOrderId = "", depth = 0) => {
    if (!value || typeof value !== "object" || depth > 8 || seen.has(value)) {
      return "";
    }
    seen.add(value);
    if (Array.isArray(value)) {
      for (const item of value) {
        const resolved = walk(item, inheritedOrderId, depth + 1);
        if (resolved) {
          return resolved;
        }
      }
      return "";
    }
    const currentOrderId = normalizeReassignmentDiagnosticText(
      value.order_id || value.orderId || inheritedOrderId,
    );
    const currentDeliveryId = normalizeReassignmentDiagnosticText(
      value.delivery_id || value.deliveryId,
    );
    if (currentDeliveryId === normalizedDeliveryId && currentOrderId) {
      return currentOrderId;
    }
    for (const child of Object.values(value)) {
      const resolved = walk(child, currentOrderId, depth + 1);
      if (resolved) {
        return resolved;
      }
    }
    return "";
  };
  return walk(resultsData);
}

function collectReassignmentMovedOrderIds(details = {}, diagnostics = null, resultsData = {}) {
  const detailObjects = collectReassignmentDiagnosticDetailObjects(diagnostics, details);
  const action = normalizeReassignmentDiagnosticText(diagnostics?.action).toLowerCase();
  if ([
    "source_changes_import_suspendable_errors",
    "source_changes_remove_modified",
    "suspend",
    "suspend_delivery_group",
    "suspend_from_error",
  ].includes(action)) {
    const suspendedOrderIds = collectDiagnosticTextValuesFromKeys(detailObjects, [
      "suspended_order_ids",
      "order_id",
    ]);
    if (suspendedOrderIds.length) {
      return suspendedOrderIds;
    }
		const anchorDeliveryId = getFirstDiagnosticTextFromKeys(detailObjects, [
			"moved_delivery_id",
			"delivery_id",
		]);
		const anchorOrderId = resolveReassignmentAnchorOrderId(resultsData, anchorDeliveryId);
    return anchorOrderId ? [anchorOrderId] : [];
  }

  const semanticKeys = action === "reactivate"
    ? ["reactivated_order_ids", "order_id", "moved_order_id", "moved_order_ids"]
    : ["release", "release_delivery_group"].includes(action)
      ? ["released_order_ids", "order_id", "moved_order_id", "moved_order_ids"]
      : [];
  if (semanticKeys.length) {
    const semanticOrderIds = collectDiagnosticTextValuesFromKeys(detailObjects, semanticKeys);
    if (semanticOrderIds.length) {
      return semanticOrderIds;
    }
  }

  const primaryOrderIds = collectDiagnosticTextValuesFromKeys(detailObjects, [
    "moved_order_id",
    "moved_order_ids",
    "order_id",
    "order_ids",
    "preloaded_previous_day_order_ids",
    "removed_order_ids",
    "suspended_order_ids",
  ]);
  if (primaryOrderIds.length) {
    return primaryOrderIds;
  }
  return [];
}

function formatReassignmentOrderSummary(details = {}, diagnostics = null, resultsData = {}) {
	const movedOrderIds = collectReassignmentMovedOrderIds(details, diagnostics, resultsData);
	const movedOrderId = normalizeReassignmentDiagnosticText(details.moved_order_id);
	const movedOrderLabel = normalizeReassignmentDiagnosticText(details.moved_order_label);
	if (movedOrderLabel && movedOrderIds.length <= 1) {
		return movedOrderId && movedOrderLabel !== movedOrderId
			? `${movedOrderLabel} (${movedOrderId})`
      : movedOrderLabel;
  }
  if (movedOrderIds.length === 1) {
    return movedOrderIds[0];
  }
  if (movedOrderIds.length > 1) {
    return `${formatDiagnosticInteger(movedOrderIds.length)} commandes : ${movedOrderIds.slice(0, 4).join(", ")}${movedOrderIds.length > 4 ? ", ..." : ""}`;
  }
	const movedDeliveryId = normalizeReassignmentDiagnosticText(details.moved_delivery_id);
	return movedDeliveryId || "Commande non identifiée";
}

function resolveReassignmentLoadingSiteSummary(resultsData = {}, details = {}, diagnostics = null) {
  const explicitSummary = normalizeReassignmentDiagnosticText(
    diagnostics?.loading_site_summary
    || details?.loading_site_summary
  );
  if (explicitSummary) {
    return explicitSummary.startsWith("Chargement") ? explicitSummary : `Chargement : ${explicitSummary}`;
  }

  const movedOrderIds = collectReassignmentMovedOrderIds(details, diagnostics, resultsData);
  const movedDeliveryId = normalizeReassignmentDiagnosticText(details.moved_delivery_id);
  const detailObjects = collectReassignmentDiagnosticDetailObjects(diagnostics, details);
  const diagnosticLoadingNames = collectDiagnosticTextValuesFromKeys(detailObjects, [
    "loading_client_name",
    "loadingClientName",
    "loading_site_name",
    "loadingSiteName",
  ]);
  if (diagnosticLoadingNames.length === 1) {
    return `Chargement : ${diagnosticLoadingNames[0]}`;
  }
  if (diagnosticLoadingNames.length > 1) {
    return `Chargement : ${formatDiagnosticInteger(diagnosticLoadingNames.length)} sites (${diagnosticLoadingNames.slice(0, 3).join(", ")}${diagnosticLoadingNames.length > 3 ? ", ..." : ""})`;
  }
  if (!movedOrderIds.length && !movedDeliveryId) {
    return "";
  }

  const movedOrderIdSet = new Set(movedOrderIds);
  const loadingSitesByKey = new Map();
  const seenRows = new Set();
  const assignments = Array.isArray(resultsData?.assignments) ? resultsData.assignments : [];

  const collectLoadingRow = (row = null) => {
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      return;
    }
    const orderId = normalizeReassignmentDiagnosticText(row.order_id || row.orderId);
    const deliveryId = normalizeReassignmentDiagnosticText(row.delivery_id || row.deliveryId);
    if ((orderId && !movedOrderIdSet.has(orderId)) && (!movedDeliveryId || deliveryId !== movedDeliveryId)) {
      return;
    }
    if (!orderId && (!movedDeliveryId || deliveryId !== movedDeliveryId)) {
      return;
    }

    const loadingName = normalizeReassignmentDiagnosticText(
      row.loading_client_name
      || row.loadingClientName
      || row.loading_site_name
      || row.loadingSiteName
    );
    const loadingAddress = normalizeReassignmentDiagnosticText(row.loading_address || row.loadingAddress);
    const loadingSilo = normalizeReassignmentDiagnosticText(row.loading_silo || row.loadingSilo);
    if (!loadingName && !loadingAddress) {
      return;
    }

    const rowKey = [orderId, deliveryId, loadingName, loadingAddress, loadingSilo].join("|").toLowerCase();
    if (seenRows.has(rowKey)) {
      return;
    }
    seenRows.add(rowKey);

    const siteKey = [loadingName, loadingAddress].join("|").toLowerCase();
    const existingSite = loadingSitesByKey.get(siteKey);
    const site = existingSite || {
      address: loadingAddress,
      label: loadingName || loadingAddress,
      silos: new Set(),
    };
    if (loadingSilo) {
      site.silos.add(loadingSilo);
    }
    loadingSitesByKey.set(siteKey, site);
  };

  assignments.forEach((assignment) => {
    if (!assignment || typeof assignment !== "object") {
      return;
    }
    (Array.isArray(assignment.orders) ? assignment.orders : []).forEach(collectLoadingRow);
    (Array.isArray(assignment.delivery_items) ? assignment.delivery_items : []).forEach(collectLoadingRow);
  });

  const loadingSites = Array.from(loadingSitesByKey.values());
  if (!loadingSites.length) {
    return "";
  }

  if (loadingSites.length === 1) {
    const [site] = loadingSites;
    return `Chargement : ${site.label || site.address}`;
  }

  const labels = loadingSites
    .map((site) => site.label || site.address)
    .filter(Boolean);
  return `Chargement : ${formatDiagnosticInteger(loadingSites.length)} sites (${labels.slice(0, 3).join(", ")}${labels.length > 3 ? ", ..." : ""})`;
}

function resolveReassignmentTruckDisplayLabel(resultsData = {}, truckId = "", fallbackLabel = "") {
  const normalizedTruckId = normalizeReassignmentDiagnosticText(truckId);
  const normalizedFallbackLabel = pickHumanTruckDisplayLabel(fallbackLabel);
  const assignments = Array.isArray(resultsData?.assignments) ? resultsData.assignments : [];
  const candidateKeys = new Set(
    [normalizedTruckId, normalizeReassignmentDiagnosticText(fallbackLabel)].filter(Boolean),
  );
  let matchedAssignment = null;

  for (const assignment of assignments) {
    if (!isPlainDiagnosticObject(assignment)) {
      continue;
    }
    const assignmentTruckId = normalizeReassignmentDiagnosticText(assignment.truck_id || assignment.truckId);
    const assignmentTruckName = normalizeReassignmentDiagnosticText(assignment.truck_name || assignment.truckName);
    if ((assignmentTruckId && candidateKeys.has(assignmentTruckId)) || (assignmentTruckName && candidateKeys.has(assignmentTruckName))) {
      matchedAssignment = assignment;
      break;
    }
  }

  if (matchedAssignment) {
    const assignmentLabel = pickHumanTruckDisplayLabel(
      matchedAssignment.truck_name,
      matchedAssignment.truckName,
      matchedAssignment.truck_number,
      matchedAssignment.truckNumber,
      matchedAssignment.truck_label,
      matchedAssignment.truckLabel,
      matchedAssignment.display_name,
      matchedAssignment.displayName,
    );
    if (assignmentLabel) {
      return assignmentLabel;
    }
  }

  return normalizedFallbackLabel;
}

function resolveReassignmentTruckDetail(truckId = "", displayLabel = "") {
  const normalizedDetail = pickHumanTruckDisplayLabel(truckId);
  const normalizedDisplayLabel = pickHumanTruckDisplayLabel(displayLabel);
  return normalizedDetail && normalizedDetail !== normalizedDisplayLabel ? normalizedDetail : "";
}

function buildReassignmentDiagnosticMetric({
  key,
  label,
  note = "",
  rawValue,
  tone = "neutral",
  type = "duration",
}) {
  if (!isFiniteDiagnosticNumber(rawValue)) {
    return null;
  }
  const value = type === "integer"
    ? formatDiagnosticInteger(rawValue)
    : formatDiagnosticDurationLabel(rawValue);
  return {
    hasData: true,
    key,
    label,
    note,
    rawValue,
    tone,
    value,
  };
}

const REASSIGNMENT_ONLY_DIAGNOSTIC_ACTIONS = new Set([
	"evening_standard_transfer",
	"move_delivery",
	"move_delivery_group",
	"move_order",
	"move_truck_orders",
  "release",
  "release_delivery_group",
]);
const REASSIGNMENT_DIAGNOSTIC_ACTIONS = new Set([
  "disable_truck_dispatch",
  "end_of_day_load_assignment",
  "end_of_day_load_removal",
  "evening_standard_transfer",
  "gantt_source_backed_save",
	"move_delivery",
	"move_delivery_group",
	"move_order",
	"move_truck_orders",
  "optimize_truck_routes",
  "preloaded_previous_day",
  "preloaded_previous_day_assignment",
  "reactivate",
  "release",
  "release_delivery_group",
  "remove_preloaded_previous_day",
  "remove_preloaded_previous_day_assignment",
  "restore_dispatch_snapshot",
	"segment_reorder",
	"source_changes_import_suspendable_errors",
	"source_changes_remove_date_changed",
	"source_changes_remove_deleted",
	"source_changes_remove_modified",
  "suspend",
  "suspend_delivery_group",
  "suspend_from_error",
]);

function getPlanningDiagnosticActionMeta(action = "") {
  switch (action) {
    case "disable_truck_dispatch":
      return {
        emptyMetricsMessage: "Aucune mesure chiffrée disponible pour cette désactivation.",
        message: "Dernière désactivation de camion ayant déplacé des commandes.",
        rejectedMessage: "Dernière tentative de désactivation mesurée.",
        orderLabel: "Commandes déplacées",
        sectionTitle: "Diagnostic désactivation camion",
        toggleAriaLabel: "le diagnostic de désactivation camion",
        truckLabel: "Camion désactivé",
      };
	case "suspend":
	case "suspend_delivery_group":
	case "suspend_from_error":
	case "source_changes_import_suspendable_errors":
	case "source_changes_remove_modified":
      return {
        emptyMetricsMessage: "Aucune mesure chiffrée disponible pour cette suspension.",
        message: "Dernière suspension mesurée sur ce résultat.",
        rejectedMessage: "Dernière tentative de suspension mesurée, même si elle a été refusée.",
        orderLabel: "Commandes suspendues",
        sectionTitle: "Diagnostic suspension",
        toggleAriaLabel: "le diagnostic de suspension",
        truckLabel: "Camion d'origine",
      };
    case "reactivate":
      return {
        emptyMetricsMessage: "Aucune mesure chiffrée disponible pour cette réactivation.",
        message: "Dernière réactivation depuis Suspendues mesurée sur ce résultat.",
        rejectedMessage: "Dernière tentative de réactivation mesurée, même si elle a été refusée.",
        orderLabel: "Commandes réactivées",
        sectionTitle: "Diagnostic réactivation",
        toggleAriaLabel: "le diagnostic de réactivation",
        truckLabel: "Camion cible",
      };
    case "release":
    case "release_delivery_group":
      return {
        emptyMetricsMessage: "Aucune mesure chiffrée disponible pour cette libération.",
        message: "Dernière libération vers l'affectation optimisée mesurée sur ce résultat.",
        rejectedMessage: "Dernière tentative de libération mesurée, même si elle a été refusée.",
        orderLabel: "Commandes libérées",
        sectionTitle: "Diagnostic libération",
        toggleAriaLabel: "le diagnostic de libération",
        truckLabel: "Camion d'origine",
      };
    case "restore_dispatch_snapshot":
      return {
        emptyMetricsMessage: "Aucune mesure chiffrée disponible pour cette restauration.",
        message: "Dernière restauration ayant modifié la position de commandes.",
        rejectedMessage: "Dernière tentative de restauration mesurée.",
        orderLabel: "Commandes restaurées",
        sectionTitle: "Diagnostic restauration",
        toggleAriaLabel: "le diagnostic de restauration",
        truckLabel: "Emplacement d'origine",
      };
    case "preloaded_previous_day":
    case "preloaded_previous_day_assignment":
    case "remove_preloaded_previous_day":
    case "remove_preloaded_previous_day_assignment":
      return {
        emptyMetricsMessage: "Aucune mesure chiffrée disponible pour cette mutation Preload.",
        message: "Dernière mutation Preload mesurée sur ce résultat.",
        rejectedMessage: "Dernière tentative Preload mesurée, même si elle a été refusée.",
        orderLabel: "Commandes Preload",
        sectionTitle: "Diagnostic Preload",
        toggleAriaLabel: "le diagnostic Preload",
        truckLabel: "Camion concerné",
      };
    case "end_of_day_load_assignment":
    case "end_of_day_load_removal":
      return {
        emptyMetricsMessage: "Aucune mesure chiffrée disponible pour cette mutation PM.",
        message: "Dernière mutation Chargé PM mesurée sur ce résultat.",
        rejectedMessage: "Dernière tentative Chargé PM mesurée, même si elle a été refusée.",
        orderLabel: "Commandes PM",
        sectionTitle: "Diagnostic PM",
        toggleAriaLabel: "le diagnostic PM",
        truckLabel: "Camion concerné",
      };
    case "evening_standard_transfer":
      return {
        emptyMetricsMessage: "Aucune mesure chiffrée disponible pour ce transfert Jour / Soir.",
        message: "Dernier transfert Jour / Soir mesuré sur ce résultat.",
        rejectedMessage: "Dernière tentative de transfert Jour / Soir mesurée, même si elle a été refusée.",
        orderLabel: "Commandes transférées",
        sectionTitle: "Diagnostic transfert Jour / Soir",
        toggleAriaLabel: "le diagnostic de transfert Jour / Soir",
        truckLabel: "Camion cible",
      };
    case "gantt_source_backed_save":
      return {
        emptyMetricsMessage: "Aucune mesure chiffrée disponible pour cette édition priorité/horaire.",
        message: "Dernière édition priorité/horaire mesurée sur ce résultat.",
        rejectedMessage: "Dernière tentative d'édition priorité/horaire mesurée, même si elle a été refusée.",
        orderLabel: "Commandes éditées",
        sectionTitle: "Diagnostic priorité",
        toggleAriaLabel: "le diagnostic priorité/horaire",
        truckLabel: "Camion concerné",
      };
    case "segment_reorder":
      return {
        emptyMetricsMessage: "Aucune mesure chiffrée supplémentaire pour ce réordonnancement.",
        message: "Dernier diagnostic de réordonnancement sur ce résultat.",
        rejectedMessage: "Dernier réordonnancement refusé et conservé pour diagnostic.",
        orderLabel: "Commandes réordonnées",
        sectionTitle: "Diagnostic réordonnancement",
        toggleAriaLabel: "le diagnostic de réordonnancement",
        truckLabel: "Camion concerné",
      };
    case "optimize_truck_routes":
      return {
        emptyMetricsMessage: "Aucune mesure chiffrée disponible pour cette optimisation camion.",
        message: "Dernière optimisation camion mesurée sur ce résultat.",
        rejectedMessage: "Dernière tentative d'optimisation camion mesurée, même si elle a été refusée.",
        orderLabel: "Commandes liées à Optimiser ce camion",
        sectionTitle: "Diagnostic routes",
        toggleAriaLabel: "le diagnostic routes",
        truckLabel: "Camion concerné",
      };
	case "source_changes_remove_date_changed":
	case "source_changes_remove_deleted":
		return {
        emptyMetricsMessage: "Aucune mesure chiffrée disponible pour cette mise à jour commandes.",
        message: "Dernière mise à jour commandes mesurée sur ce résultat.",
        rejectedMessage: "Dernière tentative de mise à jour commandes mesurée, même si elle a été refusée.",
			orderLabel: "Commandes retirées",
        sectionTitle: "Diagnostic M-à-J commandes",
        toggleAriaLabel: "le diagnostic M-à-J commandes",
        truckLabel: "Camions touchés",
      };
    default:
      return {
        emptyMetricsMessage: "Aucune mesure chiffrée disponible pour cette réaffectation.",
        message: "Dernière réaffectation mesurée sur ce résultat.",
        rejectedMessage: "Dernière tentative de réaffectation mesurée, même si elle a été refusée.",
        orderLabel: "Commande",
        sectionTitle: "Diagnostic réaffectation",
        toggleAriaLabel: "le diagnostic de réaffectation",
        truckLabel: "Camion source",
      };
  }
}

function collectPlanningDiagnosticPayloadRouteRefreshStats(diagnostics = null) {
  const stats = [];
  const seenObjects = new Set();
  const addStats = (value) => {
    if (!isPlainDiagnosticObject(value) || seenObjects.has(value)) {
      return;
    }
    seenObjects.add(value);
    stats.push(value);
  };

  addStats(diagnostics?.payload_route_refresh);
  Object.values(isPlainDiagnosticObject(diagnostics) ? diagnostics : {}).forEach((value) => {
    if (!isPlainDiagnosticObject(value)) {
      return;
    }
    addStats(value.payload_route_refresh);
    addStats(value.payloadRouteRefresh);
  });

  return stats;
}

function resolveReassignmentDiagnosticCandidate(candidate = null) {
  const diagnostics = resolveRunDiagnostics(candidate);
  if (!diagnostics) {
    return null;
  }

  const action = normalizeReassignmentDiagnosticText(diagnostics.action);
  if (REASSIGNMENT_DIAGNOSTIC_ACTIONS.has(action)) {
    return diagnostics;
  }

  const { details } = resolveReassignmentLocalPatchDetails(diagnostics);
  const detailObjects = collectReassignmentDiagnosticDetailObjects(diagnostics, details);
  const hasReassignmentContext = Boolean(
    getFirstDiagnosticTextFromKeys(detailObjects, [
      "current_truck_id",
      "source_rebuild_truck_id",
      "source_truck_id",
      "source_truck_label",
      "target_truck_id",
      "target_truck_label",
      "truck_id",
    ])
    || collectReassignmentMovedOrderIds(details, diagnostics).length > 0
  );
  return hasReassignmentContext ? diagnostics : null;
}

function resolveReassignmentDiagnostics(resultsData = {}) {
  if (!resultsData || typeof resultsData !== "object" || Array.isArray(resultsData)) {
    return null;
  }

  const candidates = [
    resultsData._planning_diagnostics,
    resultsData.planning_diagnostics,
    resultsData.planningDiagnostics,
    resultsData.last_reassignment_diagnostics,
    resultsData.lastReassignmentDiagnostics,
    resultsData.diagnostics,
    resultsData,
  ];

  for (const candidate of candidates) {
    const diagnostics = resolveReassignmentDiagnosticCandidate(candidate);
    if (diagnostics) {
      return diagnostics;
    }
  }

  return null;
}

export function buildReassignmentDiagnosticPanel(resultsData = {}) {
  const diagnostics = resolveReassignmentDiagnostics(resultsData);
  if (!diagnostics) {
    return null;
  }

  const action = normalizeReassignmentDiagnosticText(diagnostics.action);
  const actionMeta = getPlanningDiagnosticActionMeta(action);
  const { details, localPatch } = resolveReassignmentLocalPatchDetails(diagnostics);
  const detailObjects = collectReassignmentDiagnosticDetailObjects(diagnostics, details);
  const sourceTruckId = getFirstDiagnosticTextFromKeys(detailObjects, ["source_truck_id", "source_rebuild_truck_id"]);
  const isSuspendedSourceMove = isSuspendedSourceMoveDiagnostic(action, diagnostics, detailObjects, sourceTruckId);
  const sourceTruckLabel = getFirstHumanTruckLabelFromKeys(detailObjects, [
    "source_truck_label",
    "source_truck_name",
    "source_rebuild_truck_label",
  ]);
  const targetTruckLabel = getFirstHumanTruckLabelFromKeys(detailObjects, [
    "target_truck_label",
    "target_truck_name",
  ]);
  const targetTruckId = getFirstDiagnosticTextFromKeys(detailObjects, ["target_truck_id", "requested_target_truck_id"]);
  const currentTruckLabel = getFirstHumanTruckLabelFromKeys(detailObjects, [
    "current_truck_label",
    "current_truck_name",
    "truck_label",
    "truck_name",
    "source_rebuild_truck_label",
    "source_truck_label",
    "target_truck_label",
  ]);
  const currentTruckId = getFirstDiagnosticTextFromKeys(detailObjects, [
    "current_truck_id",
    "truck_id",
    "source_rebuild_truck_id",
    "source_truck_id",
    "target_truck_id",
  ]);
  const sourceTruckDisplayLabel = resolveReassignmentTruckDisplayLabel(resultsData, sourceTruckId, sourceTruckLabel);
  const targetTruckDisplayLabel = resolveReassignmentTruckDisplayLabel(resultsData, targetTruckId, targetTruckLabel);
  const currentTruckDisplayLabel = resolveReassignmentTruckDisplayLabel(resultsData, currentTruckId, currentTruckLabel);
  const orderSummary = formatReassignmentOrderSummary(details, diagnostics, resultsData);
  const loadingSiteSummary = resolveReassignmentLoadingSiteSummary(resultsData, details, diagnostics);
  const hasReassignmentContext = Boolean(
    sourceTruckDisplayLabel
    || targetTruckDisplayLabel
    || currentTruckDisplayLabel
    || collectReassignmentMovedOrderIds(details, diagnostics, resultsData).length > 0
  );
  const isReassignmentAction = REASSIGNMENT_ONLY_DIAGNOSTIC_ACTIONS.has(action);
  const isEveningStandardTransfer = action === "evening_standard_transfer";
  const isKnownPlanningDiagnosticAction = REASSIGNMENT_DIAGNOSTIC_ACTIONS.has(action);
  if (!isKnownPlanningDiagnosticAction && !hasReassignmentContext) {
    return null;
  }
  const sourceDispatchPeriod = getFirstDiagnosticTextFromKeys(detailObjects, [
    "source_dispatch_period",
    "source_period",
  ]).toLowerCase();
  const targetDispatchPeriod = getFirstDiagnosticTextFromKeys(detailObjects, [
    "target_dispatch_period",
    "target_period",
  ]).toLowerCase();
  const sourcePeriodLabel = sourceDispatchPeriod === "evening"
    ? "Soir"
    : sourceDispatchPeriod === "day"
      ? "Jour"
      : "source";
	const targetPeriodLabel = targetDispatchPeriod === "evening"
    ? "Soir"
    : targetDispatchPeriod === "day"
      ? "Jour"
		: "cible";
	const hasMovementContract = diagnostics.movement_audit_evaluated === true;
	const sourceLocation = getFirstDiagnosticTextFromKeys(detailObjects, ["source_location"]).toLowerCase();
	const targetLocation = getFirstDiagnosticTextFromKeys(detailObjects, ["target_location"]).toLowerCase();
	const formatMovementLocation = (location, truckLabel, periodLabel) => {
		const locationLabel = location === "suspended"
			? "Suspendues"
			: location === "unassigned"
				? "Non affectées"
				: location === "absent"
					? "Hors dispatch"
					: location === "assigned"
						? (truckLabel || "Affectée")
						: (truckLabel || "Emplacement non disponible");
		return periodLabel && !["source", "cible"].includes(periodLabel)
			? `${locationLabel} · ${periodLabel}`
			: locationLabel;
	};
	const movementSourceLabel = formatMovementLocation(
		sourceLocation,
		sourceTruckDisplayLabel,
		sourcePeriodLabel,
	);
	const movementTargetLabel = formatMovementLocation(
		targetLocation,
		targetTruckDisplayLabel,
		targetPeriodLabel,
	);

  const stages = diagnostics.stages_ms && typeof diagnostics.stages_ms === "object" ? diagnostics.stages_ms : {};
  const rebuildDiagnostics = isPlainDiagnosticObject(diagnostics.rebuild) ? diagnostics.rebuild : {};
  const rebuildSolverBreakdown = isPlainDiagnosticObject(diagnostics.rebuild_solver_breakdown)
    ? diagnostics.rebuild_solver_breakdown
    : {};
  const payloadSplitDiagnostics = pickFirstPlainDiagnosticObject(
    details.payload_split_cache_diagnostics,
    details.bulk_payload_split_cache_diagnostics,
    details.target_payload_split_cache_diagnostics,
    localPatch.payload_split_cache_diagnostics,
    localPatch.bulk_payload_split_cache_diagnostics,
    localPatch.target_payload_split_cache_diagnostics,
  );
  const rawPayloadSplitDiagnostics = pickFirstPlainDiagnosticObject(
    details.payload_split_diagnostics,
    details.bulk_payload_split_diagnostics,
    details.target_payload_split_diagnostics,
    localPatch.payload_split_diagnostics,
    localPatch.bulk_payload_split_diagnostics,
    localPatch.target_payload_split_diagnostics,
  );
  const payloadRouteRefreshStats = collectPlanningDiagnosticPayloadRouteRefreshStats(diagnostics);
  const frontendTiming = isPlainDiagnosticObject(diagnostics.frontend_timing)
    ? diagnostics.frontend_timing
    : {};
  const clickToVisibleMs = getFirstFiniteNumber(
    frontendTiming.click_to_visible_ms,
    frontendTiming.clickToVisibleMs,
  );
  const responseJsonParsedMs = getFirstFiniteNumber(
    frontendTiming.response_json_parsed_ms,
    frontendTiming.responseJsonParsedMs,
  );
  const resultsLatestFallbackCount = getFirstFiniteNumber(
    frontendTiming.results_latest_fallback_count,
    frontendTiming.resultsLatestFallbackCount,
  );
  const totalBackendMs = getFirstFiniteNumber(diagnostics.total_backend_ms, diagnostics.totalBackendMs);
  const eveningSourceSnapshotLookupMs = getFirstFiniteNumber(stages.source_snapshot_lookup);
  const eveningTargetSnapshotLookupMs = getFirstFiniteNumber(stages.target_evening_snapshot_lookup);
  const eveningOrderSyncMs = getFirstFiniteNumber(stages.sync_evening_standard_orders_to_pg, stages.update_source_orders);
  const eveningSourceWorkbookMs = getFirstFiniteNumber(stages.source_workbook_data);
  const eveningTargetWorkbookMs = getFirstFiniteNumber(stages.target_evening_workbook_data);
  const eveningWorkbookMs = sumFiniteNumbers(eveningSourceWorkbookMs, eveningTargetWorkbookMs);
  const currentEveningSourceRebuildMs = sumFiniteDiagnosticStagesByPrefix(stages, "source_evening_standard_");
  const currentEveningTargetRebuildMs = sumFiniteDiagnosticStagesByPrefix(stages, "target_evening_standard_");
  const eveningSourceRebuildMs = getFirstFiniteNumber(
    currentEveningSourceRebuildMs,
    stages.source_rebuild_latest_snapshot,
  );
  const eveningTargetRebuildMs = getFirstFiniteNumber(
    currentEveningTargetRebuildMs,
    stages.target_evening_rebuild_latest_snapshot,
  );
  const eveningTransferRebuildMs = sumFiniteNumbers(eveningSourceRebuildMs, eveningTargetRebuildMs);
  const eveningSourceRebaseMs = getFirstFiniteNumber(
    stages.source_evening_standard_rebase_planning_state,
    stages.source_rebase_planning_state,
  );
  const eveningTargetRebaseMs = getFirstFiniteNumber(
    stages.target_evening_standard_rebase_planning_state,
    stages.target_evening_rebase_planning_state,
  );
  const eveningStateRebaseMs = sumFiniteNumbers(eveningSourceRebaseMs, eveningTargetRebaseMs);
  const localPatchDurationMs = getFirstFiniteNumber(
    stages.local_move_delivery_patch,
    stages.local_move_delivery_group_patch,
    stages.local_bulk_truck_reassignment_patch,
    stages.local_suspend_patch,
    stages.local_suspend_group_patch,
    stages.suspended_period_patch,
    stages.local_reactivate_patch,
    stages.local_order_edit_patch,
    stages.local_truck_route_reoptimization_patch,
    stages.local_preloaded_previous_day_rebuild,
    stages.local_remove_preloaded_previous_day_rebuild,
    stages.rebuild_end_of_day_load_target_snapshot,
    stages.rebuild_end_of_day_load_source_assignment_snapshot,
    stages.rebuild_end_of_day_load_source_snapshot,
    stages.finalize_end_of_day_load_assignment_target_routes,
    stages.end_of_day_load_assignment_target_route_finalize,
    stages.end_of_day_load_removal_target_route_finalize,
    eveningTransferRebuildMs,
    stages.source_changes_route_reoptimization,
  );
  const fullRebuildMs = getFirstFiniteNumber(stages.rebuild_latest_snapshot);
  const rebuildPreparationMs = sumFiniteNumbers(
    rebuildDiagnostics.reconcile_planning_state_ms,
    rebuildDiagnostics.apply_planning_state_ms,
    rebuildDiagnostics.collect_exclusion_scope_ms,
    rebuildDiagnostics.build_optimization_orders_ms,
    rebuildDiagnostics.filter_manual_mix_exclusions_ms,
    rebuildDiagnostics.apply_confirmed_locks_ms,
    rebuildDiagnostics.load_optimizer_settings_ms,
  );
  const rebuildSolverMs = getFirstFiniteNumber(
    rebuildDiagnostics.optimize_orders_to_trucks_ms,
    rebuildSolverBreakdown.ortools_total_ms,
    rebuildSolverBreakdown.solver_ms,
  );
  const rebuildRoutesMs = getFirstFiniteNumber(rebuildDiagnostics.enrich_routes_ms);
  const rebuildViewMs = getFirstFiniteNumber(rebuildDiagnostics.build_optimization_view_ms);
  const rebuildStoreMs = getFirstFiniteNumber(rebuildDiagnostics.store_latest_snapshot_ms);
  const rebuildOrderCount = getFirstFiniteNumber(rebuildDiagnostics.optimization_order_count);
  const rebuildTruckCount = getFirstFiniteNumber(rebuildDiagnostics.optimization_truck_count);
  const rebuildExcludedTruckCount = getFirstFiniteNumber(rebuildDiagnostics.excluded_truck_count);
  const rebuildTemporaryLockCount = getFirstFiniteNumber(rebuildDiagnostics.temporary_locked_order_count);
  const localSolverSkipped = detailObjects.some((detail) => Boolean(detail.local_solver_skipped));
  const payloadSplitFastPath = detailObjects.some((detail) => Boolean(detail.payload_split_fast_path));
  const payloadSplitFastPathReasonCode = getFirstDiagnosticTextFromKeys(detailObjects, ["payload_split_fast_path_reason_code"]);
  const localSolverSkipReason = getFirstDiagnosticTextFromKeys(detailObjects, ["local_solver_skip_reason"]);
  const localSolverSkippedNote = localSolverSkipReason === "payload_split_fast_reject_capacity_insufficient"
    ? "Sauté: le split direct a déjà rejeté la capacité cible."
    : localSolverSkipReason === "ordered_replay_only"
      ? "Sauté: la séquence source/cible a été reconstruite par replay ordonné."
    : localSolverSkipReason === "ordered_replay_suspended_group"
      ? "Sauté: groupe suspendu reconstruit par replay ordonné."
    : payloadSplitFastPathReasonCode === "source_drained_bulk_group"
    ? "Sauté: le groupe vide le camion source; split direct source/cible."
    : payloadSplitFastPathReasonCode === "multi_order_bulk_group"
      ? "Sauté: groupe multi-commandes; split direct source/cible."
    : payloadSplitFastPathReasonCode === "single_trip_limit"
      ? "Sauté: split direct déclenché par single_trip_limit."
      : payloadSplitFastPathReasonCode
        ? `Sauté: split direct ${payloadSplitFastPathReasonCode}.`
        : "Sauté: split direct source/cible.";
  const rebuildMode = normalizeReassignmentDiagnosticText(diagnostics.rebuild_mode_effective)
    || getFirstDiagnosticTextFromKeys(detailObjects, ["rebuild_mode_effective"])
    || getFirstDiagnosticTextFromKeys(detailObjects, ["optimization_mode_effective"])
    || normalizeReassignmentDiagnosticText(diagnostics.rebuild_mode_requested);
  const protectAfterReassignment = resolveProtectAfterReassignmentFlag(diagnostics, detailObjects);
  const effectiveOptimizationMode = getFirstDiagnosticTextFromKeys(detailObjects, ["optimization_mode_effective"])
    || rebuildMode
    || "fast";
  const solverTimeoutSeconds = getFirstDiagnosticNumberFromKeys(detailObjects, ["optimization_mode_solver_timeout_seconds"]);
  const solverTimeoutMs = isFiniteDiagnosticNumber(solverTimeoutSeconds) ? solverTimeoutSeconds * 1000 : null;
  const localSolverMs = getFirstDiagnosticNumberFromKeys(detailObjects, ["local_solver_ms"]);
  const localSolverCallCount = getFirstDiagnosticNumberFromKeys(detailObjects, ["local_solver_calls"]);
  const reactivateCandidateSolverCount = getFirstDiagnosticNumberFromKeys(detailObjects, ["reactivate_candidate_solver_count"]);
  const candidateTruckCount = getFirstDiagnosticNumberFromKeys(detailObjects, ["candidate_truck_count"]);
  const localizedScopeOrderCount = getFirstDiagnosticNumberFromKeys(detailObjects, ["localized_scope_order_count"]);
  const selectedSequenceScore = isPlainDiagnosticObject(details.selected_sequence_score)
    ? details.selected_sequence_score
    : {};
  const selectedDeferredRuntimeStartMinutes = getFirstFiniteNumber(selectedSequenceScore.deferred_runtime_start_minutes);
  const selectedDeferredRuntimeStartMs = isFiniteDiagnosticNumber(selectedDeferredRuntimeStartMinutes)
    ? selectedDeferredRuntimeStartMinutes * 60000
    : null;
  const selectedEffectiveWindowViolationMinutes = getFirstFiniteNumber(selectedSequenceScore.effective_window_violation_minutes);
  const selectedEffectiveWindowViolationMs = isFiniteDiagnosticNumber(selectedEffectiveWindowViolationMinutes)
    ? selectedEffectiveWindowViolationMinutes * 60000
    : null;
  const sequenceCandidateCount = Array.isArray(details.sequence_candidate_scores)
    ? details.sequence_candidate_scores.length
    : null;
  const blockingOrderCount = collectDiagnosticTextValuesFromKeys(detailObjects, [
    "blocking_order_ids",
    "source_blocking_order_ids",
    "requested_target_blocking_order_id",
  ]).length || null;
  const requestedTargetRequiredMinutes = getFirstDiagnosticNumberFromKeys(detailObjects, ["requested_target_rejection_required_minutes"]);
  const requestedTargetAvailableMinutes = getFirstDiagnosticNumberFromKeys(detailObjects, ["requested_target_rejection_available_minutes"]);
  const requestedTargetRequiredMs = isFiniteDiagnosticNumber(requestedTargetRequiredMinutes)
    ? requestedTargetRequiredMinutes * 60000
    : null;
  const requestedTargetAvailableMs = isFiniteDiagnosticNumber(requestedTargetAvailableMinutes)
    ? requestedTargetAvailableMinutes * 60000
    : null;
  const requestedTargetDeficitMs = (
    isFiniteDiagnosticNumber(requestedTargetRequiredMinutes)
    && isFiniteDiagnosticNumber(requestedTargetAvailableMinutes)
    && requestedTargetRequiredMinutes > requestedTargetAvailableMinutes
  )
    ? (requestedTargetRequiredMinutes - requestedTargetAvailableMinutes) * 60000
    : null;
  const routeEnrichmentMs = getFirstDiagnosticNumberFromKeys(detailObjects, ["local_route_enrichment_ms"]);
  const routeReusedCount = getFirstDiagnosticNumberFromKeys(detailObjects, ["local_route_reused_count"]);
  const routeCacheMissCount = getFirstDiagnosticNumberFromKeys(detailObjects, ["local_route_cache_miss_count"]);
  const routeRecomputedCount = getFirstFiniteNumber(
    getFirstDiagnosticNumberFromKeys(detailObjects, ["local_route_recomputed_count"]),
    ...payloadRouteRefreshStats.map((stats) => stats.payload_route_recomputed_count),
  );
  const routeRefreshSkippedCount = getFirstFiniteNumber(
    ...payloadRouteRefreshStats.map((stats) => stats.payload_route_recompute_skipped_count),
  );
  const routeRefreshErrorCount = getFirstFiniteNumber(
    ...payloadRouteRefreshStats.map((stats) => stats.payload_route_recompute_error_count),
  );
  const routeRefreshMs = getFirstFiniteNumber(stages.payload_route_refresh);
  const candidateSequenceCount = getFirstFiniteNumber(
    payloadSplitDiagnostics.target_candidate_sequence_count,
    details.target_rebuild_unrestricted_candidate_count,
  );
  const sourceRebuildAttemptCount = getFirstFiniteNumber(payloadSplitDiagnostics.source_rebuild_attempt_count);
  const targetRebuildAttemptCount = getFirstFiniteNumber(payloadSplitDiagnostics.target_rebuild_attempt_count);
  const splitAttemptCount = getFirstFiniteNumber(payloadSplitDiagnostics.payload_sequence_split_attempt_count);
  const payloadSplitTotalMs = getFirstFiniteNumber(
    payloadSplitDiagnostics.payload_sequence_split_total_ms,
    payloadSplitDiagnostics.payload_sequence_split_attempt_total_ms,
    rawPayloadSplitDiagnostics.payload_sequence_split_attempt_total_ms,
  );
  const fallbackSolverMs = getFirstFiniteNumber(
    payloadSplitDiagnostics.fallback_local_solver_ms,
    payloadSplitDiagnostics.local_solver_ms,
    rawPayloadSplitDiagnostics.local_solver_ms,
  );
  const sourceRebuildMs = getFirstFiniteNumber(
    payloadSplitDiagnostics.source_rebuild_ms,
    rawPayloadSplitDiagnostics.source_rebuild_ms,
  );
  const targetRebuildMs = getFirstFiniteNumber(
    payloadSplitDiagnostics.target_rebuild_ms,
    rawPayloadSplitDiagnostics.target_rebuild_ms,
  );
  const targetLockedScopeEntryMs = getFirstFiniteNumber(
    payloadSplitDiagnostics.target_locked_scope_entry_ms,
    rawPayloadSplitDiagnostics.target_locked_scope_entry_ms,
  );
  const confirmedOrderValidation = isPlainDiagnosticObject(diagnostics.confirmed_order_validation)
    ? diagnostics.confirmed_order_validation
    : {};
  const confirmedValidationPrepMs = sumFiniteNumbers(
    confirmedOrderValidation.reconcile_planning_state_ms,
    confirmedOrderValidation.build_optimization_orders_ms,
    confirmedOrderValidation.apply_confirmed_locks_ms,
  );
  const confirmedValidationSolverMs = getFirstFiniteNumber(confirmedOrderValidation.optimize_orders_to_trucks_ms);
  const confirmedValidationSkipped = Boolean(confirmedOrderValidation.global_solver_skipped);
  const confirmedValidationOrderCount = getFirstFiniteNumber(
    confirmedOrderValidation.optimization_order_count,
    confirmedOrderValidation.order_count,
  );
  const confirmedValidationTruckCount = getFirstFiniteNumber(confirmedOrderValidation.truck_count);
  const confirmedAssignmentValidationMs = getFirstFiniteNumber(stages.validate_confirmed_order_assignment);
  const canonicalPersistMs = getFirstFiniteNumber(stages.persist_order_update_canonical_state, stages.persist_segment_reorder_canonical_state);
  const pgWorkbookLoadMs = getFirstFiniteNumber(stages.load_pg_workbook_data, stages.load_workbook_data);
  const legacySnapshotSaveMs = getFirstFiniteNumber(stages.persist_latest_snapshot, stages.persist_latest_optimization_snapshot);
  const snapshotSaveMs = getFirstFiniteNumber(stages.pg_persistence_snapshot_store, legacySnapshotSaveMs);
  const planningStateSaveMs = getFirstFiniteNumber(stages.pg_persistence_planning_state_store, stages.save_planning_state);
  const savedContextReadMs = getFirstFiniteNumber(stages.read_saved_planning_context);
  const runtimePersistMs = getFirstFiniteNumber(
    stages.persist_segment_reorder_runtime_bundle,
    stages.persist_runtime_bundle,
    stages.persist_deferred_runtime_bundle,
    stages.persist_fast_runtime_bundle,
    stages.persist_runtime_state_pg,
    sumFiniteNumbers(legacySnapshotSaveMs, stages.save_planning_state),
  );
  const pgFinalConnectionReadyMs = getFirstFiniteNumber(stages.pg_final_connection_ready);
  const hasPgFinalAdvisoryLockBreakdown = isFiniteDiagnosticNumber(stages.pg_final_advisory_lock);
  const pgFinalAdvisoryLockMs = getFirstFiniteNumber(
    stages.pg_final_advisory_lock,
    diagnostics.pg_final_lock_wait_ms,
  );
  const pgPersistenceCommitMs = getFirstFiniteNumber(stages.pg_persistence_commit);
  const responsePayloadMs = getFirstFiniteNumber(
    stages.build_planning_json_response,
    stages.build_planning_result_payload,
  );
  const localizedScopeValidationMs = getFirstFiniteNumber(stages.validate_localized_order_edit_scope);
  const confirmOrderOverrideMs = getFirstFiniteNumber(stages.confirm_order_override);
  const planningStateRebaseMs = getFirstFiniteNumber(stages.rebase_planning_state_after_optimization);
  const metrics = [
    buildReassignmentDiagnosticMetric({
      key: "reassignment-click-to-visible",
      label: "Temps réel",
      note: "Du clic confirmé au planning rendu dans le navigateur; inclut le serveur, le transfert, le parsing et la synchronisation.",
      rawValue: clickToVisibleMs,
      tone: clickToVisibleMs > 10000 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-response-json-parsed",
      label: "Réponse JSON prête",
      note: "Du clic confirmé à la fin du décodage JSON de la réponse de mutation.",
      rawValue: responseJsonParsedMs,
      tone: "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-results-latest-fallback",
      label: "GET de secours",
      note: "Nombre de rechargements forcés de /api/results/latest parce que le payload de mutation ne suffisait pas à resynchroniser Dispatch.",
      rawValue: resultsLatestFallbackCount,
      tone: resultsLatestFallbackCount > 0 ? "warning" : "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-total-backend",
      label: "Total backend",
      note: "Traitement serveur uniquement; exclut le transfert, le parsing et le rendu navigateur.",
      rawValue: totalBackendMs,
      tone: totalBackendMs > 10000 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-local-patch",
      label: "Patch local",
      note: isReassignmentAction
        ? "Temps backend du rebuild source/cible."
        : "Temps backend du rebuild localisé.",
      rawValue: localPatchDurationMs,
      tone: "info",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-selected-deferred-start",
      label: "Départ repoussé",
      note: "Attente implicite au dépôt dans la séquence locale choisie.",
      rawValue: selectedDeferredRuntimeStartMs,
      tone: selectedDeferredRuntimeStartMinutes > 120 ? "warning" : selectedDeferredRuntimeStartMinutes > 0 ? "neutral" : "success",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-selected-window-violation",
      label: "Violation fenêtre",
      note: "Minutes hors de la fenêtre effective pour la priorité éditée.",
      rawValue: selectedEffectiveWindowViolationMs,
      tone: selectedEffectiveWindowViolationMinutes > 0 ? "warning" : "success",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-confirmed-assignment-validation",
      label: "Validation camion",
      note: "Validation que la commande protégée reste faisable sur le camion courant.",
      rawValue: confirmedAssignmentValidationMs,
      tone: confirmedAssignmentValidationMs > 10000 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-confirmed-assignment-solver",
      label: "Solveur validation",
      note: confirmedValidationSkipped
        ? "Sauté: le rebuild localisé et le scope final prouvent déjà le verrou camion."
        : "Solveur global lancé uniquement pour valider le verrou camion de la commande.",
      rawValue: confirmedValidationSolverMs,
      tone: confirmedValidationSkipped ? "success" : confirmedValidationSolverMs > 10000 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-confirmed-assignment-prep",
      label: "Prépa validation",
      note: "Reconcile planning, construction des commandes et application des locks avant la validation.",
      rawValue: confirmedValidationPrepMs,
      tone: "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-runtime-persist",
      label: "Persistance PG",
      note: "Durée globale de persistance; inclut les sous-étapes PG affichées séparément lorsqu'elles sont mesurées.",
      rawValue: runtimePersistMs,
      tone: runtimePersistMs > 3000 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-snapshot-save",
      label: "Sauvegarde résultat",
      note: "Écriture du résultat; comprise dans la durée globale de persistance.",
      rawValue: snapshotSaveMs,
      tone: "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-state-save",
      label: "Sauvegarde planning",
      note: "Écriture du planning; comprise dans la durée globale de persistance.",
      rawValue: planningStateSaveMs,
      tone: "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-saved-context-read",
      label: "Relecture enregistrée",
      note: "Relecture indépendante du planning enregistré et de sa version avant la réponse.",
      rawValue: savedContextReadMs,
      tone: "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-pg-final-connection-ready",
      label: "Connexion PG finale",
      note: "Obtention de la connexion PostgreSQL finale, incluant sa vérification avant la persistance.",
      rawValue: pgFinalConnectionReadyMs,
      tone: pgFinalConnectionReadyMs > 500 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-pg-final-advisory-lock",
      label: hasPgFinalAdvisoryLockBreakdown ? "Verrou advisory final" : "Attente PG finale",
      note: hasPgFinalAdvisoryLockBreakdown
        ? "Attente du verrou advisory de journée après que la connexion PostgreSQL est prête."
        : "Ancienne mesure composite: connexion PostgreSQL, vérification et verrou final ne sont pas séparés.",
      rawValue: pgFinalAdvisoryLockMs,
      tone: pgFinalAdvisoryLockMs > 500 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-pg-persistence-commit",
      label: "Commit PG",
      note: "Validation atomique en base des écritures de la persistance runtime.",
      rawValue: pgPersistenceCommitMs,
      tone: pgPersistenceCommitMs > 500 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-response-payload",
      label: "Réponse payload",
      note: "Construction du payload résultat et de la réponse JSON renvoyée au frontend.",
      rawValue: responsePayloadMs,
      tone: responsePayloadMs > 3000 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-scope-validation",
      label: "Validation scope",
      note: "Validation de la portée localisée avant d'accepter le rebuild du camion.",
      rawValue: localizedScopeValidationMs,
      tone: localizedScopeValidationMs > 3000 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-confirm-order",
      label: "Protection cmd",
      note: "Application de la protection/confirmation de la commande éditée.",
      rawValue: confirmOrderOverrideMs,
      tone: confirmOrderOverrideMs > 3000 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-canonical-persist",
      label: "Écriture canonique",
      note: "Sauvegarde de la priorité/heure dans les données source.",
      rawValue: canonicalPersistMs,
      tone: canonicalPersistMs > 3000 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-pg-workbook-load",
      label: "Lecture PG",
      note: "Lecture et reconstruction du workbook depuis PostgreSQL.",
      rawValue: pgWorkbookLoadMs,
      tone: pgWorkbookLoadMs > 3000 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-state-rebase",
      label: "Rebase planning",
      note: "Rebase de l'état planning après le rebuild localisé.",
      rawValue: planningStateRebaseMs,
      tone: "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-evening-source-lookup",
      label: `Lookup ${sourcePeriodLabel}`,
      note: `Lecture du snapshot source ${sourcePeriodLabel.toLowerCase()} avant le transfert inter-période.`,
      rawValue: eveningSourceSnapshotLookupMs,
      tone: "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-evening-target-lookup",
      label: `Lookup ${targetPeriodLabel}`,
      note: `Lecture du snapshot cible ${targetPeriodLabel.toLowerCase()} avant le transfert inter-période.`,
      rawValue: eveningTargetSnapshotLookupMs,
      tone: "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-evening-order-sync",
      label: `Écriture ${targetPeriodLabel}`,
      note: `Mise à jour PostgreSQL des commandes vers la période ${targetPeriodLabel.toLowerCase()}.`,
      rawValue: eveningOrderSyncMs,
      tone: eveningOrderSyncMs > 3000 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-evening-workbook",
      label: "Lecture PG transfert",
      note: "Reconstruction des données source et cible depuis PostgreSQL après le transfert.",
      rawValue: eveningWorkbookMs,
      tone: eveningWorkbookMs > 3000 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-evening-source-rebuild",
      label: `Temps source ${sourcePeriodLabel}`,
      note: "Temps cumulé des étapes source_evening_standard_* mesurées après le retrait du groupe.",
      rawValue: eveningSourceRebuildMs,
      tone: eveningSourceRebuildMs > 3000 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-evening-target-rebuild",
      label: `Temps cible ${targetPeriodLabel}`,
      note: "Temps cumulé des étapes target_evening_standard_* mesurées après l'ajout du groupe.",
      rawValue: eveningTargetRebuildMs,
      tone: eveningTargetRebuildMs > 3000 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-evening-state-rebase",
      label: "Rebase Soir",
      note: "Rebase des états planning jour et soir après les snapshots reconstruits.",
      rawValue: eveningStateRebaseMs,
      tone: "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-confirmed-assignment-orders",
      label: "Cmd validation",
      note: "Commandes envoyées au solveur de validation du verrou camion.",
      rawValue: confirmedValidationOrderCount,
      tone: confirmedValidationOrderCount > 50 ? "warning" : "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-confirmed-assignment-trucks",
      label: "Camions validation",
      note: "Camions envoyés au solveur de validation du verrou camion.",
      rawValue: confirmedValidationTruckCount,
      tone: confirmedValidationTruckCount > 8 ? "warning" : "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-full-rebuild",
      label: "Rebuild complet",
      note: "Fallback ou validation complète déclenchée après le patch local.",
      rawValue: fullRebuildMs,
      tone: fullRebuildMs > 0 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-rebuild-solver",
      label: "Rebuild solveur",
      note: "Temps passé dans l'optimisation globale déclenchée après le patch.",
      rawValue: rebuildSolverMs,
      tone: rebuildSolverMs > 10000 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-rebuild-routes",
      label: "Rebuild routes",
      note: "Enrichissement des routes pendant le rebuild global.",
      rawValue: rebuildRoutesMs,
      tone: rebuildRoutesMs > 10000 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-rebuild-view",
      label: "Vue rebuild",
      note: "Construction du payload résultat après le rebuild global.",
      rawValue: rebuildViewMs,
      tone: "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-rebuild-store",
      label: "Store rebuild",
      note: "Temps de stockage/normalisation du snapshot reconstruit.",
      rawValue: rebuildStoreMs,
      tone: "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-rebuild-prep",
      label: "Prépa rebuild",
      note: "Reconcile, scope, commandes, locks et paramètres avant le solveur global.",
      rawValue: rebuildPreparationMs,
      tone: "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-rebuild-orders",
      label: "Cmd rebuild",
      note: "Commandes envoyées au rebuild global.",
      rawValue: rebuildOrderCount,
      tone: rebuildOrderCount > 50 ? "warning" : "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-rebuild-trucks",
      label: "Camions rebuild",
      note: "Camions évalués par le rebuild global.",
      rawValue: rebuildTruckCount,
      tone: rebuildTruckCount > 8 ? "warning" : "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-rebuild-locks",
      label: "Locks temp.",
      note: "Commandes verrouillées temporairement pendant le rebuild global.",
      rawValue: rebuildTemporaryLockCount,
      tone: rebuildTemporaryLockCount > 0 ? "warning" : "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-rebuild-excluded-trucks",
      label: "Camions exclus",
      note: "Camions conservés hors solveur global parce qu'ils sont gelés ou exclus.",
      rawValue: rebuildExcludedTruckCount,
      tone: rebuildExcludedTruckCount > 0 ? "warning" : "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-local-solver",
      label: action === "optimize_truck_routes" ? "Moteur local" : "Solveur local",
      note: action === "optimize_truck_routes"
        ? "Durée du moteur local, incluant les traitements autour de la recherche de tournée."
        : localSolverSkipped ? localSolverSkippedNote : `Mini-optimisation source/cible en mode ${effectiveOptimizationMode}.`,
      rawValue: localSolverMs,
      tone: localSolverSkipped && localSolverSkipReason !== "ordered_replay_only" ? "warning" : "success",
    }),
    ...buildSolverExecutionMetrics(diagnostics),
    ...buildSolverRoutingSummaryMetrics(diagnostics),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-local-solver-calls",
      label: "Appels solveur",
      note: "Appels du mini-solveur pendant le patch local.",
      rawValue: localSolverCallCount,
      tone: localSolverCallCount > 1 ? "warning" : "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-reactivate-candidate-solvers",
      label: "Solveurs candidats",
      note: "Camions candidats réellement testés par la réactivation.",
      rawValue: reactivateCandidateSolverCount,
      tone: reactivateCandidateSolverCount > 1 ? "warning" : "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-solver-timeout",
      label: "Budget mode",
      note: `Timeout par appel solveur en mode ${effectiveOptimizationMode}.`,
      rawValue: solverTimeoutMs,
      tone: "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-candidate-trucks",
      label: "Camions candidats",
      note: "Camions compatibles considérés avant filtrage par cible demandée.",
      rawValue: candidateTruckCount,
      tone: candidateTruckCount > 8 ? "warning" : "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-local-scope-orders",
      label: "Scope local",
      note: "Commandes dans la portée localisée du patch.",
      rawValue: localizedScopeOrderCount,
      tone: localizedScopeOrderCount > 8 ? "warning" : "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-sequence-candidates",
      label: "Séquences testées",
      note: "Séquences locales faisables scorées pendant le rebuild priorité/horaire.",
      rawValue: sequenceCandidateCount,
      tone: sequenceCandidateCount > 1 ? "success" : "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-blocking-orders",
      label: "Blocages",
      note: "Commandes qui empêchent la réactivation ciblée.",
      rawValue: blockingOrderCount,
      tone: blockingOrderCount > 0 ? "warning" : "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-target-required-time",
      label: "Temps requis",
      note: "Temps camion requis par la cible d'après le rejet local.",
      rawValue: requestedTargetRequiredMs,
      tone: "warning",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-target-available-time",
      label: "Temps dispo",
      note: "Temps quotidien disponible sur le camion cible.",
      rawValue: requestedTargetAvailableMs,
      tone: "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-target-time-deficit",
      label: "Écart temps",
      note: "Dépassement temps qui bloque la cible demandée.",
      rawValue: requestedTargetDeficitMs,
      tone: "warning",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-payload-split-total",
      label: payloadSplitFastPath ? "Split total" : "Fallback total",
      note: payloadSplitFastPath
        ? "Temps total du split direct source/cible."
        : "Temps total du payload-split source/cible.",
      rawValue: payloadSplitTotalMs,
      tone: payloadSplitTotalMs > 0 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-fallback-solver",
      label: "Solveur fallback",
      note: "Solveur cumulé dans le fallback payload-split.",
      rawValue: fallbackSolverMs,
      tone: fallbackSolverMs > 0 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-route-enrichment",
      label: "Routes",
      note: "Enrichissement HERE/cache appliqué après rebuild.",
      rawValue: routeEnrichmentMs,
      tone: "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-routes-reused",
      label: "Routes réutilisées",
      note: "Routes reprises depuis le payload précédent.",
      rawValue: routeReusedCount,
      tone: "success",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-routes-recomputed",
      label: "Routes recalculées",
      note: isReassignmentAction
        ? "Routes recalculées pour la portée source/cible."
        : "Routes recalculées pour la portée locale.",
      rawValue: routeRecomputedCount,
      tone: routeRecomputedCount > 0 ? "warning" : "success",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-route-cache-misses",
      label: "Manques cache route",
      note: "Itinéraires absents du cache pendant le replay rapide; tout manque bloque la mutation avant sauvegarde.",
      rawValue: routeCacheMissCount,
      tone: routeCacheMissCount > 0 ? "danger" : "success",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-route-refresh",
      label: "Refresh routes",
      note: "Temps de refresh des routes du payload.",
      rawValue: routeRefreshMs,
      tone: "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-routes-skipped",
      label: "Routes inchangées",
      note: "Routes déjà valides pendant le refresh payload.",
      rawValue: routeRefreshSkippedCount,
      tone: "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "planning-route-errors",
      label: "Erreurs routes",
      note: "Routes qui n'ont pas pu être recalculées pendant le refresh.",
      rawValue: routeRefreshErrorCount,
      tone: routeRefreshErrorCount > 0 ? "danger" : "success",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-target-candidates",
      label: "Séquences cible",
      note: "Candidats testés par le fallback payload-split.",
      rawValue: candidateSequenceCount,
      tone: candidateSequenceCount > 0 ? "warning" : "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-source-rebuild-ms",
      label: "Temps source",
      note: "Temps de reconstruction du camion source dans le fallback.",
      rawValue: sourceRebuildMs,
      tone: sourceRebuildMs > 0 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-target-rebuild-ms",
      label: "Temps cible",
      note: "Temps de reconstruction du camion cible dans le fallback.",
      rawValue: targetRebuildMs,
      tone: targetRebuildMs > 0 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-target-locked-scope-ms",
      label: "Locked scope",
      note: "Temps passé dans le chemin locked-scope de la cible.",
      rawValue: targetLockedScopeEntryMs,
      tone: targetLockedScopeEntryMs > 0 ? "warning" : "neutral",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-source-rebuilds",
      label: "Rebuild source",
      note: "Tentatives fallback côté camion source.",
      rawValue: sourceRebuildAttemptCount,
      tone: sourceRebuildAttemptCount > 0 ? "warning" : "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-target-rebuilds",
      label: "Rebuild cible",
      note: "Tentatives fallback côté camion cible.",
      rawValue: targetRebuildAttemptCount,
      tone: targetRebuildAttemptCount > 0 ? "warning" : "neutral",
      type: "integer",
    }),
    buildReassignmentDiagnosticMetric({
      key: "reassignment-split-attempts",
      label: "Fallback split",
      note: "Nombre de passes payload-split déclenchées.",
      rawValue: splitAttemptCount,
      tone: splitAttemptCount > 0 ? "warning" : "neutral",
      type: "integer",
    }),
  ].filter(Boolean);
  const maxWorkPolicy = normalizeReassignmentDiagnosticText(diagnostics.max_work_policy);
  const resolutionMode = normalizeReassignmentDiagnosticText(diagnostics.resolution_mode) || "localized_rebuild";
  const moveMode = normalizeReassignmentDiagnosticText(diagnostics.move_mode) || "";
  const reason = normalizeReassignmentDiagnosticText(diagnostics.reason_code)
    || getFirstDiagnosticTextFromKeys(detailObjects, ["requested_target_rejection_code"])
    || normalizeReassignmentDiagnosticText(localPatch.reason)
    || getFirstDiagnosticTextFromKeys(detailObjects, ["reason"]);
  const isRejectedReassignment = normalizeReassignmentDiagnosticText(diagnostics.movement_outcome) === "rejected"
    || moveMode === "local_patch_rejected"
    || (isReassignmentAction && resolutionMode === "persisted_snapshot");
  const segmentReorderRequest = isPlainDiagnosticObject(diagnostics.segment_reorder_request)
    ? diagnostics.segment_reorder_request
    : {};
  const segmentReorderBusinessEntityIds = collectDiagnosticTextValuesFromKeys(
    [segmentReorderRequest],
    ["moved_entity_ids"],
  );
  const segmentReorderMovedEntityIds = segmentReorderBusinessEntityIds.length
    ? segmentReorderBusinessEntityIds
    : collectDiagnosticTextValuesFromKeys([segmentReorderRequest], ["dragged_step_ids"]);
  const segmentReorderTargetEntityId = getFirstDiagnosticTextFromKeys(
    [segmentReorderRequest],
    ["target_entity_id", "target_step_id"],
  );
  const segmentReorderMovement = [
    segmentReorderMovedEntityIds.join(", "),
    segmentReorderTargetEntityId,
  ].filter(Boolean).join(" → ") || "Mouvement non disponible";
  const segmentReorderScopeLabel = {
    loads: "Chargements du camion",
    "deliveries-with-same-parent-load": "Livraisons du même chargement",
    "delivery-block": "Livraisons du même bloc",
  }[normalizeReassignmentDiagnosticText(segmentReorderRequest.scope)]
    || normalizeReassignmentDiagnosticText(segmentReorderRequest.scope);
  const segmentReorderPlacement = normalizeReassignmentDiagnosticText(segmentReorderRequest.placement);
  const segmentReorderPlacementLabel = segmentReorderPlacement === "after"
    ? "Après la cible"
    : segmentReorderPlacement === "before"
      ? "Avant la cible"
      : segmentReorderPlacement;
	const segmentReorderContextItem = action === "segment_reorder" && Object.keys(segmentReorderRequest).length
    ? {
      key: "segment-reorder-request",
      label: "Réordonnancement demandé",
      value: segmentReorderMovement,
      detail: [segmentReorderScopeLabel, segmentReorderPlacementLabel, reason ? `Cause : ${reason}` : ""]
        .filter(Boolean).join(" · "),
    }
    : null;
	const contextItems = hasMovementContract
		? [
			{
				key: "source",
				label: "Source",
				value: movementSourceLabel,
				detail: resolveReassignmentTruckDetail(sourceTruckId, sourceTruckDisplayLabel),
			},
			{
				key: "order",
				label: actionMeta.orderLabel,
				value: orderSummary,
				detail: loadingSiteSummary,
			},
			{
				key: "target",
				label: "Destination",
				value: movementTargetLabel,
				detail: resolveReassignmentTruckDetail(targetTruckId, targetTruckDisplayLabel),
			},
		]
		: isSuspendedSourceMove
    ? [
      {
        key: "source",
        label: "Source",
        value: "Suspendues",
        detail: "",
      },
      {
        key: "order",
        label: actionMeta.orderLabel,
        value: orderSummary,
        detail: loadingSiteSummary
          || getFirstDiagnosticTextFromKeys(detailObjects, ["moved_delivery_id", "delivery_id", "delivery_ids"]),
      },
      {
        key: "target",
        label: "Camion cible",
        value: targetTruckDisplayLabel || currentTruckDisplayLabel || "Cible non disponible",
        detail: resolveReassignmentTruckDetail(
          targetTruckId || currentTruckId,
          targetTruckDisplayLabel || currentTruckDisplayLabel,
        ),
      },
    ]
    : isReassignmentAction
    ? [
      {
        key: "source",
        label: "Camion source",
        value: sourceTruckDisplayLabel || "Source non disponible",
        detail: resolveReassignmentTruckDetail(sourceTruckId, sourceTruckDisplayLabel),
      },
      {
        key: "order",
        label: actionMeta.orderLabel,
        value: orderSummary,
        detail: loadingSiteSummary || normalizeReassignmentDiagnosticText(details.moved_delivery_id),
      },
      {
        key: "target",
        label: "Camion cible",
        value: targetTruckDisplayLabel || "Cible non disponible",
        detail: resolveReassignmentTruckDetail(targetTruckId, targetTruckDisplayLabel),
      },
    ]
    : [
      {
        key: "truck",
        label: actionMeta.truckLabel,
        value: currentTruckDisplayLabel || sourceTruckDisplayLabel || targetTruckDisplayLabel || "Camion non disponible",
        detail: resolveReassignmentTruckDetail(
          currentTruckId || sourceTruckId || targetTruckId,
          currentTruckDisplayLabel || sourceTruckDisplayLabel || targetTruckDisplayLabel,
        ),
      },
      segmentReorderContextItem || {
        key: "order",
        label: actionMeta.orderLabel,
        value: orderSummary,
        detail: loadingSiteSummary
          || getFirstDiagnosticTextFromKeys(detailObjects, ["moved_delivery_id", "delivery_id", "delivery_ids"]),
      },
      targetTruckDisplayLabel && targetTruckDisplayLabel !== currentTruckDisplayLabel ? {
        key: "target",
        label: "Camion cible",
        value: targetTruckDisplayLabel,
        detail: resolveReassignmentTruckDetail(targetTruckId, targetTruckDisplayLabel),
      } : null,
    ].filter(Boolean);
  const timestampItems = buildReassignmentDiagnosticTimestampItems(resultsData, diagnostics);
  const pills = [
    rebuildMode === "fast"
      ? { key: "rebuild-mode", label: "Réaffectation rapide", tone: "success" }
      : rebuildMode === "complete"
        ? { key: "rebuild-mode", label: "Réaffectation complète", tone: "neutral" }
        : null,
    protectAfterReassignment === true
      ? { key: "protect-after-reassignment", label: "On", tone: "success" }
      : protectAfterReassignment === false
        ? { key: "protect-after-reassignment", label: "Off", tone: "warning" }
        : null,
    maxWorkPolicy === "enforced"
      ? {
        key: "max-work-policy",
        label: isEveningStandardTransfer ? "Heures max bloquantes" : "Heures max validées",
        tone: "success",
      }
      : maxWorkPolicy === "not_enforced"
        ? { key: "max-work-policy", label: "Heures max non bloquantes", tone: "warning" }
      : maxWorkPolicy
        ? { key: "max-work-policy", label: `Heures max: ${maxWorkPolicy}`, tone: "warning" }
        : null,
    { key: "resolution", label: ({ localized_rebuild: "Rebuild localisé", suspended_period_transfer: "Transfert entre suspendues", source_target_snapshot_rebuild: "Transfert Jour / Soir" })[resolutionMode] || resolutionMode, tone: resolutionMode === "localized_rebuild" ? "success" : "neutral" },
    payloadSplitFastPath ? { key: "fast-path", label: "Split direct", tone: "warning" } : null,
    moveMode ? { key: "move-mode", label: moveMode === "local_patch" ? "Patch local" : moveMode, tone: moveMode === "local_patch" ? "success" : "warning" } : null,
    reason ? { key: "reason", label: reason, tone: "warning" } : null,
  ].filter(Boolean);

  return {
    availability: "available",
    badgeLabel: isRejectedReassignment ? "Échec" : reason ? "À vérifier" : "Mesuré",
    badgeTone: isRejectedReassignment ? "danger" : reason ? "warning" : "success",
    contextItems,
    emptyMetricsMessage: actionMeta.emptyMetricsMessage,
    metrics,
    message: isRejectedReassignment ? actionMeta.rejectedMessage : actionMeta.message,
    pills,
    sectionTitle: actionMeta.sectionTitle,
    timestampItems,
    signature: [
      action,
      currentTruckId || currentTruckDisplayLabel,
      sourceTruckId || sourceTruckDisplayLabel,
      targetTruckId || targetTruckDisplayLabel,
      orderSummary,
      rebuildMode,
      protectAfterReassignment === null ? "" : String(protectAfterReassignment),
      maxWorkPolicy,
      normalizeInlineText(diagnostics.recorded_at),
      normalizeInlineText(resultsData.captured_at),
      resolveReassignmentDiagnosticDispatchDate(resultsData),
      String(clickToVisibleMs ?? ""),
      String(localPatchDurationMs ?? ""),
      String(localSolverMs ?? ""),
      String(routeCacheMissCount ?? ""),
    ].join("|"),
    toggleAriaLabel: actionMeta.toggleAriaLabel,
  };
}

function splitAssignmentsByActivity(assignments) {
  const activeAssignments = [];
  const idleAssignments = [];

  if (!Array.isArray(assignments)) {
    return { activeAssignments, idleAssignments };
  }

  assignments.forEach((assignment) => {
    if (getAssignmentTripCount(assignment) > 0) {
      activeAssignments.push(assignment);
      return;
    }

    idleAssignments.push(assignment);
  });

  return { activeAssignments, idleAssignments };
}

const NON_RENDERABLE_INACTIVE_STATUSES = new Set([
  "annule",
  "annulee",
  "cancelled",
  "canceled",
  "delivery_suspended",
  "removed",
  "suspended",
]);

function normalizeRenderabilityToken(value) {
  return normalizeInlineText(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();
}

function isActiveRenderabilityContentItem(item) {
  if (!item || typeof item !== "object") {
    return false;
  }
  if (
    item.is_cancelled === true
    || item.is_canceled === true
    || item.is_delivery_suspended === true
    || item.is_removed === true
    || item.is_suspended === true
    || item.cancelled_at
    || item.canceled_at
    || item.removed_at
  ) {
    return false;
  }

  return ![
    item.dispatch_status,
    item.manual_status_code,
    item.planning_status,
    item.status,
    item.status_code,
  ].some((status) => NON_RENDERABLE_INACTIVE_STATUSES.has(normalizeRenderabilityToken(status)));
}

function addRenderabilityOrderIds(orderIds, items) {
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!isActiveRenderabilityContentItem(item)) {
      return;
    }
    const orderId = normalizeInlineText(item?.order_id || item?.orderId);
    if (orderId) {
      orderIds.add(orderId);
    }
    const itemOrderIds = Array.isArray(item?.order_ids)
      ? item.order_ids
      : (Array.isArray(item?.orderIds) ? item.orderIds : []);
    itemOrderIds.forEach((value) => {
      const normalizedOrderId = normalizeInlineText(value);
      if (normalizedOrderId) {
        orderIds.add(normalizedOrderId);
      }
    });
  });
}

function assignmentHasMaterialRouteStop(assignment) {
  return (Array.isArray(assignment?.trips) ? assignment.trips : []).some((trip) => (
    ["stops", "routable_stops"].some((field) => (
      (Array.isArray(trip?.[field]) ? trip[field] : []).some((stop) => (
        stop
        && typeof stop === "object"
        && ["loading", "delivery"].includes(normalizeRenderabilityToken(stop.stop_type || stop.type))
      ))
    ))
  ));
}

function collectLocallyNonRenderableOrderIds(assignments) {
  const nonRenderableOrderIds = new Set();
  collectLocallyNonRenderableTripConflicts(assignments).forEach((conflict) => {
    addRenderabilityOrderIds(nonRenderableOrderIds, [{
      order_ids: conflict.order_ids,
    }]);
  });
  return nonRenderableOrderIds;
}

export function collectLocallyNonRenderableTripConflicts(assignments) {
  const conflicts = [];
  (Array.isArray(assignments) ? assignments : []).forEach((assignment, assignmentIndex) => {
    if (!assignment || typeof assignment !== "object" || assignmentHasMaterialRouteStop(assignment)) {
      return;
    }

    const assignmentOrderIds = new Set();
    ["orders", "clients", "delivery_items", "operational_steps"].forEach((field) => {
      addRenderabilityOrderIds(assignmentOrderIds, assignment[field]);
    });
    (Array.isArray(assignment.trips) ? assignment.trips : []).forEach((trip) => {
      ["orders", "clients", "delivery_items", "operational_steps"].forEach((field) => {
        addRenderabilityOrderIds(assignmentOrderIds, trip?.[field]);
      });
    });
    if (assignmentOrderIds.size === 0) {
      return;
    }

    const firstTrip = (Array.isArray(assignment.trips) ? assignment.trips : [])
      .find((trip) => trip && typeof trip === "object");
    conflicts.push({
      active_order_count: assignmentOrderIds.size,
      code: "non_renderable_trip",
      order_ids: [...assignmentOrderIds],
      trip_id: normalizeInlineText(firstTrip?.trip_id),
      truck_id: normalizeInlineText(assignment.truck_id),
      truck_name: normalizeInlineText(assignment.truck_name || assignment.truck_label),
      source: "local_assignment_fallback",
      source_index: assignmentIndex,
    });
  });
  return conflicts;
}

export function collectNonRenderableTripConflicts(resultsData = {}, assignments = null) {
  const conflicts = [];
  const appendConflict = (conflict, fallback = {}) => {
    if (!conflict || typeof conflict !== "object") {
      return;
    }
    const code = normalizeRenderabilityToken(conflict.code || fallback.code);
    if (code && code !== "non_renderable_trip") {
      return;
    }
    conflicts.push({
      ...fallback,
      ...conflict,
      code: "non_renderable_trip",
      order_id: conflict.order_id || fallback.order_id,
      order_ids: conflict.order_ids || fallback.order_ids,
      delivery_id: conflict.delivery_id || fallback.delivery_id,
      delivery_ids: conflict.delivery_ids || fallback.delivery_ids,
      trip_id: conflict.trip_id || fallback.trip_id,
      truck_id: conflict.truck_id || fallback.truck_id,
      truck_name: conflict.truck_name || fallback.truck_name || fallback.truck_label,
    });
  };

  const explicitConflicts = Array.isArray(resultsData?.non_renderable_trip_conflicts)
    ? resultsData.non_renderable_trip_conflicts
    : (Array.isArray(resultsData?.renderability?.conflicts) ? resultsData.renderability.conflicts : []);
  explicitConflicts.forEach((conflict) => appendConflict(conflict));

  const sourceChangeDetails = Array.isArray(resultsData?.source_changes?.details?.integrity_conflicts)
    ? resultsData.source_changes.details.integrity_conflicts
    : [];
  sourceChangeDetails.forEach((detail) => {
    if (!detail || typeof detail !== "object") {
      return;
    }
    const fallback = {
      code: detail.change_type,
      delivery_id: detail.delivery_id,
      delivery_ids: detail.delivery_ids,
      missing_delivery_id: detail.missing_delivery_id,
      missing_delivery_ids: detail.missing_delivery_ids,
      missing_order_id: detail.missing_order_id,
      missing_order_ids: detail.missing_order_ids,
      order_id: detail.order_id,
      order_ids: detail.order_ids,
      trip_id: detail.trip_id,
      truck_id: detail.truck_id,
      truck_name: detail.truck_name,
      truck_label: detail.truck_label,
    };
    const nestedConflicts = Array.isArray(detail.integrity_conflicts)
      ? detail.integrity_conflicts
      : (Array.isArray(detail.integrityConflicts) ? detail.integrityConflicts : []);
    const matchingConflicts = nestedConflicts.filter((conflict) => (
      normalizeRenderabilityToken(conflict && typeof conflict === "object" ? conflict.code : conflict)
      === "non_renderable_trip"
    ));
    if (matchingConflicts.length > 0) {
      matchingConflicts.forEach((conflict) => appendConflict(conflict, fallback));
    } else if (normalizeRenderabilityToken(detail.change_type) === "non_renderable_trip") {
      appendConflict({}, fallback);
    }
  });

  const resolvedAssignments = Array.isArray(assignments)
    ? assignments
    : (Array.isArray(resultsData?.assignments) ? resultsData.assignments : []);
  collectLocallyNonRenderableTripConflicts(resolvedAssignments).forEach((conflict) => appendConflict(conflict));
  return conflicts;
}

function resolveNonRenderableOrderCount(resultsData, assignments) {
  const explicitCounts = [
    resultsData?.non_renderable_order_count,
    resultsData?.renderability?.non_renderable_order_count,
    resultsData?.renderability?.counts?.orders,
  ];
  for (const explicitCount of explicitCounts) {
    const numericCount = Number(explicitCount);
    if (explicitCount !== null && explicitCount !== undefined && Number.isFinite(numericCount) && numericCount >= 0) {
      return Math.round(numericCount);
    }
  }

  const conflicts = collectNonRenderableTripConflicts(resultsData, assignments);
  if (conflicts.length > 0) {
    const conflictOrderIds = new Set();
    conflicts.forEach((conflict) => {
      addRenderabilityOrderIds(conflictOrderIds, [{
        order_id: conflict?.order_id,
        order_ids: conflict?.order_ids,
      }]);
    });
    if (conflictOrderIds.size > 0) {
      return conflictOrderIds.size;
    }
  }

  return collectLocallyNonRenderableOrderIds(assignments).size;
}

function normalizeOrderId(value) {
  return typeof value === "string" ? value.trim() : "";
}

function stepHasManualReorderProtection(step) {
  if (!step || typeof step !== "object" || !step.is_manually_reordered) {
    return false;
  }

  const manualReorderReleaseDeliveryId = typeof step.manual_reorder_release_delivery_id === "string"
    ? step.manual_reorder_release_delivery_id.trim()
    : "";
  const stepDeliveryId = typeof step.delivery_id === "string" ? step.delivery_id.trim() : "";

  return Boolean(manualReorderReleaseDeliveryId || stepDeliveryId);
}

function itemHasManualReorderProtection(item) {
  if (!item || typeof item !== "object" || !item.is_manually_reordered) {
    return false;
  }

  const manualReorderScope = typeof item.manual_reorder_scope === "string"
    ? item.manual_reorder_scope.trim()
    : "";

  return Boolean(manualReorderScope);
}

export function collectVisibleOrderProtectionSummary(assignments, resultsData = {}) {
  const orderMap = new Map();

  const ensureOrderEntry = (orderId) => {
    const normalizedOrderId = normalizeOrderId(orderId);

    if (!normalizedOrderId) {
      return null;
    }

    const existingEntry = orderMap.get(normalizedOrderId);
    if (existingEntry) {
      return existingEntry;
    }

    const nextEntry = {
      deliveryConfirmed: false,
      hasExplicitProtection: false,
      hasManualReassignment: false,
      hasManualReorder: false,
      loadingConfirmed: false,
      orderId: normalizedOrderId,
    };
    orderMap.set(normalizedOrderId, nextEntry);
    return nextEntry;
  };

  const readItemOrderIds = (item) => {
    if (!item || typeof item !== "object") {
      return [];
    }
    return Array.from(new Set([
      item.order_id,
      item.orderId,
      ...(Array.isArray(item.order_ids) ? item.order_ids : []),
      ...(Array.isArray(item.orderIds) ? item.orderIds : []),
    ].map(normalizeOrderId).filter(Boolean)));
  };

  const registerProtectedItem = (item) => {
    const orderIds = readItemOrderIds(item);
    if (!orderIds.length) {
      return;
    }
    const manualStatusCode = normalizeInlineText(item?.manual_status_code || item?.manualStatusCode).toLowerCase();
    const statusCode = normalizeInlineText(item?.status_code || item?.statusCode).toLowerCase();
    const assignmentOrigin = normalizeInlineText(
      item?.assignment_origin
      || item?.assignmentOrigin
      || item?.order_assignment_origin
      || item?.orderAssignmentOrigin,
    ).toLowerCase();
    const stepType = normalizeInlineText(item?.step_type || item?.stepType).toLowerCase();
    const currentTruckId = normalizeInlineText(item?.current_truck_id || item?.currentTruckId);
    const optimizedTruckId = normalizeInlineText(item?.optimized_truck_id || item?.optimizedTruckId);
    const dispatcherExecutionStatus = normalizeInlineText(
      item?.dispatcher_execution_status || item?.dispatcherExecutionStatus,
    ).toLowerCase();
    const isLoadingConfirmed = item?.is_loading_confirmed === true
      || item?.isLoadingConfirmed === true
      || (stepType === "loading" && (
        item?.is_confirmed === true
        || item?.isConfirmed === true
        || manualStatusCode === "loading_confirmed"
      ));
    const isDeliveryConfirmed = item?.is_delivery_confirmed === true
      || item?.isDeliveryConfirmed === true
      || manualStatusCode === "delivery_confirmed"
      || (stepType === "delivery" && (item?.is_confirmed === true || item?.isConfirmed === true));
    const hasExplicitProtection = (
      (item?.is_confirmed === true || item?.isConfirmed === true) && !stepType
    ) || ["manual_confirmed", "automatic_confirmed"].includes(statusCode)
      || dispatcherExecutionStatus === "frozen"
      || dispatcherExecutionStatus === "delivered"
      || item?.is_dispatcher_frozen === true
      || item?.isDispatcherFrozen === true
      || item?.is_dispatcher_delivered === true
      || item?.isDispatcherDelivered === true;
    const hasManualReassignment = assignmentOrigin === "manual"
      || manualStatusCode === "delivery_reassigned"
      || Boolean(currentTruckId && optimizedTruckId && currentTruckId !== optimizedTruckId);
    const hasManualReorder = itemHasManualReorderProtection(item)
      || stepHasManualReorderProtection(item);

    orderIds.forEach((orderId) => {
      const entry = ensureOrderEntry(orderId);
      if (!entry) {
        return;
      }
      entry.loadingConfirmed = entry.loadingConfirmed || isLoadingConfirmed;
      entry.deliveryConfirmed = entry.deliveryConfirmed || isDeliveryConfirmed;
      entry.hasExplicitProtection = entry.hasExplicitProtection || hasExplicitProtection;
      entry.hasManualReassignment = entry.hasManualReassignment || hasManualReassignment;
      entry.hasManualReorder = entry.hasManualReorder || hasManualReorder;
    });
  };

  const registerOrderCollection = (items, { inspectProtection = true } = {}) => {
    (Array.isArray(items) ? items : []).forEach((item) => {
      if (inspectProtection) {
        registerProtectedItem(item);
        return;
      }
      readItemOrderIds(item).forEach(ensureOrderEntry);
    });
  };

  (Array.isArray(assignments) ? assignments : []).forEach((assignment) => {
    registerOrderCollection(assignment?.orders);
    registerOrderCollection(assignment?.delivery_items);
    registerOrderCollection(assignment?.operational_steps);
    (Array.isArray(assignment?.trips) ? assignment.trips : []).forEach((trip) => {
      registerOrderCollection(trip?.orders);
      registerOrderCollection(trip?.delivery_items);
      registerOrderCollection(trip?.clients);
      registerOrderCollection(trip?.operational_steps);
      registerOrderCollection(trip?.stops);
      registerOrderCollection(trip?.routable_stops);
    });
  });

  registerOrderCollection(resultsData?.unassigned_orders, { inspectProtection: false });

  const protectedOrderIds = [];
  const unprotectedOrderIds = [];
  Array.from(orderMap.values())
    .sort((leftEntry, rightEntry) => leftEntry.orderId.localeCompare(rightEntry.orderId, "fr", { numeric: true, sensitivity: "base" }))
    .forEach((entry) => {
      if (
        (entry.loadingConfirmed && entry.deliveryConfirmed)
        || entry.hasExplicitProtection
        || entry.hasManualReassignment
        || entry.hasManualReorder
      ) {
        protectedOrderIds.push(entry.orderId);
        return;
      }

      unprotectedOrderIds.push(entry.orderId);
    });

  return {
    protectedOrderIds,
    totalVisibleOrders: orderMap.size,
    unprotectedOrderIds,
  };
}

function collectAssignmentPreviewOrderIds(assignment, targetOrderIds) {
  if (!assignment || typeof assignment !== "object" || !(targetOrderIds instanceof Set)) {
    return;
  }

  const registerItems = (items) => {
    (Array.isArray(items) ? items : []).forEach((item) => {
      const orderId = normalizeOrderId(item?.order_id || item?.orderId);
      if (orderId) {
        targetOrderIds.add(orderId);
      }
      (Array.isArray(item?.order_ids) ? item.order_ids : []).forEach((candidateOrderId) => {
        const normalizedOrderId = normalizeOrderId(candidateOrderId);
        if (normalizedOrderId) {
          targetOrderIds.add(normalizedOrderId);
        }
      });
    });
  };

  registerItems(assignment.orders);
  registerItems(assignment.delivery_items);
  registerItems(assignment.operational_steps);
  (Array.isArray(assignment.trips) ? assignment.trips : []).forEach((trip) => {
    registerItems(trip?.orders);
    registerItems(trip?.delivery_items);
    registerItems(trip?.operational_steps);
    registerItems(trip?.stops);
  });
}

export function collectOptimizationPreviewReserveState(
  assignments,
  resultsData = {},
  revealedOrderIds = [],
  additionalProtectedOrderIds = [],
  { operationKind = "optimization" } = {},
) {
  const sourceAssignments = Array.isArray(assignments) && assignments.length > 0
    ? assignments
    : (Array.isArray(resultsData?.assignments) ? resultsData.assignments : []);
  const visibleSummary = collectVisibleOrderProtectionSummary(sourceAssignments, resultsData);
  const isPlanningReset = operationKind === "planning_reset";
  const retainedOrderIds = collectOptimizationPreviewRetainedOrderIds(sourceAssignments, { operationKind });
  const excludedTruckIds = new Set(
    (isPlanningReset ? [] : collectFleetOptimizationExcludedTrucks(sourceAssignments, resultsData))
      .map((entry) => normalizeInlineText(entry?.truckId))
      .filter(Boolean),
  );
  const excludedOrderIds = new Set();
  sourceAssignments.forEach((assignment) => {
    if (excludedTruckIds.has(normalizeInlineText(assignment?.truck_id || assignment?.truckId))) {
      collectAssignmentPreviewOrderIds(assignment, excludedOrderIds);
    }
  });
  const visibleOrderIds = new Set([
    ...visibleSummary.protectedOrderIds,
    ...visibleSummary.unprotectedOrderIds,
  ]);
  const protectedOrderIds = new Set(isPlanningReset ? [] : visibleSummary.protectedOrderIds);
  (!isPlanningReset && Array.isArray(additionalProtectedOrderIds) ? additionalProtectedOrderIds : [])
    .map((orderId) => normalizeOrderId(orderId))
    .filter((orderId) => orderId && visibleOrderIds.has(orderId))
    .forEach((orderId) => protectedOrderIds.add(orderId));
  const stationaryOrderIds = new Set([...retainedOrderIds, ...protectedOrderIds, ...excludedOrderIds]);
  const unprotectedOrderIds = (isPlanningReset ? Array.from(visibleOrderIds) : visibleSummary.unprotectedOrderIds).filter(
    (orderId) => !stationaryOrderIds.has(orderId),
  );
  const eligibleOrderIds = new Set(unprotectedOrderIds);

  return {
    excludedOrderIds: Array.from(excludedOrderIds),
    excludedTruckIds: Array.from(excludedTruckIds),
    initialCount: unprotectedOrderIds.length,
    initialUnprotectedCount: unprotectedOrderIds.length,
    protectedOrderIds: Array.from(protectedOrderIds),
    retainedOrderIds,
    revealedOrderIds: (Array.isArray(revealedOrderIds) ? revealedOrderIds : [])
      .map((orderId) => normalizeOrderId(orderId))
      .filter((orderId) => orderId && eligibleOrderIds.has(orderId)),
    stationaryCount: Array.from(stationaryOrderIds).filter((orderId) => visibleOrderIds.has(orderId)).length,
    stationaryOrderIds: Array.from(stationaryOrderIds),
    unprotectedOrderIds,
  };
}

const FLEET_OPTIMIZATION_EXCLUSION_REASON_LABELS = {
  dispatch_disabled: "Désactivé pour ce dispatch",
  frozen: "Commande gelée",
  optimization_excluded: "Exclu de l'optimisation",
  protected_product_mix: "Mélange de produits protégé",
  protected_compartment_sharing: "Partage de compartiment protégé",
  reordered: "Réordonnancement manuel",
  truck_incident: "Bris mécanique / Contretemps",
  truck_protected: "Camion protégé",
};

const FLEET_OPTIMIZATION_EXCLUSION_REASON_ORDER = [
  "truck_protected",
  "protected_product_mix",
  "protected_compartment_sharing",
  "dispatch_disabled",
  "truck_incident",
  "frozen",
  "reordered",
  "optimization_excluded",
];

function payloadItemIsDispatcherFrozen(item) {
  if (!item || typeof item !== "object") {
    return false;
  }

  const executionStatus = normalizeInlineText(
    item.dispatcher_execution_status || item.execution_status,
  ).toLowerCase();

  return executionStatus === "frozen" || Boolean(item.is_dispatcher_frozen);
}

function assignmentContainsFrozenDispatcherItem(assignment) {
  for (const collectionKey of ["orders", "delivery_items"]) {
    const items = assignment?.[collectionKey];
    if (!Array.isArray(items)) {
      continue;
    }

    for (const item of items) {
      if (payloadItemIsDispatcherFrozen(item)) {
        return true;
      }
    }
  }

  return false;
}

function assignmentContainsManuallyReorderedItems(assignment) {
  for (const collectionKey of ["orders", "delivery_items"]) {
    const items = assignment?.[collectionKey];
    if (!Array.isArray(items)) {
      continue;
    }

    for (const item of items) {
      if (itemHasManualReorderProtection(item)) {
        return true;
      }
    }
  }

  return false;
}

function formatFleetOptimizationExclusionReasons(reasons = []) {
  const reasonSet = new Set(Array.isArray(reasons) ? reasons : []);

  return FLEET_OPTIMIZATION_EXCLUSION_REASON_ORDER
    .filter((reason) => reasonSet.has(reason))
    .map((reason) => FLEET_OPTIMIZATION_EXCLUSION_REASON_LABELS[reason] || "")
    .filter(Boolean)
    .join(" · ");
}

export function buildExcludedTruckRefreshByTruckId(resultsData = {}) {
  const items = Array.isArray(resultsData?.excluded_truck_refresh?.items)
    ? resultsData.excluded_truck_refresh.items
    : [];
  const refreshByTruckId = new Map();

  items.forEach((item) => {
    const truckId = normalizeInlineText(item?.truck_id);
    if (!truckId) {
      return;
    }
    refreshByTruckId.set(truckId, {
      actionCode: normalizeInlineText(item?.action_code || item?.actionCode),
      impactedLocations: normalizeExcludedTruckRefreshImpactedLocations(item?.impacted_locations || item?.impactedLocations),
      reasonCode: normalizeInlineText(item?.reason_code || item?.reasonCode),
      reasonContext: item?.reason_context && typeof item.reason_context === "object" && !Array.isArray(item.reason_context)
        ? item.reason_context
        : (item?.reasonContext && typeof item.reasonContext === "object" && !Array.isArray(item.reasonContext) ? item.reasonContext : null),
      reasonDetail: normalizeInlineText(item?.reason_detail || item?.reasonDetail),
      reasonLabel: normalizeInlineText(item?.reason_label || item?.reasonLabel),
      reasonStage: normalizeInlineText(item?.reason_stage || item?.reasonStage),
      // Conseil du serveur, posé seulement quand « Optimiser ce camion » refuse le camion (mélange, partage).
      recommendedAction: normalizeInlineText(item?.recommended_action || item?.recommendedAction),
      refreshLabel: normalizeInlineText(item?.label),
      refreshStatus: normalizeInlineText(item?.status),
      tables: normalizeInlineTextList(item?.tables),
      truckId,
      truckName: normalizeInlineText(item?.truck_name || item?.truckName),
    });
  });

  return refreshByTruckId;
}

export function buildExcludedTruckRefreshErrorId(truckId = "") {
  const normalizedTruckId = normalizeInlineText(truckId);
  return normalizedTruckId ? `excluded-truck-refresh:${normalizedTruckId}` : "";
}

function normalizeRoutingIssueEntry(entry = {}) {
  const technicalDetails = entry?.technical_details && typeof entry.technical_details === "object"
    ? entry.technical_details
    : (entry?.technicalDetails && typeof entry.technicalDetails === "object" ? entry.technicalDetails : null);
  const haversineFallback = technicalDetails?.haversine_fallback && typeof technicalDetails.haversine_fallback === "object"
    ? technicalDetails.haversine_fallback
    : (technicalDetails?.haversineFallback && typeof technicalDetails.haversineFallback === "object" ? technicalDetails.haversineFallback : null);

  return {
    cause: normalizeInlineText(entry?.cause),
    errorId: normalizeInlineText(entry?.error_id || entry?.errorId),
    haversineFallback,
    isHaversine: Boolean(haversineFallback)
      || /haversine/i.test(`${normalizeInlineText(entry?.title)} ${normalizeInlineText(entry?.cause)}`),
    recommendedAction: normalizeInlineText(entry?.recommended_action || entry?.recommendedAction),
    severity: normalizeInlineText(entry?.severity),
    technicalDetails,
    title: normalizeInlineText(entry?.title),
    tripId: normalizeInlineText(entry?.trip_id || entry?.tripId),
    truckId: normalizeInlineText(entry?.truck_id || entry?.truckId),
    truckName: normalizeInlineText(entry?.truck_name || entry?.truckName),
  };
}

export function buildRoutingIssueByTruckId(resultsData = {}) {
  const entries = Array.isArray(resultsData?.error_entries) ? resultsData.error_entries : [];
  const issueByTruckId = new Map();

  entries.forEach((entry) => {
    const filterKey = normalizeInlineText(entry?.filter_key || entry?.filterKey).toLowerCase();
    if (filterKey !== "routing_issues") {
      return;
    }

    const issue = normalizeRoutingIssueEntry(entry);
    if (!issue.truckId || !issue.errorId) {
      return;
    }

    const currentIssue = issueByTruckId.get(issue.truckId);
    if (!currentIssue || (!currentIssue.isHaversine && issue.isHaversine)) {
      issueByTruckId.set(issue.truckId, issue);
    }
  });

  return issueByTruckId;
}

export function collectFleetOptimizationExcludedTrucks(assignments, resultsData = {}) {
  const sourceAssignments = Array.isArray(assignments) && assignments.length > 0
    ? assignments
    : (Array.isArray(resultsData?.assignments) ? resultsData.assignments : []);
  const truckMap = new Map();
  const refreshByTruckId = buildExcludedTruckRefreshByTruckId(resultsData);
  const incidentTruckIds = new Set(
    (Array.isArray(resultsData?.truck_incidents) ? resultsData.truck_incidents : [])
      .map((incident) => normalizeInlineText(incident?.truck_id || incident?.truckId))
      .filter(Boolean),
  );

  sourceAssignments.forEach((assignment) => {
    const truckId = normalizeInlineText(assignment?.truck_id);
    if (!truckId) {
      return;
    }

    const reasons = [];
    if (assignment?.optimization_exclusion_reasons?.includes("Mélange de produits protégé")) reasons.push("protected_product_mix");
    if (assignment?.optimization_exclusion_reasons?.includes("Partage de compartiment protégé")) reasons.push("protected_compartment_sharing");
    if (Boolean(assignment?.is_truck_protected)) {
      reasons.push("truck_protected");
    }
    if (Boolean(assignment?.is_dispatch_disabled)) {
      reasons.push("dispatch_disabled");
    }
    const assignmentIncidents = Array.isArray(assignment?.truck_incidents)
      ? assignment.truck_incidents.filter(Boolean)
      : [];
    if (
      incidentTruckIds.has(truckId)
      || assignmentIncidents.length > 0
      || Number(assignment?.truck_incident_count) > 0
    ) {
      reasons.push("truck_incident");
    }
    if (assignmentContainsFrozenDispatcherItem(assignment)) {
      reasons.push("frozen");
    }
    if (assignmentContainsManuallyReorderedItems(assignment)) {
      reasons.push("reordered");
    }
    if (!reasons.length && Boolean(assignment?.is_excluded_from_optimization) && !Boolean(assignment?.is_manual_mix)) {
      reasons.push("optimization_excluded");
    }

    if (!reasons.length) {
      return;
    }

    const truckLabel = normalizeInlineText(assignment?.truck_name) || truckId;
    const existingEntry = truckMap.get(truckId);
    if (existingEntry) {
      reasons.forEach((reason) => existingEntry.reasons.add(reason));
      existingEntry.refresh = existingEntry.refresh || refreshByTruckId.get(truckId) || null;
      return;
    }

    truckMap.set(truckId, {
      reasonLabel: "",
      reasons: new Set(reasons),
      refresh: refreshByTruckId.get(truckId) || null,
      truckId,
      truckLabel,
    });
  });

  return Array.from(truckMap.values())
    .map((entry) => ({
      truckId: entry.truckId,
      truckLabel: entry.truckLabel,
      reasons: Array.from(entry.reasons),
      reasonLabel: formatFleetOptimizationExclusionReasons(Array.from(entry.reasons)),
      refreshStatus: entry.refresh?.refreshStatus || "",
      refreshLabel: entry.refresh?.refreshLabel || "",
      refreshReasonLabel: entry.refresh?.reasonLabel || "",
      refreshReasonDetail: entry.refresh?.reasonDetail || "",
      refreshTables: entry.refresh?.tables || [],
    }))
    .sort((leftTruck, rightTruck) => (
      leftTruck.truckLabel.localeCompare(rightTruck.truckLabel, "fr", { sensitivity: "base" })
    ));
}

function buildDispatchOrdersHoverCardDetails({
  assignments,
  confirmedCount = 0,
  newOrderIds = [],
  nonRenderableOrderCount = 0,
  orderTotalCount = 0,
  pendingCount = 0,
  resultsData,
  suspendedCount = 0,
  visibleOrderCount = 0,
  visibleOrderSummary = null,
}) {
  const resolvedVisibleOrderSummary = visibleOrderSummary && typeof visibleOrderSummary === "object"
    ? visibleOrderSummary
    : collectVisibleOrderProtectionSummary(assignments, resultsData);
  const hasOrders = orderTotalCount > 0;
  const resolvedVisibleOrderCount = Number.isFinite(Number(visibleOrderCount))
    ? Math.max(0, Math.round(Number(visibleOrderCount)))
    : resolvedVisibleOrderSummary.totalVisibleOrders;
  const resolvedSuspendedCount = Number.isFinite(Number(suspendedCount))
    ? Math.max(0, Math.round(Number(suspendedCount)))
    : 0;
  const resolvedNewOrderIds = [...new Set(
    (Array.isArray(newOrderIds) ? newOrderIds : [])
      .map((orderId) => (typeof orderId === "string" ? orderId.trim() : ""))
      .filter(Boolean),
  )];
  const newOrderCount = resolvedNewOrderIds.length;
  const hasMissingNewOrders = newOrderCount > 0;
  const resolvedNonRenderableOrderCount = Number.isFinite(Number(nonRenderableOrderCount))
    ? Math.max(0, Math.round(Number(nonRenderableOrderCount)))
    : 0;
  const hasTripToReview = resolvedNonRenderableOrderCount > 0;
  const groups = [];

  if (hasMissingNewOrders) {
    groups.push({
      countLabel: String(newOrderCount),
      emptyLabel: "Aucune nouvelle commande",
      key: "new-source-orders",
      orderIds: resolvedNewOrderIds,
      title: "Nouvelles",
      tone: "danger",
    });
  }

  groups.push({
    countLabel: String(confirmedCount),
    emptyLabel: "Aucune commande protégée",
    key: "protected",
    orderIds: resolvedVisibleOrderSummary.protectedOrderIds,
    title: "Protégées",
    tone: "success",
  });

  return {
    groups,
    note: hasTripToReview
      ? `${resolvedNonRenderableOrderCount} commande${resolvedNonRenderableOrderCount > 1 ? "s sont" : " est"} affectée${resolvedNonRenderableOrderCount > 1 ? "s" : ""}, mais les étapes nécessaires à sa tournée sont incomplètes. Ouvrez les Alertes pour voir le camion, les commandes et le plan de correction.`
      : hasMissingNewOrders
      ? `Le total (visibles + suspendues) doit correspondre au Data Manager. ${newOrderCount} nouvelle${newOrderCount > 1 ? "s" : ""} commande${newOrderCount > 1 ? "s" : ""} à importer via M-à-J commandes. Une réaffectation ou un réordonnancement manuel compte aussi comme protection pour Optimiser.`
      : (hasOrders
        ? "Le total (visibles + suspendues) doit correspondre au Data Manager. Aucune nouvelle commande en attente dans M-à-J commandes. Une réaffectation ou un réordonnancement manuel compte aussi comme protection pour Optimiser."
        : "Aucune commande dans ce planning."),
    summaryLines: [
      {
        label: "Commandes",
        value: String(orderTotalCount),
      },
      {
        label: "Visibles",
        value: String(resolvedVisibleOrderCount),
      },
      {
        label: "Suspendues",
        value: String(resolvedSuspendedCount),
      },
      {
        label: "Nouvelles (Data Manager)",
        value: String(newOrderCount),
      },
      {
        label: "Protégées",
        value: String(confirmedCount),
      },
      {
        label: "Non protégées",
        value: String(pendingCount),
      },
      ...(hasTripToReview ? [{
        label: "À vérifier",
        value: String(resolvedNonRenderableOrderCount),
      }] : []),
    ],
    total: String(orderTotalCount),
    totalLabel: "Commandes",
    type: "order-groups",
  };
}

function buildResultsPlanningPills(planningSummary, { assignments = [], resultsData = {} } = {}) {
  const summary = planningSummary && typeof planningSummary === "object" ? planningSummary : {};
  const fallbackConfirmedCount = getSafeResultsNumber(summary, "confirmed_count");
  const fallbackPendingCount = getSafeResultsNumber(summary, "pending_count");
  const fallbackOrderTotalCount = getSafeResultsNumber(summary, "order_total_count") || (fallbackConfirmedCount + fallbackPendingCount);
  const visibleOrderSummary = collectVisibleOrderProtectionSummary(assignments, resultsData);
  const hasVisibleOrderSummary = visibleOrderSummary.totalVisibleOrders > 0;
  const confirmedCount = hasVisibleOrderSummary
    ? visibleOrderSummary.protectedOrderIds.length
    : fallbackConfirmedCount;
  const visibleOrderCount = hasVisibleOrderSummary
    ? visibleOrderSummary.totalVisibleOrders
    : fallbackOrderTotalCount;
  const { suspendedCount } = resolveLiveSuspendedDeliveryStats(resultsData, summary);
  const orderTotalCount = visibleOrderCount + suspendedCount;
  const approvalPendingCount = hasVisibleOrderSummary
    ? visibleOrderSummary.unprotectedOrderIds.length
    : Math.max(fallbackPendingCount, fallbackOrderTotalCount - fallbackConfirmedCount, 0);
  const nonRenderableOrderCount = resolveNonRenderableOrderCount(resultsData, assignments);
  const hasTripToReview = nonRenderableOrderCount > 0;

  return [
    {
      key: "confirmed-orders",
      metricKind: "planning-pill",
      label: `Commandes ${orderTotalCount}`,
      tone: hasTripToReview ? "warning" : "success",
      metricLabel: "Commandes",
      metricValue: String(orderTotalCount),
      hoverCardTitle: "Commandes",
      hoverCardDetails: buildDispatchOrdersHoverCardDetails({
        assignments,
        confirmedCount,
        nonRenderableOrderCount,
        orderTotalCount,
        pendingCount: approvalPendingCount,
        resultsData,
        suspendedCount,
        visibleOrderCount,
        visibleOrderSummary,
      }),
      hoverCardMaxWidth: 520,
      confirmedCount,
      pendingCount: approvalPendingCount,
      suspendedCount,
      visibleOrderCount,
      nonRenderableOrderCount,
      isInteractive: hasTripToReview,
      errorTrigger: hasTripToReview,
      errorViewName: hasTripToReview ? "errors" : "",
      errorFilter: hasTripToReview ? "non_renderable_trip" : "",
      errorSource: hasTripToReview ? "results_overview" : "",
      ariaLabel: hasTripToReview ? "Ouvrir les alertes des tournées à vérifier" : "",
      dataAttribute: "data-planning-confirmed-count",
    },
  ];
}

function getRouteDiagnostic(route) {
  if (!route || !route.diagnostic || typeof route.diagnostic !== "object") {
    return {};
  }

  return route.diagnostic;
}

function getRouteStatusMeta(route, diagnostic) {
  if (!route || !route.status) {
    return { label: "Routage indisponible", tone: "neutral" };
  }

  if (route.status === "success") {
    if (!route.geometry_available && diagnostic.error_type === "geometry_missing") {
      return { label: "Géométrie manquante", tone: "warning" };
    }

    return { label: "", tone: "success", isHidden: true };
  }

  const errorType = diagnostic.error_type;
  if (route.status === "error") {
    if (errorType === "address_invalid") {
      return { label: "Adresse non routable", tone: "warning" };
    }
    if (errorType === "coordinates_invalid") {
      return { label: "Coordonnées invalides", tone: "warning" };
    }
    if (errorType === "quota") {
      return { label: "Routage saturé", tone: "danger" };
    }
    if (errorType === "timeout") {
      return { label: "Routage trop lent", tone: "danger" };
    }
    if (errorType === "empty_response") {
      return { label: "Routage sans réponse", tone: "danger" };
    }
    if (errorType === "parse_error" || errorType === "backend_processing") {
      return { label: "Trajet illisible", tone: "danger" };
    }
    if (errorType === "service_unavailable") {
      return { label: "Service de routage indisponible", tone: "danger" };
    }

    return { label: "Erreur routage", tone: "danger" };
  }

  if (route.error_code === "missing_coordinates") {
    return { label: "Coordonnées manquantes", tone: "warning" };
  }

  if (route.error_code === "not_enough_waypoints") {
    return { label: "Points insuffisants", tone: "warning" };
  }

  return { label: "Routage non tenté", tone: "neutral" };
}

function getTripLoadSummary(trip) {
  if (!trip || !Array.isArray(trip.orders) || !trip.orders.length) {
    return null;
  }

  const seenGrains = new Set();
  const grainLabels = [];

  trip.orders.forEach((order) => {
    if (!order || typeof order !== "object") {
      return;
    }

    const grainLabel = typeof order.grain_type === "string" ? order.grain_type.trim() : "";
    if (!grainLabel) {
      return;
    }

    const normalizedKey = grainLabel.toLowerCase();
    if (seenGrains.has(normalizedKey)) {
      return;
    }

    seenGrains.add(normalizedKey);
    grainLabels.push(grainLabel);
  });

  return grainLabels.length ? grainLabels.join(", ") : null;
}

export function selectAlertBannerData(state) {
  const alertMessages = Array.isArray(state?.resultsData?.alert_messages)
    ? state.resultsData.alert_messages.filter((message) => typeof message === "string" && message.trim())
    : [];

  return {
    messages: alertMessages,
  };
}

export function selectDispatchRibbonData(state, {
  buildPlanningOptimizeConfirmationContent = null,
  includeSuspendedDeliveriesInOptimization = false,
} = {}) {
  const resultsData = state?.resultsData && typeof state.resultsData === "object" ? state.resultsData : {};
  const optimizationDiagnostics = resolveRunDiagnostics(resultsData);
  const currentAssignment = state?.currentAssignment && typeof state.currentAssignment === "object"
    ? state.currentAssignment
    : null;
  const exportContext = resultsData?.export_context && typeof resultsData.export_context === "object"
    ? resultsData.export_context
    : {};
  const planningSummary = state?.planningSummary && typeof state.planningSummary === "object" ? state.planningSummary : {};
  const assignments = Array.isArray(state?.assignmentList) ? state.assignmentList.filter(Boolean) : [];
  const truckLabelById = {};
  // Chauffeur affiché dans le Gantt de cette période ; "" = ligne sans chauffeur.
  const truckDriverById = {};
  const truckLabelAssignments = [
    ...assignments,
    ...(Array.isArray(resultsData?.assignments) ? resultsData.assignments.filter(Boolean) : []),
  ];
  for (const assignment of truckLabelAssignments) {
    const truckId = normalizeInlineText(assignment?.truck_id);
    const truckName = normalizeInlineText(assignment?.truck_name);
    if (truckId && !(truckId in truckDriverById)) {
      truckDriverById[truckId] = normalizeInlineText(assignment?.driver_name);
    }
    if (!truckId || !truckName || truckLabelById[truckId]) {
      continue;
    }
    truckLabelById[truckId] = truckName;
  }
  const { activeAssignments, idleAssignments } = splitAssignmentsByActivity(assignments);
  const totalOrders = getSafeResultsNumber(resultsData, "total_orders");
  const totalTrips = getSafeResultsNumber(resultsData, "total_trips");
  const technicalPrecheckCount = getSafeResultsNumber(resultsData, "technical_precheck_failed_count");
  const businessNonAssignableCount = getSafeResultsNumber(resultsData, "business_non_assignable_count");
  const renderabilityAssignments = assignments.length > 0
    ? assignments
    : (Array.isArray(resultsData?.assignments) ? resultsData.assignments.filter(Boolean) : []);
  const nonRenderableOrderCount = resolveNonRenderableOrderCount(resultsData, renderabilityAssignments);
  const orderIssueCount = technicalPrecheckCount + businessNonAssignableCount + nonRenderableOrderCount;
  const liveActiveTruckCount = getSafeResultsNumber(resultsData, "active_truck_count");
  const trackedTrucks = activeAssignments.length;
  const idleTruckCount = idleAssignments.length;
  const manualCount = getSafeResultsNumber(planningSummary, "manual_count");
  const {
    suspendedCount,
    suspendedQuantityTons,
  } = resolveLiveSuspendedDeliveryStats(resultsData, planningSummary);
  const {
    routingAddressCount,
    routingServiceIssueCount,
    tone: validationTone,
  } = resolveServiceValidationMeta(resultsData);

  const metrics = [
    buildAddressRoutingMetric(resultsData),
    {
      key: "non-assignable-orders",
      label: businessNonAssignableCount > 0 ? "Non assignables" : "Non assign.",
      value: String(businessNonAssignableCount),
      hoverCardTitle: "Non assignables",
      hoverCardMaxWidth: 420,
      hoverCardDetails: buildBusinessNonAssignableHoverCardDetails(resultsData, businessNonAssignableCount),
      isInteractive: businessNonAssignableCount > 0,
      tone: businessNonAssignableCount > 0 ? "danger" : "neutral",
      emphasis: businessNonAssignableCount > 0 ? "blocking" : "",
      errorTrigger: businessNonAssignableCount > 0,
      errorViewName: "errors",
      errorFilter: businessNonAssignableCount > 0 ? "non_assignable_orders" : "",
      errorSource: businessNonAssignableCount > 0 ? "results_overview" : "",
      ariaControls: "errors-center",
      ariaExpanded: "false",
      ariaLabel: "Ouvrir les alertes Non assignables",
    },
    {
      key: "suspended-deliveries",
      label: "Suspendues",
      value: String(suspendedCount),
      hoverCardTitle: "Suspendues",
      hoverCardDetails: {
        type: "formula",
        lines: [
          {
            label: "Commandes suspendues",
            value: String(suspendedCount),
          },
          {
            label: "Tonnage suspendu",
            value: `${formatTons(suspendedQuantityTons)} t`,
          },
        ],
        total: String(suspendedCount),
        totalLabel: "Commandes à part",
        note: suspendedCount > 0
          ? "Compte les commandes mises de côté avant ou après optimisation. Tant qu'elles restent suspendues, elles sont exclues des routes actives et ne sont pas proposées au solveur."
          : "Compte les commandes mises de côté avant ou après optimisation. Aucune commande visible n'est actuellement suspendue.",
      },
      isInteractive: true,
      tone: suspendedCount > 0 ? "warning" : "neutral",
      detailViewLink: "suspended",
      detailForceOpen: true,
      ariaControls: "suspended-center",
      ariaExpanded: "false",
      valueDataAttribute: "data-suspended-metric-count",
      noteDataAttribute: "data-suspended-metric-note",
    },
    {
      key: "manual-count",
      label: "Réaffectées",
      value: String(manualCount),
      note: manualCount > 0 ? "Verrous manuels actifs" : "Aucune réaffectation",
      tone: manualCount > 0 ? "warning" : "neutral",
      valueDataAttribute: "data-planning-manual-count",
    },
    {
      key: "total-trips",
      label: "Tournées",
      value: String(totalTrips),
      note: "Missions affichables",
    },
  ];

  const planningPills = buildResultsPlanningPills(planningSummary, { assignments, resultsData });
  const dispatchItemSort = (leftItem, rightItem) => {
    const leftIndex = DISPATCH_METRIC_DISPLAY_ORDER.indexOf(leftItem?.key);
    const rightIndex = DISPATCH_METRIC_DISPLAY_ORDER.indexOf(rightItem?.key);
    const safeLeftIndex = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
    const safeRightIndex = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;

    return safeLeftIndex - safeRightIndex;
  };
  const orderItems = [
    ...metrics.filter((metric) => (
      metric.key === "assignment-progress"
      || metric.key === "non-assignable-orders"
      || metric.key === "suspended-deliveries"
    )),
    ...planningPills,
  ].sort(dispatchItemSort);
  const totalTrackedFleet = trackedTrucks + idleTruckCount;
  const truckPreloadTimeOverrideCount = countTruckPreloadTimeOverrides(assignments);
  const pendingGlobalPlanningMutationCount = Number.isFinite(Number(state?.pendingGlobalPlanningMutationCount))
    ? Math.max(0, Math.round(Number(state.pendingGlobalPlanningMutationCount)))
    : 0;
  const pendingPlanningMutationCount = [
    state?.pendingMutationByOrderId,
    state?.pendingDeliveryMutationById,
    state?.pendingLoadingMutationById,
  ].reduce(
    (count, pendingSet) => count + (pendingSet instanceof Set ? pendingSet.size : 0),
    pendingGlobalPlanningMutationCount + (getPlanningMutationActivityMessage() ? 1 : 0),
  );
  const preflightTrackedFleetCount = totalTrackedFleet > 0 ? totalTrackedFleet : liveActiveTruckCount;
  const optimizationLaunchPreflight = {
    pendingPlanningMutationCount,
    routingAddressCount,
    ...(suspendedCount > 0 ? { suspendedDeliveryCount: suspendedCount } : {}),
    technicalPrecheckCount,
    totalOrders,
    totalTrackedFleet: preflightTrackedFleetCount,
  };
  const optimizationExcludedTrucks = collectFleetOptimizationExcludedTrucks(assignments, resultsData);
  const snapshotStatus = typeof exportContext?.snapshot_status === "string" && exportContext.snapshot_status.trim() === "stale"
    ? "stale"
    : "ready";
  const truckSheetPdfExportRevision = "report_v42";
  const snapshotGeneratedAt = typeof exportContext?.generated_at === "string" ? exportContext.generated_at.trim() : "";
  const planningUpdatedAt = typeof planningSummary?.updated_at === "string" ? planningSummary.updated_at.trim() : "";
  const sourceStaleNotice = buildDispatchSourceStaleNotice(resultsData, { planningUpdatedAt });
  const allowStaleSnapshotActions = sourceStaleNotice !== null;
  const shouldBlockStaleSnapshotActions = snapshotStatus === "stale" && !allowStaleSnapshotActions;
  const invalidTourExportBlockedReason = nonRenderableOrderCount > 0
    ? `Tournée à vérifier : ${nonRenderableOrderCount} commande${nonRenderableOrderCount > 1 ? "s" : ""} affectée${nonRenderableOrderCount > 1 ? "s" : ""} dont les étapes sont incomplètes. Relancez l'optimisation et vérifiez la durée avant d'exporter.`
    : "";
  const resultsExportBlockedReason = invalidTourExportBlockedReason || (shouldBlockStaleSnapshotActions
    ? "Le snapshot d'optimisation est obsolète. Relancez l'optimisation avant d'exporter les résultats."
    : "");
  const truckSheetPdfBlockedReason = invalidTourExportBlockedReason || (shouldBlockStaleSnapshotActions
    ? "Dispatch à revalider : relancez « Optimiser » avant d'imprimer l'horaire."
    : "");
  const exportFingerprint = `${snapshotGeneratedAt}::${planningUpdatedAt}`;
  const selectedTruckId = typeof currentAssignment?.truck_id === "string" ? currentAssignment.truck_id.trim() : "";
  const selectedTruckLabel = typeof currentAssignment?.truck_name === "string" && currentAssignment.truck_name.trim()
    ? currentAssignment.truck_name.trim()
    : selectedTruckId || "Camion sélectionné";
  const dispatchDays = Array.isArray(resultsData?.dispatch_days) ? resultsData.dispatch_days.filter(Boolean) : [];
  const activeDispatchDate = typeof resultsData?.dispatch_date === "string" && resultsData.dispatch_date.trim()
    ? resultsData.dispatch_date.trim()
    : typeof planningSummary?.dispatch_date === "string" && planningSummary.dispatch_date.trim()
      ? planningSummary.dispatch_date.trim()
      : "";
  const activeDispatchMonth = typeof resultsData?.ui_dispatch_month === "string" && resultsData.ui_dispatch_month.trim()
    ? resultsData.ui_dispatch_month.trim()
    : "";
  const showDispatchArchive = resultsData?.ui_dispatch_archive_open === true;
  const dispatchDayControls = buildDispatchDayControlsModel({
    activeDispatchDate,
    activeDispatchMonth,
    activeDispatchState: planningSummary?.dispatch_day_state || "open",
    dispatchDays,
    planningSummary,
    selectedLabel: activeDispatchDate || "Journée active",
    showArchive: showDispatchArchive,
  });

  return {
    allowStaleSnapshotActions,
    dispatchDayControls,
    // Version du Dispatch affiché : le contrôle Geotab avant clôture est relu à chaque version.
    runtimeRevision: normalizeInlineText(resultsData?.runtime_revision),
    planningResetProtection: planningSummary?.planning_reset_protection || {},
    metrics,
    nonRenderableOrderCount,
    pendingPlanningMutationCount,
    truckCards: [],
    truckDriverById,
    truckLabelById,
    truckPreloadTimeOverrideCount,
    planningPills,
    optimizationMode: resolveOptimizationModeFromDiagnostics(optimizationDiagnostics),
    optimizationModeAvailability: {
      canUseFastMode: resultsData?.can_use_fast_optimization_mode === true,
      fastModeEnabled: resultsData?.fast_optimization_mode_enabled !== false,
    },
    optimizationLaunchPreflight,
    optimizationExcludedTrucks,
    includeSuspendedDeliveriesInOptimization: includeSuspendedDeliveriesInOptimization === true,
    resultsExport: {
      blocked: Boolean(resultsExportBlockedReason),
      blockedReason: resultsExportBlockedReason,
      fingerprint: exportFingerprint,
      snapshotGeneratedAt,
      snapshotStatus,
    },
    sourceStaleNotice,
    truckSheetPdf: {
      blocked: Boolean(truckSheetPdfBlockedReason),
      blockedReason: truckSheetPdfBlockedReason,
      fleetFingerprint: `${exportFingerprint}::truck_sheet_pdf::${truckSheetPdfExportRevision}::combined::all`,
      fleetTruckCount: trackedTrucks,
      selectedFingerprint: selectedTruckId
        ? `${exportFingerprint}::truck_sheet_pdf::${truckSheetPdfExportRevision}::single::${selectedTruckId}`
        : "",
      selectedTruckId,
      selectedTruckLabel,
      snapshotGeneratedAt,
      snapshotStatus,
    },
    sections: [
      {
        key: "orders",
        label: "Commandes",
        summary: `${totalOrders} visibles`,
        note: "",
        tone: validationTone !== "neutral" ? validationTone : orderIssueCount > 0 ? "warning" : "neutral",
        items: orderItems,
        truckCards: [],
      },
    ],
    optimizeConfirmation: typeof buildPlanningOptimizeConfirmationContent === "function"
      ? buildPlanningOptimizeConfirmationContent(state?.planningSummary, {
        assignments,
        includeSuspendedDeliveries: includeSuspendedDeliveriesInOptimization === true,
        resultsData,
      })
      : null,
  };
}

export function selectRouteHeaderData(assignment, trip) {
  const route = trip ? trip.route || {} : null;
  const diagnostic = getRouteDiagnostic(route);
  const statusMeta = getRouteStatusMeta(route, diagnostic);

  return {
    assignmentName: assignment ? (assignment.truck_name || assignment.truck_id || "Camion à sélectionner") : "Camion à sélectionner",
    statusHidden: Boolean(statusMeta.isHidden),
    tripLoad: getTripLoadSummary(trip) || "Aucun chargement",
    truckCapacity: assignment && typeof assignment.capacity_tons === "number" ? `${formatTons(assignment.capacity_tons)} t` : "Non disponible",
    statusTone: statusMeta.tone,
    statusLabel: statusMeta.label,
    tripId: trip ? trip.trip_id : "Aucune tournée",
  };
}

function formatObservabilityPercentValue(value, fallback = "\u2014") {
  const numericValue = getFirstFiniteNumber(value);
  if (numericValue === null) {
    return fallback;
  }

  return `${Math.round(numericValue * 10) / 10} %`;
}

function getObservabilityMetricTone(value, { warningThreshold = null } = {}) {
  const numericValue = getFirstFiniteNumber(value);
  if (numericValue === null) {
    return "neutral";
  }

  if (warningThreshold !== null && numericValue >= warningThreshold) {
    return "warning";
  }

  return "accent";
}

function buildAssignmentObservabilityItems(assignment, trip) {
  if (!assignment || typeof assignment !== "object") {
    return [];
  }

  const peakLoadFillRatePct = getFirstFiniteNumber(assignment?.peak_load_fill_rate_pct);
  const compartmentFillRatePct = getFirstFiniteNumber(assignment?.compartment_fill_rate_pct);
  const timeBudgetConsumedPct = getFirstFiniteNumber(assignment?.time_budget_consumed_pct);
  const peakLoadLabel = typeof trip?.peak_load_tons === "number"
    ? `${formatTons(trip.peak_load_tons)} t`
    : typeof assignment?.peak_trip_load_tons === "number"
      ? `${formatTons(assignment.peak_trip_load_tons)} t`
      : null;
  const capacityLabel = typeof assignment?.capacity_tons === "number"
    ? `${formatTons(assignment.capacity_tons)} t`
    : null;
  const usedCompartments = Number.isFinite(Number(assignment?.compartments_used))
    ? String(Math.max(Number(assignment.compartments_used), 0))
    : null;
  const totalCompartments = Number.isFinite(Number(assignment?.total_compartments))
    ? String(Math.max(Number(assignment.total_compartments), 0))
    : null;
  const maxWorkHoursLabel = Number.isFinite(Number(assignment?.max_work_hours)) && Number(assignment.max_work_hours) > 0
    ? `${Math.round(Number(assignment.max_work_hours) * 10) / 10} h`
    : null;
  const consumedDurationLabel = assignment?.total_actual_duration_label
    || assignment?.total_estimated_duration_label
    || trip?.route?.actual_duration_label
    || trip?.route?.estimated_duration_label
    || null;

  return [
    {
      key: "peak-load-fill-rate",
      label: "Rempl. pic",
      note: peakLoadFillRatePct === null ? "Non disponible" : "Charge / capacité",
      tone: getObservabilityMetricTone(peakLoadFillRatePct),
      value: formatObservabilityPercentValue(peakLoadFillRatePct),
      title: "Remplissage au pic de charge",
      hoverCardDetails: {
        type: "formula",
        lines: [
          {
            label: "Pic charge",
            value: peakLoadLabel || "-",
          },
          {
            label: "Capacité",
            value: capacityLabel || "-",
          },
        ],
        note: "Ratio déjà calculé côté backend entre le pic de charge porté sur la tournée et la capacité utile.",
        total: formatObservabilityPercentValue(peakLoadFillRatePct, null),
        totalLabel: "Taux au pic",
      },
    },
    {
      key: "compartment-fill-rate",
      label: "Comp. util.",
      note: compartmentFillRatePct === null ? "Non disponible" : "Compartiments occupés",
      tone: getObservabilityMetricTone(compartmentFillRatePct),
      value: formatObservabilityPercentValue(compartmentFillRatePct),
      title: "Utilisation des compartiments",
      hoverCardDetails: {
        type: "formula",
        lines: [
          {
            label: "Occupés",
            value: usedCompartments || "-",
          },
          {
            label: "Déclarés",
            value: totalCompartments || "-",
          },
        ],
        note: "Ratio déjà calculé côté backend entre les compartiments utilisés et le nombre total de compartiments déclarés.",
        total: formatObservabilityPercentValue(compartmentFillRatePct, null),
        totalLabel: "Taux d'usage",
      },
    },
    {
      key: "time-budget-consumed",
      label: "Budget temps",
      note: timeBudgetConsumedPct === null ? "Non disponible" : "Temps consommé",
      tone: getObservabilityMetricTone(timeBudgetConsumedPct, { warningThreshold: 90 }),
      value: formatObservabilityPercentValue(timeBudgetConsumedPct),
      title: "Budget temps consommé",
      hoverCardDetails: {
        type: "formula",
        lines: [
          {
            label: "Durée visible",
            value: consumedDurationLabel || "-",
          },
          {
            label: "Heures max chauffeur",
            value: maxWorkHoursLabel || "-",
          },
        ],
        note: "Taux déjà calculé côté backend à partir du temps consommé sur la tournée et des heures max du chauffeur.",
        total: formatObservabilityPercentValue(timeBudgetConsumedPct, null),
        totalLabel: "Budget consommé",
      },
    },
  ];
}

export function selectKpiGridData(assignmentOrTrip, tripArg, routeSequenceArg = null) {
  const assignment = tripArg === undefined ? null : assignmentOrTrip;
  const trip = tripArg === undefined ? assignmentOrTrip : tripArg;
  const routeSequence = routeSequenceArg && typeof routeSequenceArg === "object" ? routeSequenceArg : null;
  const stopSummary = resolveDisplayedStopSummary(trip, routeSequence);
  const route = trip ? trip.route || {} : null;
  const displayedTotalDurationLabel = typeof routeSequence?.displayedTotalDurationLabel === "string"
    ? routeSequence.displayedTotalDurationLabel.trim()
    : "";
  const totalEstimatedDurationLabel = displayedTotalDurationLabel || (route?.estimated_duration_label || "");
  const estimatedDistanceLabel = route?.estimated_distance_label || trip?.total_estimated_distance_label || assignment?.total_estimated_distance_label || null;
  const estimatedDistanceSource = resolveEstimatedRouteDistanceSource(route);
  const estimatedDistanceSourceLabel = getRouteDistanceSourceLabel(estimatedDistanceSource);
  const estimatedDistanceHoverSourceLabel = getRouteDistanceHoverSourceLabel(estimatedDistanceSource, estimatedDistanceSourceLabel);
  const distanceKpiMeta = buildDistanceKpiMeta(
    estimatedDistanceSource,
    Boolean(estimatedDistanceLabel),
    estimatedDistanceSourceLabel,
  );
  const averageSpeedKmh = getFirstFiniteNumber(
    assignment?.avg_speed_kmh,
    assignment?.average_speed_kmh,
    assignment?.truck_raw_data?.Vitesse_moy,
  );
  const loadingRateTonsPerHour = getFirstFiniteNumber(
    assignment?.loading_rate_tons_per_hour,
    assignment?.loading_time_minutes,
    assignment?.truck_raw_data?.Temps_Chargement,
  );
  const unloadingRateTonsPerHour = getFirstFiniteNumber(
    assignment?.unloading_rate_tons_per_hour,
    assignment?.unloading_time_minutes,
    assignment?.truck_raw_data?.Temps_Dechargement,
  );
  const loadingCount = stopSummary.hasOperationalData
    ? stopSummary.loadingCount
    : (Number.isFinite(Number(trip?.loading_count)) ? Number(trip.loading_count) : 0);
  const deliveryCount = stopSummary.hasOperationalData
    ? stopSummary.deliveryCount
    : (Number.isFinite(Number(trip?.delivery_count)) ? Number(trip.delivery_count) : 0);
  const loadingServiceMinutes = sumTripStopServiceMinutesByType(trip, "loading");
  const unloadingServiceMinutes = sumTripStopServiceMinutesByType(trip, "delivery");
  const loadingBreakdownLabel = loadingCount <= 0
    ? "Inclus"
    : loadingServiceMinutes > 0
      ? `${formatDurationMinutesLabel(loadingServiceMinutes) || `${loadingServiceMinutes} min`} (${loadingCount} chargement${loadingCount > 1 ? "s" : ""}${loadingRateTonsPerHour !== null ? `, débit ${formatServiceRateLabel(loadingRateTonsPerHour)} t/h` : ""}${Number(assignment?.loading_fixed_minutes) > 0 ? ` + ${Math.round(Number(assignment.loading_fixed_minutes))} min fixes par visite` : ""})`
      : `Inclus (${loadingCount} chargement${loadingCount > 1 ? "s" : ""})`;
  const unloadingBreakdownLabel = deliveryCount <= 0
    ? "Inclus"
    : unloadingServiceMinutes > 0
      ? `${formatDurationMinutesLabel(unloadingServiceMinutes) || `${unloadingServiceMinutes} min`} (${deliveryCount} déchargement${deliveryCount > 1 ? "s" : ""}${unloadingRateTonsPerHour !== null ? `, débit ${formatServiceRateLabel(unloadingRateTonsPerHour)} t/h` : ""}${Number(assignment?.delivery_fixed_minutes) > 0 ? ` + ${Math.round(Number(assignment.delivery_fixed_minutes))} min fixes par visite` : ""})`
      : `Inclus (${deliveryCount} déchargement${deliveryCount > 1 ? "s" : ""})`;
  const estimatedCostNote = cleanBackendNote(route && route.estimated_cost_note
    ? route.estimated_cost_note
    : "Coût estimé calculé à partir des paramètres du camion sélectionné.");
  const estimatedDrivingDurationNote = cleanBackendNote(route && route.driving_duration_note
    ? route.driving_duration_note
    : "Durée estimée de conduite calculée à partir du trajet routier et de la vitesse moyenne du camion, sans chargements ni déchargements.");
  const estimatedTotalDurationNote = cleanBackendNote(route && route.estimated_duration_note
    ? route.estimated_duration_note
    : "Durée totale estimée: conduite + chargements + déchargements. À comparer au maximum d'heures du chauffeur.");
  const estimatedCostDetails = buildEstimatedCostDetails(assignment, trip);
  const averageSpeedLabel = averageSpeedKmh !== null
    ? `vitesse moyenne camion (${Math.round(averageSpeedKmh)} km/h), chargements et déchargements`
    : "vitesse moyenne camion, chargements et déchargements";
  const averageSpeedShortLabel = averageSpeedKmh !== null
    ? `${Math.round(averageSpeedKmh)} km/h`
    : "le paramètre vitesse camion";
  const estimatedDrivingDurationClarification = estimatedDistanceSource === "roadapi"
    ? `La distance de base vient de HERE en mode camion. Le ${averageSpeedShortLabel} n'est pas envoyé à HERE dans cette vue; il sert seulement à recalculer une durée estimée camion à partir de cette distance. Il n'y a pas deux diminutions successives: la durée HERE et l'estimation à ${averageSpeedShortLabel} sont deux valeurs parallèles.`
    : `La durée estimée utilise la distance de référence affichée et ${averageSpeedShortLabel}. Il n'y a pas deux diminutions successives: c'est la formule propre à cette métrique, pas un second ralentissement ajouté à un temps HERE.`;
  const estimatedTotalDurationClarification = estimatedDistanceSource === "roadapi"
    ? `La durée totale estimée additionne la conduite estimée camion et les temps de service. Quand HERE est disponible, sa distance nourrit la base de calcul, mais le ${averageSpeedShortLabel} ne s'ajoute pas à une durée HERE. Il n'y a pas deux diminutions successives.`
    : `La durée totale estimée additionne la conduite estimée camion et les temps de service à partir de la distance de référence affichée.`;
  const distanceClarification = estimatedDistanceSource === "roadapi"
    ? "Source explicite: HERE Routing en mode camion."
    : "La source affichée ci-dessous indique comment cette distance a été obtenue.";
  const observabilityItems = buildAssignmentObservabilityItems(assignment, trip);

  return {
    items: [
      {
        key: "peak-load",
        label: "Pic charge",
        value: trip && typeof trip.peak_load_tons === "number" ? `${formatTons(trip.peak_load_tons)} t` : "Non disponible",
        title: "Pic de charge",
        hoverCardDetails: {
          type: "formula",
          lines: [
            {
              label: "Pic de charge",
              value: trip && typeof trip.peak_load_tons === "number" ? `${formatTons(trip.peak_load_tons)} t` : "-",
            },
            assignment && typeof assignment.capacity_tons === "number"
              ? {
                  label: "Capacité",
                  value: `${formatTons(assignment.capacity_tons)} t`,
                }
              : null,
            {
              label: "Livraisons",
              value: trip ? String(trip.delivery_count || 0) : "-",
            },
          ].filter(Boolean),
          note: "Le pic de charge correspond à la charge la plus lourde portée par le camion à un moment de sa tournée. Dans l'interface, on considère qu'il est atteint à partir de 85 % de la capacité utile.",
          total: trip && typeof trip.peak_load_tons === "number" ? `${formatTons(trip.peak_load_tons)} t` : null,
          totalLabel: "Pic de charge",
        },
        valueDataAttribute: "data-route-target-peak-load",
      },
      {
        key: "delivered-quantity",
        label: "Total livré",
        value: trip ? `${formatTons(trip.total_quantity_tons)} t` : "Non disponible",
        title: "Total livré",
        hoverCardDetails: {
          type: "formula",
          lines: [
            {
              label: "Tonnage total",
              value: trip && typeof trip.total_quantity_tons === "number" ? `${formatTons(trip.total_quantity_tons)} t` : "-",
            },
            {
              label: "Livraisons",
              value: trip ? String(trip.delivery_count || 0) : "-",
            },
          ],
        },
        valueDataAttribute: "data-route-target-quantity",
      },
      {
        key: "distance",
        label: distanceKpiMeta.label,
        value: estimatedDistanceLabel || "Non disponible",
        title: distanceKpiMeta.title,
        hoverCardDetails: {
          type: estimatedDistanceLabel ? "formula" : "note",
          lines: estimatedDistanceLabel
            ? [
                {
                  label: "Distance",
                  value: estimatedDistanceLabel,
                },
                estimatedDistanceHoverSourceLabel
                  ? {
                      label: "Source",
                      value: estimatedDistanceHoverSourceLabel,
                    }
                  : null,
              ].filter(Boolean)
            : [],
          note: joinNoteSections(distanceClarification, distanceKpiMeta.note),
        },
        valueDataAttribute: "data-route-target-distance",
      },
      {
        key: "stop-count",
        label: "Arrêts",
        value: String(stopSummary.totalCount),
        title: "Nombre d'arrêts",
        hoverCardDetails: {
          type: "formula",
          lines: [
            {
              label: "Total sites",
              value: String(stopSummary.totalCount),
            },
            {
              label: "Sites chargement",
              value: String(stopSummary.loadingCount),
            },
            {
              label: "Sites livraison",
              value: String(stopSummary.deliveryCount),
            },
          ],
          note: stopSummary.note,
        },
        valueDataAttribute: "data-route-target-stop-count",
      },
      {
        key: "estimated-duration",
        label: "Durée estimée",
        value: route && route.driving_duration_label ? route.driving_duration_label : "Non disponible",
        title: "Durée estimée",
        hoverCardDetails: {
          type: "formula",
          lines: [
            {
              label: "Distance",
              value: estimatedDistanceLabel || "-",
            },
            estimatedDistanceHoverSourceLabel
              ? {
                  label: "Source dist.",
                  value: estimatedDistanceHoverSourceLabel,
                }
              : null,
            {
              label: "Vitesse moy.",
              operator: "÷",
              value: averageSpeedKmh !== null ? `${Math.round(averageSpeedKmh)} km/h` : "Paramètre camion",
            },
          ].filter(Boolean),
          note: joinNoteSections(estimatedDrivingDurationClarification, estimatedDrivingDurationNote),
          total: route?.driving_duration_label || null,
          totalLabel: "Durée estimée",
        },
        cardDataAttribute: "data-route-target-estimated-duration-card",
        valueDataAttribute: "data-route-target-estimated-duration",
        ariaLabel: estimatedDrivingDurationNote,
      },
      {
        key: "total-estimated-duration",
        label: "Durée totale estimée",
        value: totalEstimatedDurationLabel || "Non disponible",
        title: "Durée totale estimée",
        hoverCardDetails: {
          type: "formula",
          lines: [
            {
              label: "Conduite",
              value: route?.driving_duration_label || "-",
            },
            {
              label: "Chargements",
              operator: "+",
              value: loadingBreakdownLabel,
            },
            {
              label: "Déchargements",
              operator: "+",
              value: unloadingBreakdownLabel,
            },
          ],
          note: joinNoteSections(estimatedTotalDurationClarification, estimatedTotalDurationNote),
          total: totalEstimatedDurationLabel || null,
          totalLabel: "Durée totale estimée",
        },
        cardDataAttribute: "data-route-target-total-estimated-duration-card",
        valueDataAttribute: "data-route-target-total-estimated-duration",
        ariaLabel: estimatedTotalDurationNote,
      },
      {
        key: "estimated-cost",
        label: "Coût estimé",
        value: route && route.estimated_cost_label ? normalizeCurrencyDisplay(route.estimated_cost_label) : "Non disponible",
        isAccent: true,
        cardDataAttribute: "data-route-target-estimated-cost-card",
        valueDataAttribute: "data-route-target-estimated-cost",
        title: estimatedCostNote,
        ariaLabel: estimatedCostNote,
        hoverCardDetails: estimatedCostDetails
          ? {
              ...estimatedCostDetails,
              note: estimatedCostNote,
            }
          : {
              note: estimatedCostNote,
            },
      },
    ],
    observabilityItems,
  };
}

function normalizeSuspendedGroupMemberIds(item) {
  if (!Array.isArray(item?.suspended_group_member_ids)) {
    return [];
  }
  return item.suspended_group_member_ids
    .filter((deliveryId) => typeof deliveryId === "string" && deliveryId.trim())
    .map((deliveryId) => deliveryId.trim())
    .filter((deliveryId, index, items) => items.indexOf(deliveryId) === index);
}

function isSuspendedDeliveryActivationHighlighted(state, deliveryId) {
  const highlightKey = getDeliveryHighlightKey(deliveryId);
  return Boolean(highlightKey && state?.transientActivatedStepKeys?.has(highlightKey));
}

function buildSuspendedDeliveryPanelItem(state, item, index, {
  dispatchPeriod = "day",
  runtimeRevision = "",
} = {}) {
  const compatibilities = resolveSuspendedDeliveryCompatibilities(state, item);
  const loadingLocation = buildSuspendedLocationLine(item?.loading_client_name, item?.loading_address);
  const deliveryLocation = buildSuspendedLocationLine(
    item?.client_name || item?.delivery_client_name || item?.delivery_id || item?.order_id,
    item?.address,
  );
  const productLabel = normalizeInlineText(item?.product);
  const quantityTons = typeof item?.quantity_tons === "number" ? item.quantity_tons : null;
  const quantityLabel = quantityTons != null ? `${formatTons(quantityTons)} t` : "";
  const siloLabel = resolveSuspendedDeliverySiloLabel(state, item);
  const buildingLabel = resolveSuspendedDeliveryBuildingLabel(state, item);
  const workOrderLabel = resolveSuspendedDeliveryWorkOrderLabel(state, item);
  const isDompeur = resolveSuspendedDeliveryIsDompeur(state, item);
  const details = quantityLabel || "Tonnage indisponible";
  return {
    type: "delivery",
    key: item?.delivery_id || item?.order_id || `suspended-${index}`,
    title: deliveryLocation.name,
    subtitle: "1 commande",
    badgeLabel: item?.manual_status_label || "Suspendue",
    compatibilities,
    loadingAddress: loadingLocation.address,
    loadingName: loadingLocation.name,
    deliveryAddress: deliveryLocation.address,
    deliveryName: deliveryLocation.name,
    meta: details,
    orderLabel: item?.order_id || "",
    workOrderLabel,
    productLabel,
    quantityLabel,
    quantityTons,
    requiresAspirator: normalizeInlineText(item?.aspirator).toLowerCase() === "oui",
    isDompeur,
    requiresOntario: normalizeInlineText(item?.ontario).toLowerCase() === "oui",
    siloLabel,
    buildingLabel,
    currentTruckId: item?.current_truck_id || "",
    deliveryId: item?.delivery_id || "",
    dispatchPeriod,
    runtimeRevision,
    suspendedGroupKey: normalizeInlineText(item?.suspended_group_key),
    suspendedGroupLabel: normalizeInlineText(item?.suspended_group_label),
    suspendedGroupMemberIds: normalizeSuspendedGroupMemberIds(item),
    suspendedGroupSourceKey: normalizeInlineText(item?.suspended_group_source_key),
    isActivationHighlighted: isSuspendedDeliveryActivationHighlighted(state, item?.delivery_id),
    isPending: Boolean(item?.delivery_id && state?.pendingDeliveryMutationById?.has(item.delivery_id)),
  };
}

function buildSuspendedGroupPanelItem(groupKey, memberItems, index) {
  const orderedMemberIds = memberItems[0]?.suspendedGroupMemberIds || [];
  const memberRankById = new Map(orderedMemberIds.map((deliveryId, memberIndex) => [deliveryId, memberIndex]));
  const orderedMembers = [...memberItems].sort((left, right) => {
    const leftRank = memberRankById.has(left.deliveryId) ? memberRankById.get(left.deliveryId) : Number.MAX_SAFE_INTEGER;
    const rightRank = memberRankById.has(right.deliveryId) ? memberRankById.get(right.deliveryId) : Number.MAX_SAFE_INTEGER;
    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }
    return String(left.deliveryId || left.key).localeCompare(String(right.deliveryId || right.key));
  });
  const firstMember = orderedMembers[0] || {};
  const deliveryIds = orderedMembers.map((item) => item.deliveryId).filter(Boolean);
  const orderIds = orderedMembers
    .map((item) => normalizeInlineText(item.orderLabel))
    .filter(Boolean)
    .filter((orderId, orderIndex, items) => items.indexOf(orderId) === orderIndex);
  const totalQuantityTons = orderedMembers.reduce(
    (total, item) => (Number.isFinite(Number(item.quantityTons)) ? total + Number(item.quantityTons) : total),
    0,
  );
  const productLabels = orderedMembers
    .map((item) => item.productLabel)
    .filter(Boolean)
    .filter((productLabel, productIndex, items) => items.indexOf(productLabel) === productIndex);
  const quantityLabel = totalQuantityTons > 0 ? `${formatTons(totalQuantityTons)} t` : "";
  const memberCount = orderedMembers.length;

  return {
    type: "group",
    key: `suspended-group-${groupKey || index}`,
    groupKey,
    title: firstMember.deliveryName || firstMember.title || firstMember.suspendedGroupLabel || "Groupe suspendu",
    subtitle: `${memberCount} commande${memberCount > 1 ? "s" : ""}`,
    badgeLabel: "Groupe suspendu",
    compatibilities: firstMember.compatibilities || [],
    currentTruckId: firstMember.currentTruckId || "",
    deliveryId: deliveryIds[0] || "",
    deliveryIds,
    dispatchPeriod: firstMember.dispatchPeriod === "evening" ? "evening" : "day",
    orderIds,
    isActivationHighlighted: orderedMembers.some((item) => item.isActivationHighlighted),
    isPending: orderedMembers.some((item) => item.isPending),
    memberCount,
    memberItems: orderedMembers,
    meta: quantityLabel || `${memberCount} commande${memberCount > 1 ? "s" : ""}`,
    productLabel: productLabels.length === 1 ? productLabels[0] : (productLabels.length > 1 ? "Mixte" : ""),
    quantityLabel,
    requiresAspirator: orderedMembers.some((item) => item.requiresAspirator === true),
    isDompeur: orderedMembers.some((item) => item.isDompeur === true),
    requiresOntario: orderedMembers.some((item) => item.requiresOntario === true),
    runtimeRevision: firstMember.runtimeRevision || "",
    sourceGroupKey: groupKey || firstMember.suspendedGroupSourceKey || "",
  };
}

function buildSuspendedPanelItems(state, suspendedDeliveries, {
  dispatchPeriod = "day",
  runtimeRevision = "",
} = {}) {
  const deliveryItems = suspendedDeliveries.map((item, index) => buildSuspendedDeliveryPanelItem(state, item, index, {
    dispatchPeriod,
    runtimeRevision,
  }));
  const groupsByKey = new Map();
  for (const item of deliveryItems) {
    if (!item.suspendedGroupKey || item.suspendedGroupMemberIds.length < 2) {
      continue;
    }
    const group = groupsByKey.get(item.suspendedGroupKey) || [];
    group.push(item);
    groupsByKey.set(item.suspendedGroupKey, group);
  }

  const emittedGroupKeys = new Set();
  const panelItems = [];
  deliveryItems.forEach((item, index) => {
    const effectiveGroupKey = item.suspendedGroupKey;
    const groupItems = effectiveGroupKey ? groupsByKey.get(effectiveGroupKey) : null;
    if (!groupItems || groupItems.length < 2) {
      const panelItem = buildSuspendedGroupPanelItem(effectiveGroupKey || item.suspendedGroupKey || item.key, [item], index);
      panelItem.sourceGroupKey = item.suspendedGroupKey || panelItem.sourceGroupKey;
      panelItems.push(panelItem);
      return;
    }
    if (emittedGroupKeys.has(effectiveGroupKey)) {
      return;
    }
    emittedGroupKeys.add(effectiveGroupKey);
    panelItems.push(buildSuspendedGroupPanelItem(effectiveGroupKey, groupItems, index));
  });
  return panelItems;
}

export function selectSuspendedPanelData(state, {
  dispatchPeriod = "day",
  includeSuspendedDeliveriesInOptimization = false,
} = {}) {
  const normalizedDispatchPeriod = dispatchPeriod === "evening" ? "evening" : "day";
  const runtimeRevision = normalizeInlineText(state?.resultsData?.runtime_revision);
  const suspendedDeliveries = Array.isArray(state?.resultsData?.suspended_deliveries)
    ? state.resultsData.suspended_deliveries.filter(Boolean)
    : [];
  const deliveryCount = suspendedDeliveries.length;

  return {
    countLabel: deliveryCount ? `${deliveryCount} commande(s) suspendue(s)` : "0 commande suspendue",
    deliveryCount,
    dispatchPeriod: normalizedDispatchPeriod,
    hasItems: deliveryCount > 0,
    includeSuspendedDeliveriesInOptimization: includeSuspendedDeliveriesInOptimization === true,
    items: buildSuspendedPanelItems(state, suspendedDeliveries, {
      dispatchPeriod: normalizedDispatchPeriod,
      runtimeRevision,
    }),
    runtimeRevision,
  };
}
